import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './ReferredUsersPage.css';
import { useAuthContext } from '../../../../context/AuthContext';

const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';

// ─── Avatar colour pool ───────────────────────────────────────────────────────
const AVATAR_COLORS = ['#6c5ce7', '#00b894', '#e17055', '#0984e3', '#fd79a8', '#fdcb6e'];
const avatarColor = (idx) => AVATAR_COLORS[idx % AVATAR_COLORS.length];

// ─── Plan colour map ──────────────────────────────────────────────────────────
const PLAN_COLORS = {
  business: '#6c5ce7', premium: '#6c5ce7',
  pro: '#0984e3', basic: '#fdcb6e', standard: '#00b894', free: '#b2bec3',
};
function generateGroupCode() {
  // 12-char code e.g. 'ABCD-EFGH-JKLM' — groups TXN refs approved together
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 12; i++) {
    if (i === 4 || i === 8) code += '-';
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

const planColor = (type = '') =>
  PLAN_COLORS[(type || '').toLowerCase()] || '#b2bec3';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getInitials(name = '') {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase() || '??';
}
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' }),
  };
}
function fmtKsh(val) {
  const n = parseFloat(val) || 0;
  return `KSh ${n.toLocaleString('en-KE', { minimumFractionDigits: 0 })}`;
}
function daysBetween(a, b) {
  return Math.max(0, Math.ceil((new Date(b) - new Date(a)) / 86400000));
}

// PAGE_SIZE is now dynamic per-table (see state below)

// ─── Campaign Progress Bar ────────────────────────────────────────────────────
function CampaignBar({ campaign, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [startDate, setStartDate] = useState(campaign.start_date || '');
  const [endDate, setEndDate] = useState(campaign.end_date || '');
  const [saving, setSaving] = useState(false);

  const now = new Date();
  const start = campaign.start_date ? new Date(campaign.start_date) : null;
  const end   = campaign.end_date   ? new Date(campaign.end_date)   : null;

  let pct = 0;
  let daysLeft = null;
  let statusLabel = 'Not configured';
  let barColor = '#b2bec3';

  if (start && end) {
    const total   = daysBetween(start, end);
    const elapsed = daysBetween(start, now);
    pct       = total > 0 ? Math.min(100, Math.round((elapsed / total) * 100)) : 0;
    daysLeft  = Math.max(0, daysBetween(now, end));
    if (!campaign.is_active) {
      statusLabel = '⏸ Paused'; barColor = '#f59e0b';
    } else if (now < start) {
      statusLabel = '⏳ Not started'; barColor = '#0984e3'; pct = 0;
    } else if (now > end) {
      statusLabel = '✅ Ended'; barColor = '#00b894'; pct = 100;
    } else {
      statusLabel = `🟢 Active · ${daysLeft}d left`; barColor = '#6c5ce7';
    }
  } else if (campaign.is_active) {
    statusLabel = '🟢 Running (no end date)'; barColor = '#6c5ce7';
  }

  const handleSave = async () => {
    setSaving(true);
    await onUpdate({ start_date: startDate || null, end_date: endDate || null });
    setSaving(false);
    setEditing(false);
  };

  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
      padding: '14px 18px', marginBottom: 18,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: '#1e293b' }}>📅 Campaign Period</span>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20,
            background: `${barColor}18`, color: barColor, border: `1px solid ${barColor}40`,
          }}>{statusLabel}</span>
          {start && end && (
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              {fmtDate(campaign.start_date)} → {fmtDate(campaign.end_date)}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          {/* ── Pause / Resume ── */}
          <button
            onClick={() => onUpdate({ is_active: !campaign.is_active })}
            style={{
              padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700,
              cursor: 'pointer', border: 'none',
              background: campaign.is_active ? '#fef3c7' : '#d1fae5',
              color: campaign.is_active ? '#b45309' : '#065f46',
            }}
          >
            {campaign.is_active ? '⏸ Pause Referrals' : '▶ Resume Referrals'}
          </button>

          {/* ── Set dates ── */}
          <button
            onClick={() => setEditing(e => !e)}
            style={{
              padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700,
              cursor: 'pointer', border: '1px solid #c7d2fe',
              background: '#eef2ff', color: '#6366f1',
            }}
          >
            📆 {editing ? 'Cancel' : 'Set Dates'}
          </button>
        </div>
      </div>

      {/* ── Date picker inline form ── */}
      {editing && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, background: '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: '#6366f1' }}>
            Start
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              style={{ marginLeft: 6, height: 32, padding: '0 8px', border: '1.5px solid #c7d2fe', borderRadius: 7, fontSize: 12 }} />
          </label>
          <label style={{ fontSize: 12, fontWeight: 700, color: '#6366f1' }}>
            End
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              min={startDate}
              style={{ marginLeft: 6, height: 32, padding: '0 8px', border: '1.5px solid #c7d2fe', borderRadius: 7, fontSize: 12 }} />
          </label>
          <button onClick={handleSave} disabled={saving}
            style={{ height: 32, padding: '0 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? '…' : '✓ Save'}
          </button>
        </div>
      )}

      {/* ── Progress bar ── */}
      {start && end ? (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>Progress</span>
            <span style={{ fontSize: 11, fontWeight: 800, color: barColor }}>{pct}%</span>
          </div>
          <div style={{ height: 8, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${pct}%`, borderRadius: 99,
              background: `linear-gradient(90deg, ${barColor}, ${barColor}bb)`,
              transition: 'width 0.5s ease',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            <span style={{ fontSize: 10, color: '#94a3b8' }}>{fmtDate(campaign.start_date)}</span>
            <span style={{ fontSize: 10, color: '#94a3b8' }}>{fmtDate(campaign.end_date)}</span>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 11.5, color: '#94a3b8', fontStyle: 'italic' }}>
          Set start &amp; end dates to see a campaign progress bar.
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const ReferredUsersPage = () => {
  const { getToken } = useAuthContext();

  // ── Remote data ──────────────────────────────────────────────────────────────
  const [stats,           setStats]           = useState(null);
  const [rows,            setRows]            = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState('');
  // Maps referrer email → their referral_mpesa number from UserProfile
  // Built once at fetch time — used in both table PAYOUT PHONE cells and handleApprove
  const [referrerMpesaMap, setReferrerMpesaMap] = useState({});

  // ── Campaign state (stored in localStorage — replace with API when ready) ────
  const [campaign, setCampaign] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('referral_campaign') || '{}');
    } catch { return {}; }
  });
  const saveCampaign = (updates) => {
    const next = { ...campaign, ...updates };
    setCampaign(next);
    localStorage.setItem('referral_campaign', JSON.stringify(next));
    return Promise.resolve();
  };
  const campaignData = { is_active: true, ...campaign };

  // ── UI ───────────────────────────────────────────────────────────────────────
  const [search,           setSearch]           = useState('');
  const [statsDateFilter,  setStatsDateFilter]  = useState('month'); // for stats cards
  const [dateFilter,       setDateFilter]       = useState('month'); // for table rows
  const [approvalFilter,   setApprovalFilter]   = useState('all');
  const [currentPage,    setCurrentPage]    = useState(1);
  const [pageSize,       setPageSize]       = useState(10);
  const [pageSizeInput,  setPageSizeInput]  = useState('10');
  // Commission Payments table — separate pagination
  const [commPage,       setCommPage]       = useState(1);
  const [commPageSize,   setCommPageSize]   = useState(10);
  const [commPageInput,  setCommPageInput]  = useState('10');
  const [disperseMsg,    setDisperseMsg]    = useState('');
  const [editModeRows,   setEditModeRows]   = useState(new Set());
  // ── Expandable TXN REF — set of row IDs where the full ref is shown ──────────
  const [expandedTxnRefs, setExpandedTxnRefs] = useState(new Set());
  // ── Expanded rows in Approved & Not Paid table (click to see individual commissions) ─
  const [expandedAnpRows, setExpandedAnpRows] = useState(new Set());
  const toggleAnpRow = useCallback((refKey) => {
    setExpandedAnpRows(prev => {
      const next = new Set(prev);
      if (next.has(refKey)) { next.delete(refKey); } else { next.add(refKey); }
      return next;
    });
  }, []);
  // ── Inline payment-status editing for individual commission rows ──────────────
  const [payStatusSaving, setPayStatusSaving] = useState({});
  const toggleTxnRef = useCallback((rowKey) => {
    setExpandedTxnRefs(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) { next.delete(rowKey); } else { next.add(rowKey); }
      return next;
    });
  }, []);

  // ── Approval state ────────────────────────────────────────────────────────────
  // ✅ FIX: localStorage completely removed from approval flow.
  // All approval state is loaded exclusively from the backend on every fetch,
  // so all tabs/browsers/sessions always show the same data without stale
  // localStorage values overriding what's in the database.
  //
  // approvalMap:      { [referrerKey]: 'approved' | 'pending' | 'paid' }
  // approvedPayments: full row objects for the Commission Payments table
  // rowApprovalSet:   { [txnRef]: { approvedAt, referrerKey } }

  const [rowApprovalSet,    setRowApprovalSet]    = useState({});
  const [approvalMap,       setApprovalMap]       = useState({});
  const [approvedPayments,  setApprovedPayments]  = useState([]);
  const [approvingKey,      setApprovingKey]      = useState(null);
  const [approvingRowKey,   setApprovingRowKey]   = useState(null);
  const [dispersingPayment, setDispersingPayment] = useState({});

  // ── Helper: update React state — backend is the only source of truth ─────────
  const persistApprovals = useCallback((payments) => {
    const newMap = Object.fromEntries(payments.map(p => [p.referrerKey, p.status]));
    setApprovedPayments(payments);
    setApprovalMap(newMap);
    // ✅ No localStorage write — DB is the source of truth
  }, []);

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const token   = getToken();
      const headers = { Authorization: `Token ${token}` };

      // Fetch referrals + users — these endpoints always exist
      const [statsRes, usersRes] = await Promise.all([
        fetch(`${API_BASE}/admin/referrals/`, { headers })
          .then(r => { if (!r.ok) throw new Error(`referrals: ${r.status}`); return r.json(); }),
        fetch(`${API_BASE}/admin/referrals/referred-users/`, { headers })
          .then(r => { if (!r.ok) throw new Error(`referred-users: ${r.status}`); return r.json(); }),
      ]);

      // ── Build referrer email → referral_mpesa lookup map ─────────────────────
      // Fetches the admin users list (same source as UsersView REFERRAL MPESA column).
      // Non-fatal: if this call fails the page still loads, PAYOUT PHONE just shows —
      let mpesaMap = {};
      try {
        const adminUsersRes = await fetch(`${API_BASE}/admin/users/?page_size=1000`, { headers });
        if (adminUsersRes.ok) {
          const adminUsersData = await adminUsersRes.json();
          const userList = adminUsersData?.results || adminUsersData || [];
          userList.forEach(u => {
            if (u.email && u.referral_mpesa) {
              mpesaMap[u.email.toLowerCase()] = u.referral_mpesa;
            }
            if (u.username && u.referral_mpesa) {
              mpesaMap[u.username.toLowerCase()] = u.referral_mpesa;
            }
          });
          setReferrerMpesaMap(mpesaMap);
        }
      } catch {
        // Non-fatal — PAYOUT PHONE column will show — until resolved
      }

      // Build stats
      const referralList    = statsRes.results || [];
      const totalReferrals  = referralList.reduce((s, r) => s + (r.total_referrals  || 0), 0);
      const activeReferrals = referralList.reduce((s, r) => s + (r.active_referrals || 0), 0);
      const totalEarnings   = referralList.reduce((s, r) => s + (r.total_earnings   || 0), 0);
      const pendingBalance  = referralList.reduce((s, r) => s + (r.pending_balance  || 0), 0);

      setStats({
        total_referrals:  totalReferrals,
        active_referrals: activeReferrals,
        total_earnings:   totalEarnings,
        pending_balance:  pendingBalance,
        withdrawn_total:  totalEarnings - pendingBalance,
      });

      setRows(usersRes?.results || []);

      // ✅ FIX: Load cumulative approvals from backend — no localStorage fallback.
      // Backend is the single source of truth so all tabs always show the same state.
      try {
        const approvalsRes = await fetch(`${API_BASE}/admin/referrals/approvals/`, { headers });
        if (approvalsRes.ok) {
          const approvalsData = await approvalsRes.json();
          const approvals = approvalsData?.results || [];
          const newPayments = approvals.map(a => {
            const refEmailKey = (a.referrer_email || '').toLowerCase();
            const refKeyKey   = (a.referrer_key   || '').toLowerCase();
            const resolvedPhone =
              mpesaMap[refEmailKey] ||
              mpesaMap[refKeyKey]   ||
              a.payout_phone        ||
              '—';
            return {
              referrerKey:      a.referrer_key,
              approvalId:       a.id,
              refName:          a.referrer_name  || a.referrer_key,
              refEmail:         a.referrer_email || '—',
              totalCommission:  parseFloat(a.total_commission)   || 0,
              totalPlanPayment: parseFloat(a.total_plan_payment) || 0,
              rowCount:         a.row_count      || 0,
              payoutPhone:      resolvedPhone,
              payoutReceipt:    a.payout_receipt || null,
              status:           a.status,
              approvedAt:       a.approved_at,
              groupCode:        a.group_code || null,
              dispersedAt:      a.dispersed_at || null,
            };
          });
          persistApprovals(newPayments);
        }
        // If endpoint returns non-200, approvals start empty — user sees unapproved state
      } catch { /* non-fatal — approvals will show as empty until resolved */ }

      // ✅ FIX: Load individual row approvals from backend only — no localStorage merge.
      try {
        const rowApprovalsRes = await fetch(
          `${API_BASE}/admin/referrals/row-approvals/`,
          { headers }
        );
        if (rowApprovalsRes.ok) {
          const rowApprovalsData = await rowApprovalsRes.json();
          const rowApprovalsList = rowApprovalsData?.results || rowApprovalsData || [];
          const fromBackend = {};
          rowApprovalsList.forEach(ra => {
            if (ra.txn_ref) {
              fromBackend[ra.txn_ref] = {
                approvedAt:  ra.approved_at,
                referrerKey: ra.referrer_key,
                groupCode:   ra.group_code || null,  // ✅ FIX: load groupCode from backend
              };
            }
          });
          setRowApprovalSet(fromBackend);  // ✅ backend only — no localStorage merge
        }
      } catch { /* non-fatal */ }

    } catch (err) {
      console.error('ReferredUsersPage fetch error:', err);
      setError('Failed to load referral data. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, [getToken, persistApprovals]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setCurrentPage(1); }, [search, dateFilter, approvalFilter]);

  // ── Filter + paginate ────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const now = new Date();
    let result = rows;

    // Date filter by ad_posted_at
    if (dateFilter !== 'all') {
      result = result.filter(r => {
        if (!r.ad_posted_at) return false;
        const d = new Date(r.ad_posted_at);
        if (dateFilter === 'day') {
          return d.toDateString() === now.toDateString();
        }
        if (dateFilter === 'yesterday') {
          const yest = new Date(now); yest.setDate(now.getDate() - 1);
          return d.toDateString() === yest.toDateString();
        }
        if (dateFilter === 'week') {
          const weekAgo = new Date(now); weekAgo.setDate(now.getDate() - 7);
          return d >= weekAgo;
        }
        if (dateFilter === 'last_week') {
          // Calendar last week: Mon–Sun of the previous week
          const dayOfWeek = now.getDay() || 7; // 1=Mon … 7=Sun
          const thisMonday = new Date(now); thisMonday.setDate(now.getDate() - dayOfWeek + 1); thisMonday.setHours(0,0,0,0);
          const lastMonday = new Date(thisMonday); lastMonday.setDate(thisMonday.getDate() - 7);
          const lastSunday = new Date(thisMonday); lastSunday.setMilliseconds(-1);
          return d >= lastMonday && d <= lastSunday;
        }
        if (dateFilter === 'month') {
          return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
        }
        if (dateFilter === 'last_month') {
          const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const lmEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
          return d >= lm && d <= lmEnd;
        }
        return true;
      });
    }

    // Approval filter
    if (approvalFilter !== 'all') {
      result = result.filter(r => {
        const key    = r.referral?.referrer_email || r.referral?.referrer_username || 'unknown';
        const status = approvalMap[key]; // 'approved' | 'pending' | 'paid' | undefined
        if (approvalFilter === 'approved') return status === 'approved' || status === 'paid';
        if (approvalFilter === 'pending')  return !status || status === 'pending';
        return true;
      });
    }

    // Text search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(r =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.referral?.referrer_username || '').toLowerCase().includes(q) ||
        (r.referral?.referrer_email || '').toLowerCase().includes(q) ||
        (r.premium_type || '').toLowerCase().includes(q) ||
        (r.referred_by_code || '').toLowerCase().includes(q)
      );
    }

    return result;
  }, [rows, search, dateFilter, approvalFilter, approvalMap]);

  // ── Stats-section filtered rows (uses its own date filter, independent of table) ─
  const statsFiltered = useMemo(() => {
    if (statsDateFilter === 'all') return rows;
    const now = new Date();
    return rows.filter(r => {
      if (!r.ad_posted_at) return false;
      const d = new Date(r.ad_posted_at);
      if (statsDateFilter === 'day')   return d.toDateString() === now.toDateString();
      if (statsDateFilter === 'yesterday') {
        const yest = new Date(now); yest.setDate(now.getDate() - 1);
        return d.toDateString() === yest.toDateString();
      }
      if (statsDateFilter === 'week')  { const wa = new Date(now); wa.setDate(now.getDate() - 7); return d >= wa; }
      if (statsDateFilter === 'last_week') {
        const dayOfWeek = now.getDay() || 7;
        const thisMonday = new Date(now); thisMonday.setDate(now.getDate() - dayOfWeek + 1); thisMonday.setHours(0,0,0,0);
        const lastMonday = new Date(thisMonday); lastMonday.setDate(thisMonday.getDate() - 7);
        const lastSunday = new Date(thisMonday); lastSunday.setMilliseconds(-1);
        return d >= lastMonday && d <= lastSunday;
      }
      if (statsDateFilter === 'month') return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      if (statsDateFilter === 'last_month') {
        const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lmEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        return d >= lm && d <= lmEnd;
      }
      return true;
    });
  }, [rows, statsDateFilter]);

  const statsPlanTotal  = useMemo(() => statsFiltered.reduce((s, r) => s + (r.plan_amount       || 0), 0), [statsFiltered]);
  const statsCommTotal  = useMemo(() => statsFiltered.reduce((s, r) => s + (r.commission_amount || 0), 0), [statsFiltered]);
  const statsTotalRefs  = statsFiltered.length;
  const statsActiveRefs = useMemo(() => statsFiltered.filter(r => r.is_active).length, [statsFiltered]);
  const statsPending    = useMemo(() => statsFiltered.reduce((s, r) => s + (r.pending_balance || 0), 0), [statsFiltered]);
  const statsWithdrawn  = useMemo(() => statsFiltered.reduce((s, r) => s + (r.plan_amount     || 0) - (r.pending_balance || 0), 0), [statsFiltered]);

  const statsDateLabel = {
    day: 'Today', yesterday: 'Yesterday',
    week: 'This Week', last_week: 'Last Week',
    month: 'This Month', last_month: 'Last Month',
    all: 'All Time',
  }[statsDateFilter] || 'All Time';

  // ── Stats cards ──────────────────────────────────────────────────────────────
  const statsCards = stats ? [
    { icon: '👥', label: 'Total Referrals',  value: String(statsTotalRefs),   sub: `↑ +${statsActiveRefs} active`,                                                                                         subColor: '#6c5ce7', iconColor: '#6c5ce7' },
    { icon: '✅', label: 'Active Referrals',  value: String(statsActiveRefs),  sub: statsTotalRefs > 0 ? `◎ ${Math.round((statsActiveRefs / statsTotalRefs) * 100)}% approved` : '◎ 0% approved',          subColor: '#00b894', iconColor: '#00b894' },
    { icon: '💼', label: 'Pending Balance',   value: fmtKsh(statsPending),     sub: `● Withdrawn: ${fmtKsh(statsWithdrawn)}`,                                                                               subColor: '#e17055', iconColor: '#e17055', hasInfo: true },
    { icon: '🧾', label: 'Plan Payment',      value: fmtKsh(statsPlanTotal),   sub: `📅 ${statsDateLabel}`,                                                                                                 subColor: '#0984e3', iconColor: '#0984e3' },
    { icon: '💰', label: 'Commission',         value: fmtKsh(statsCommTotal),   sub: `📅 ${statsDateLabel}`,                                                                                                 subColor: '#00b894', subBg: '#e0faf0',   iconColor: '#00b894' },
    { icon: '💸', label: 'Total Earnings',    value: fmtKsh(stats.total_earnings ?? 0), sub: '◆ Disperse Payments',                                                                                         subColor: '#065f46', subBg: '#d1fae5',   iconColor: '#6366f1', dark: true, lifetime: true },
  ] : [];

  // ── CSV Export ───────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    if (!filtered.length) return;
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const filename = `referred-users_${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}.csv`;
    const headers = ['New User Name','New User Email','Ref Code','Referrer','Referrer Email','Date Joined','Plan Type','Plan Payment (KSh)','Commission (KSh)','Comm Status','TXN Ref','Ad Posted At','Payout Phone','Payout Receipt (M-Pesa Code)','Status'];
    const csvRows = filtered.map(r => {
      // ✅ All fields now come directly from the row — no txnMap lookup
      const commStatus = r.commission_status || (r.is_active ? 'Paid' : 'Pending');
      const refKey     = (r.referral?.referrer_email || r.referral?.referrer_username || '').toLowerCase();
      const payoutPhone = referrerMpesaMap[refKey] || r.payout_phone || '—';
      return [
        r.name  || '—', r.email || '—',
        r.referred_by_code || '—',
        r.referral?.referrer_username || '—', r.referral?.referrer_email || '—',
        fmtDate(r.joined_at),
        r.premium_type || 'Free',
        (r.plan_amount || 0).toFixed(2),
        (r.commission_amount || 0).toFixed(2),
        commStatus,
        r.transaction_ref || '—',
        r.ad_posted_at ? new Date(r.ad_posted_at).toLocaleString('en-KE') : '—',
        payoutPhone,
        r.payout_receipt || 'Pending',
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
    });
    const blob = new Blob(['\uFEFF' + [headers.join(','), ...csvRows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename;
    document.body.appendChild(a); a.click(); URL.revokeObjectURL(a.href); document.body.removeChild(a);
  };

  // ── Delete row ───────────────────────────────────────────────────────────────
  const handleDelete = useCallback(async (rowId, commissionId) => {
    if (!window.confirm('Delete this commission record? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_BASE}/admin/referrals/commissions/${commissionId}/`, {
        method: 'DELETE',
        headers: { Authorization: `Token ${getToken()}` },
      });
      if (res.ok || res.status === 204) {
        setRows(prev => prev.filter(r => r.id !== rowId));
        setDisperseMsg('✅ Record deleted successfully.');
      } else {
        const data = await res.json().catch(() => ({}));
        setDisperseMsg(`❌ ${data.detail || data.error || 'Delete failed.'}`);
      }
    } catch (err) {
      setDisperseMsg(`❌ ${err?.message || 'Delete failed.'}`);
    }
  }, [getToken]);

  // ── Toggle edit mode for a row (unlocks delete) ───────────────────────────
  const toggleEditMode = useCallback((rowKey) => {
    setEditModeRows(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) { next.delete(rowKey); } else { next.add(rowKey); }
      return next;
    });
  }, []);

  // ── Mark individual commission row as Paid (saves to DB) ────────────────────
  // Called from the EDIT cell when user selects "Paid" from the dropdown.
  // PATCHes the commission record's payment_status to 'paid' on the backend.
  // On success it also updates approvedPayments so the GROUPED CODE clears for that txn.
  const handleMarkRowPaid = useCallback(async (rowId, commissionId, txnRef, referrerKey) => {
    setPayStatusSaving(prev => ({ ...prev, [rowId]: true }));
    try {
      const res = await fetch(`${API_BASE}/admin/referrals/commissions/${commissionId}/`, {
        method: 'PATCH',
        headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_status: 'paid', commission_status: 'paid' }),
      });
      if (res.ok) {
        // Update the local rows so PAYMENT STATUS shows Paid immediately
        setRows(prev => prev.map(r =>
          r.id === rowId ? { ...r, commission_status: 'paid', payment_status: 'paid' } : r
        ));
        // Remove this txnRef from its batch's approvedTxnRefs so the grouped code clears
        if (txnRef) {
          setApprovedPayments(prev => prev.map(p => {
            if (!Array.isArray(p.approvedTxnRefs) || !p.approvedTxnRefs.includes(txnRef)) return p;
            const remaining = p.approvedTxnRefs.filter(r => r !== txnRef);
            // If all txns in the batch are now paid, mark the batch itself as paid
            return { ...p, approvedTxnRefs: remaining };
          }));
        }
        // Close edit mode for this row
        setEditModeRows(prev => { const n = new Set(prev); n.delete(rowId); return n; });
        setDisperseMsg('✅ Payment status updated to Paid.');
      } else {
        const data = await res.json().catch(() => ({}));
        setDisperseMsg(`❌ ${data.detail || data.error || 'Update failed.'}`);
      }
    } catch (err) {
      setDisperseMsg(`❌ ${err?.message || 'Update failed.'}`);
    } finally {
      setPayStatusSaving(prev => { const n = { ...prev }; delete n[rowId]; return n; });
    }
  }, [getToken]);

  // ── Approve ───────────────────────────────────────────────────────────────────
  // KEY FIX: approved_at is ALWAYS updated to now() on every approval/re-approval.
  // This is the timestamp the frontend compares against commission ad_posted_at
  // to detect new commissions that need fresh approval.
  // Re-approval is always allowed unless the referrer is already paid.
  const handleApprove = useCallback(async (group) => {
    const { referrerKey, refName, refEmail, totalComm, totalPlan, rows: groupRows } = group;
    const existingStatus = approvalMap[referrerKey];
    // Only block if already paid — 'approved' can always be re-approved
    if (existingStatus === 'paid') return;
    setApprovingKey(referrerKey);
    try {
      const now = new Date().toISOString();  // ✅ always fresh — never reused from existingPayment
      const existingPayment = approvedPayments.find(p => p.referrerKey === referrerKey);

      // ✅ KEY FIX: capture the exact TXN REFs included in this approval batch.
      // PAYMENT STATUS on individual rows is driven by this set — NOT by the
      // referrer's global approval status. This prevents new post-approval
      // commissions from inheriting "Approved" status automatically.
      const approvedTxnRefs = groupRows
        .map(r => r.transaction_ref)
        .filter(Boolean);

      // ✅ FIX: Resolve groupCode using this priority:
      //   1. Existing cumulative approval for this referrer — BUT ONLY if NOT yet paid
      //      (re-approval of unpaid batch → keep same code so rows stay grouped)
      //      If already paid → the old batch is closed; generate a FRESH code for the new group
      //   2. Any individual row already in rowApprovalSet for this referrer that is NOT paid
      //      (rows approved individually before cumulative → share their unpaid code)
      //   3. Generate a fresh code (first approval, or all previous batches are paid)
      const unpaidExistingPayment = existingPayment?.status !== 'paid' ? existingPayment : null;
      const existingRowCode = Object.values(rowApprovalSet)
        .find(ra => {
          if (ra.referrerKey !== referrerKey || !ra.groupCode) return false;
          // Only reuse the code if this TXN ref's batch is NOT paid
          const batch = approvedPayments.find(p =>
            Array.isArray(p.approvedTxnRefs) && p.approvedTxnRefs.includes(ra.txnRef)
          );
          return !batch || batch.status !== 'paid';
        })?.groupCode;
      const groupCode = unpaidExistingPayment?.groupCode || existingRowCode || generateGroupCode();

      const newPayment = {
        referrerKey,
        approvalId:       existingPayment?.approvalId || null,
        refName:          refName  || referrerKey,
        refEmail:         refEmail || '—',
        totalCommission:  totalComm  || 0,
        totalPlanPayment: totalPlan  || 0,
        rowCount:         groupRows.length,
        // ── Payout phone: look up referrer's referral_mpesa from users list ──
        // referrerMpesaMap is keyed by email (lowercase) and built at fetch time.
        payoutPhone: (
          referrerMpesaMap[(refEmail || '').toLowerCase()] ||
          referrerMpesaMap[(referrerKey || '').toLowerCase()] ||
          groupRows.find(r => r.payout_phone)?.payout_phone ||
          '—'
        ),
        payoutReceipt:    groupRows.find(r => r.payout_receipt)?.payout_receipt || null,
        status:           'approved',
        approvedAt:       now,   // ✅ ALWAYS NOW — refreshes the comparison baseline
        approvedTxnRefs,         // ✅ per-transaction approval tracking
        groupCode,               // ✅ NEW — batch identifier for GROUPED CODE column
        dispersedAt:      null,
      };

      // 1️⃣ Optimistic UI update
      const updated = existingPayment
        ? approvedPayments.map(p => p.referrerKey === referrerKey ? newPayment : p)
        : [...approvedPayments, newPayment];
      persistApprovals(updated);

      // 2️⃣ Persist to backend — always required (no localStorage fallback) if row exists (UniqueConstraint = one row per referrer)
      {  // ✅ Always persist to backend
        const payload = {
          referrer_key:       referrerKey,
          referrer_name:      refName,
          referrer_email:     refEmail,
          total_commission:   totalComm,
          total_plan_payment: totalPlan,
          row_count:          groupRows.length,
          payout_phone:       newPayment.payoutPhone !== '—' ? newPayment.payoutPhone : null,
          payout_receipt:     newPayment.payoutReceipt || null,
          status:             'approved',
          approved_at:        now,
          group_code:         groupCode,  // ✅ NEW — batch identifier
        };
        // Use PATCH if we have an approvalId, otherwise POST
        // If POST returns 409 (UniqueConstraint), fall back to a lookup + PATCH
        const url    = existingPayment?.approvalId
          ? `${API_BASE}/admin/referrals/approvals/${existingPayment.approvalId}/`
          : `${API_BASE}/admin/referrals/approvals/`;
        const method = existingPayment?.approvalId ? 'PATCH' : 'POST';
        try {
          let res = await fetch(url, {
            method,
            headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });

          // If POST hit the UniqueConstraint (409) — fetch the existing row and PATCH it
          if (!res.ok && method === 'POST' && res.status === 409) {
            const listRes = await fetch(
              `${API_BASE}/admin/referrals/approvals/?referrer_key=${encodeURIComponent(referrerKey)}`,
              { headers: { Authorization: `Token ${getToken()}` } }
            );
            if (listRes.ok) {
              const listData = await listRes.json();
              const existing = (listData.results || listData)[0];
              if (existing?.id) {
                res = await fetch(`${API_BASE}/admin/referrals/approvals/${existing.id}/`, {
                  method: 'PATCH',
                  headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload),
                });
              }
            }
          }

          if (res.ok) {
            const saved = await res.json();
            const withId = updated.map(p =>
              p.referrerKey === referrerKey ? { ...p, approvalId: saved.id } : p
            );
            persistApprovals(withId);
          }
          // Non-ok responses are silent — optimistic UI update already applied
        } catch { /* non-fatal */ }
      }
    } finally {
      setApprovingKey(null);
    }
  }, [approvalMap, approvedPayments, getToken, persistApprovals]);

  // ── Approve individual row ────────────────────────────────────────────────────
  // Tracked PURELY by TXN ref in rowApprovalSet.
  // Does NOT touch approvalMap or approvedPayments — those are for cumulative only.
  // This prevents a single row approval from marking all rows as approved.
  const handleApproveRow = useCallback(async (row, group) => {
    const txnRef      = row.transaction_ref;
    const referrerKey = group.referrerKey;
    const rowKey      = row.id;

    if (!txnRef) return; // no TXN ref — can't track individually
    if (approvalMap[referrerKey] === 'paid') return; // whole referrer locked
    if (rowApprovalSet[txnRef]) return;              // already individually approved

    setApprovingRowKey(rowKey);
    try {
      const now = new Date().toISOString();

      // ✅ FIX: Resolve groupCode for this individual row using same priority as handleApprove:
      //   1. Existing cumulative approval for this referrer (approved but NOT paid)
      //   2. Any other individual row already approved for this referrer whose batch is NOT paid
      //   3. Generate a fresh code (first approval, or all previous batches are paid)
      const existingCumulative = approvedPayments.find(
        p => p.referrerKey === referrerKey && p.status !== 'paid'
      );
      const existingRowCode = Object.values(rowApprovalSet)
        .find(ra => {
          if (ra.referrerKey !== referrerKey || !ra.groupCode) return false;
          // Only reuse if the batch containing this row's TXN ref is not yet paid
          const batch = approvedPayments.find(p =>
            Array.isArray(p.approvedTxnRefs) && p.approvedTxnRefs.includes(ra.txnRef)
          );
          return !batch || batch.status !== 'paid';
        })?.groupCode;
      const groupCode = existingCumulative?.groupCode || existingRowCode || generateGroupCode();

      // 1️⃣ Write only to rowApprovalSet — never to approvalMap
      const updatedRowSet = {
        ...rowApprovalSet,
        [txnRef]: { approvedAt: now, referrerKey, groupCode },
      };
      setRowApprovalSet(updatedRowSet);
      // ✅ No localStorage write — backend is source of truth

      // 2️⃣ Persist row approval to backend
      try {
        const rowSaveRes = await fetch(`${API_BASE}/admin/referrals/row-approvals/`, {
          method: 'POST',
          headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            txn_ref:      txnRef,
            referrer_key: referrerKey,
            approved_at:  now,
            group_code:   groupCode,   // ✅ FIX: persist groupCode to backend
          }),
        });
        // 409 = already exists (unique on txn_ref) — not an error, just a no-op
        if (!rowSaveRes.ok && rowSaveRes.status !== 409) {
          console.warn('[row-approvals] POST failed:', rowSaveRes.status, 'txn_ref:', txnRef);
        }
      } catch (err) {
        console.warn('[row-approvals] backend sync failed (non-fatal):', err?.message);
      }
    } finally {
      setApprovingRowKey(null);
    }
  }, [approvalMap, rowApprovalSet, getToken]);

  // ── Revoke individual row approval ───────────────────────────────────────────
  // Removes a single TXN ref from rowApprovalSet and deletes it from the backend.
  // Uses the TXN ref string directly in the URL — no integer PK needed.
  const handleRevokeRow = useCallback(async (txnRef) => {
    if (!txnRef || !rowApprovalSet[txnRef]) return; // not approved — nothing to revoke

    // 1️⃣ Optimistic UI update — remove from local state immediately
    const updatedRowSet = { ...rowApprovalSet };
    delete updatedRowSet[txnRef];
    setRowApprovalSet(updatedRowSet);

    // 2️⃣ DELETE from backend — URL uses txn_ref string (not int pk)
    try {
      const res = await fetch(
        `${API_BASE}/admin/referrals/row-approvals/${encodeURIComponent(txnRef)}/`,
        {
          method: 'DELETE',
          headers: { Authorization: `Token ${getToken()}` },
        }
      );
      if (!res.ok && res.status !== 404) {
        // Revert optimistic update if backend returned unexpected error
        setRowApprovalSet(prev => ({ ...prev, [txnRef]: rowApprovalSet[txnRef] }));
        console.warn('[row-approvals] DELETE failed:', res.status, 'txn_ref:', txnRef);
      }
    } catch (err) {
      // Revert optimistic update on network error
      setRowApprovalSet(prev => ({ ...prev, [txnRef]: rowApprovalSet[txnRef] }));
      console.warn('[row-approvals] revoke failed (non-fatal):', err?.message);
    }
  }, [rowApprovalSet, getToken]);

  // ── Revoke cumulative approval ────────────────────────────────────────────────
  const handleRevoke = useCallback(async (referrerKey, approvalId) => {
    if (approvalMap[referrerKey] === 'paid') return;
    if (!window.confirm('Revoke this approval? The referrer will be moved back to unapproved.')) return;

    // 1️⃣ Optimistic UI update — remove from local state and localStorage immediately
    const updated = approvedPayments.filter(p => p.referrerKey !== referrerKey);
    persistApprovals(updated);

    // 2️⃣ Delete from backend
    // Strategy: use the known approvalId directly if we have it.
    // If not (e.g. approval was stored in localStorage before backend sync),
    // look up the row by referrer_key first, then DELETE it.
    try {
      const headers = { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' };

      let idToDelete = approvalId;

      if (!idToDelete) {
        // Lookup the backend row by referrer_key to get its id
        const lookupRes = await fetch(
          `${API_BASE}/admin/referrals/approvals/?referrer_key=${encodeURIComponent(referrerKey)}`,
          { headers }
        );
        if (lookupRes.ok) {
          const lookupData = await lookupRes.json();
          const existing = (lookupData?.results || lookupData)?.[0];
          idToDelete = existing?.id || null;
        }
      }

      if (idToDelete) {
        const deleteRes = await fetch(
          `${API_BASE}/admin/referrals/approvals/${idToDelete}/`,
          { method: 'DELETE', headers }
        );
        if (!deleteRes.ok && deleteRes.status !== 404) {
          // DELETE failed — re-fetch to resync state with whatever the backend actually has
          console.error(`Revoke DELETE failed: ${deleteRes.status}`);
          fetchData();
        }
      }
      // If idToDelete is still null, the row never reached the backend —
      // localStorage was already cleared above, so UI is correct.
    } catch (err) {
      console.error('Revoke error:', err);
      // On network error, re-fetch so UI reflects true backend state on next render
      fetchData();
    }
  }, [approvalMap, approvedPayments, getToken, persistApprovals, fetchData]);

  // ── Disperse payment ─────────────────────────────────────────────────────────
  const handleDispersePayment = useCallback(async (referrerKey, approvalId) => {
    if (approvalMap[referrerKey] === 'paid') return;
    setDispersingPayment(prev => ({ ...prev, [referrerKey]: true }));
    setDisperseMsg('');
    try {
      // 1️⃣ Optimistic UI update — mark as 'pending'
      const updated = approvedPayments.map(p =>
        p.referrerKey === referrerKey ? { ...p, status: 'pending' } : p
      );
      persistApprovals(updated);

      // 2️⃣ PATCH backend status
      if (approvalId) {
        try {
          await fetch(`${API_BASE}/admin/referrals/approvals/${approvalId}/`, {
            method: 'PATCH',
            headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'pending' }),
          });
        } catch { /* silent */ }
      }

      // 3️⃣ Trigger M-Pesa payout
      const withdrawRes = await fetch(`${API_BASE}/referrals/withdraw/`, {
        method: 'POST',
        headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const withdrawData = await withdrawRes.json();
      if (withdrawRes.ok) {
        setDisperseMsg('✅ Payment dispersal initiated — M-Pesa payout is processing.');
        // If backend returns a receipt, mark as paid
        if (withdrawData?.receipt || withdrawData?.transaction_id) {
          const paid = approvedPayments.map(p =>
            p.referrerKey === referrerKey
              ? { ...p, status: 'paid', payoutReceipt: withdrawData.receipt || withdrawData.transaction_id, dispersedAt: new Date().toISOString() }
              : p
          );
          persistApprovals(paid);
        }
        await fetchData();
      } else {
        setDisperseMsg(`❌ ${withdrawData.detail || withdrawData.error || 'Dispersal failed.'}`);
        // Revert optimistic update
        const reverted = approvedPayments.map(p =>
          p.referrerKey === referrerKey ? { ...p, status: 'approved' } : p
        );
        persistApprovals(reverted);
      }
    } catch (err) {
      setDisperseMsg(`❌ ${err?.message || 'Dispersal failed.'}`);
      const reverted = approvedPayments.map(p =>
        p.referrerKey === referrerKey ? { ...p, status: 'approved' } : p
      );
      persistApprovals(reverted);
    } finally {
      setDispersingPayment(prev => ({ ...prev, [referrerKey]: false }));
    }
  }, [approvalMap, approvedPayments, getToken, persistApprovals, fetchData]);
  const groupedByReferrer = useMemo(() => {
    const groups = {};
    filtered.forEach(r => {
      const key = r.referral?.referrer_email || r.referral?.referrer_username || 'unknown';
      if (!groups[key]) {
        groups[key] = {
          rows: [],
          referrerKey: key,
          refName:  r.referral?.referrer_username || r.referral?.referrer_name || '—',
          refEmail: r.referral?.referrer_email || '—',
        };
      }
      groups[key].rows.push(r);
    });
    return groups;
  }, [filtered]);

  // Flattened list with summary rows injected after each referrer group
  const groupedPageRows = useMemo(() => {
    const flat = [];
    Object.values(groupedByReferrer).forEach(group => {
      group.rows.forEach(r => flat.push({ type: 'row', data: r, group }));
      const totalComm = group.rows.reduce((s, r) => s + (r.commission_amount || 0), 0);
      const totalPlan = group.rows.reduce((s, r) => s + (r.plan_amount || 0), 0);

      // ── Stale-approval detection ──────────────────────────────────────────
      // If any commission's ad_posted_at is AFTER the last approvedAt,
      // the summary row needs re-approval — not just approved.
      const existingApproval = approvedPayments.find(p => p.referrerKey === group.referrerKey);
      const approvedAt = existingApproval?.approvedAt ? new Date(existingApproval.approvedAt) : null;
      const hasNewSinceApproval = approvedAt
        ? group.rows.some(r => {
            const ts = r.ad_posted_at || r.joined_at;
            return ts && new Date(ts) > approvedAt;
          })
        : false;

      flat.push({ type: 'summary', data: {
        ...group, totalComm, totalPlan,
        hasNewSinceApproval,
        approvedAt: existingApproval?.approvedAt || null,
      }});
    });
    return flat;
  }, [groupedByReferrer, approvedPayments]);

  // Page only data rows
  const dataRowCount = useMemo(() => groupedPageRows.filter(r => r.type === 'row').length, [groupedPageRows]);
  const totalPagesGrouped = Math.max(1, Math.ceil(dataRowCount / pageSize));
  const safePageGrouped   = Math.min(currentPage, totalPagesGrouped);

  const pagedGroupRows = useMemo(() => {    const start = (safePageGrouped - 1) * pageSize;
    const end   = start + pageSize;
    let dataCount = 0, startIdx = -1, endIdx = -1;
    for (let i = 0; i < groupedPageRows.length; i++) {
      if (groupedPageRows[i].type === 'row') {
        if (dataCount === start) startIdx = i;
        dataCount++;
        if (dataCount === end) { endIdx = i; break; }
      }
    }
    if (startIdx === -1) return [];
    const slice = groupedPageRows.slice(startIdx, endIdx === -1 ? undefined : endIdx + 1);
    // Include trailing summary row if it follows the last included row
    const nextIdx = (endIdx === -1 ? groupedPageRows.length : endIdx) + 1;
    if (nextIdx < groupedPageRows.length && groupedPageRows[nextIdx]?.type === 'summary') {
      slice.push(groupedPageRows[nextIdx]);
    }
    return slice;
  }, [groupedPageRows, safePageGrouped, pageSize]);

  // ── Pagination numbers (must be after totalPagesGrouped & safePageGrouped) ──
  const pageNumbers = useMemo(() => {
    const tp = totalPagesGrouped;
    const sp = safePageGrouped;
    if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1);
    if (sp <= 4)      return [1,2,3,4,5,'…',tp];
    if (sp >= tp - 3) return [1,'…',tp-4,tp-3,tp-2,tp-1,tp];
    return [1,'…',sp-1,sp,sp+1,'…',tp];
  }, [totalPagesGrouped, safePageGrouped]);

  // ── Commission Payments table — grouped from ALL rows (unfiltered) ───────────
  // Independent of the upper table search / date / approval filters so that a
  // referrer always appears here with FULL cumulative totals once their summary
  // row has been cumulatively approved (approvalMap = approved | pending).
  const allGroupedByReferrer = useMemo(() => {
    const groups = {};
    rows.forEach(r => {
      const key = r.referral?.referrer_email || r.referral?.referrer_username || 'unknown';
      if (!groups[key]) {
        groups[key] = {
          rows:        [],
          referrerKey: key,
          refName:     r.referral?.referrer_username || r.referral?.referrer_name || 'Unknown',
          refEmail:    r.referral?.referrer_email || 'Unknown',
        };
      }
      groups[key].rows.push(r);
    });
    return groups;
  }, [rows]);

  const commTableRows = useMemo(() => {
    return Object.values(allGroupedByReferrer)
      .filter(group => {
        const status = approvalMap[group.referrerKey];
        return status === 'approved' || status === 'pending';
      })
      .map(group => {
        const totalComm = group.rows.reduce((s, r) => s + (r.commission_amount || 0), 0);
        const totalPlan = group.rows.reduce((s, r) => s + (r.plan_amount || 0), 0);
        const stored = approvedPayments.find(p => p.referrerKey === group.referrerKey);
        const phone =
          (stored?.payoutPhone && stored.payoutPhone !== '—' ? stored.payoutPhone : null) ||
          referrerMpesaMap[(group.refEmail || '').toLowerCase()] ||
          referrerMpesaMap[(group.referrerKey || '').toLowerCase()] ||
          null;
        return {
          referrerKey:      group.referrerKey,
          approvalId:       stored?.approvalId  || null,
          refName:          group.refName,
          refEmail:         group.refEmail,
          totalCommission:  totalComm,
          totalPlanPayment: totalPlan,
          rowCount:         group.rows.length,
          payoutPhone:      phone || '—',
          payoutReceipt:    stored?.payoutReceipt || null,
          status:           approvalMap[group.referrerKey],
          approvedAt:       stored?.approvedAt || null,
        };
      });
  }, [allGroupedByReferrer, approvalMap, approvedPayments, referrerMpesaMap]);

  // ── Commission Payments table pagination ─────────────────────────────────────
  const totalCommPages = Math.max(1, Math.ceil(commTableRows.length / commPageSize));
  const safeCommPage   = Math.min(commPage, totalCommPages);
  const commFirstIdx   = (safeCommPage - 1) * commPageSize;
  const pagedPayments  = commTableRows.slice(commFirstIdx, commFirstIdx + commPageSize);
  const commPageNumbers = (() => {
    const tp = totalCommPages, sp = safeCommPage;
    if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1);
    if (sp <= 4)      return [1,2,3,4,5,'…',tp];
    if (sp >= tp - 3) return [1,'…',tp-4,tp-3,tp-2,tp-1,tp];
    return [1,'…',sp-1,sp,sp+1,'…',tp];
  })();

  // ── "Approved & Not Paid" table — flat list of individual commission rows ─────
  // A row qualifies if:
  //   • APPROVALS column = "Approved" (individually via rowApprovalSet OR cumulatively via approvalMap)
  //   • PAYMENT STATUS   ≠ "Paid"     (txn ref not in a paid batch AND referrer not globally paid)
  const approvedNotPaidRows = useMemo(() => {
    return rows.filter(r => {
      const txnRef      = r.transaction_ref;
      const referrerKey = r.referral?.referrer_email || r.referral?.referrer_username || 'unknown';
      const refApproval = approvalMap[referrerKey]; // 'approved'|'pending'|'paid'|undefined

      // ── PAYMENT STATUS check — ALL paid signals must be caught ────────────
      // 1. Referrer globally marked paid in approvalMap
      if (refApproval === 'paid') return false;

      // 2. Row directly marked paid on the row object itself
      //    (set by handleMarkRowPaid via Edit dropdown → commission_status / payment_status)
      if (r.commission_status === 'paid' || r.payment_status === 'paid') return false;

      // 3. The batch that contains this TXN ref is marked paid in approvedPayments
      const batchApproval = txnRef
        ? approvedPayments.find(p => Array.isArray(p.approvedTxnRefs) && p.approvedTxnRefs.includes(txnRef))
        : null;
      if (batchApproval?.status === 'paid') return false;

      // 4. The referrer's cumulative approval entry is marked paid
      const cumulApproval = approvedPayments.find(p => p.referrerKey === referrerKey);
      if (cumulApproval?.status === 'paid') return false;

      // ── APPROVALS check — must be Approved ──────────────────────────────
      // Case 1: individually approved via rowApprovalSet
      if (txnRef && rowApprovalSet[txnRef]) return true;
      // Case 2: covered by cumulative approval (not newer than approvedAt)
      const approvedAt = cumulApproval?.approvedAt ? new Date(cumulApproval.approvedAt) : null;
      const rowTs      = r.ad_posted_at || r.joined_at;
      const isNewRow   = approvedAt && rowTs && new Date(rowTs) > approvedAt;
      if (!isNewRow && (refApproval === 'approved' || refApproval === 'pending')) return true;

      return false;
    });
  }, [rows, approvalMap, approvedPayments, rowApprovalSet]);

  // ── Revoke approval for a single row (removes from rowApprovalSet by TXN ref) ─
  const handleRevokeRowApproval = useCallback(async (r) => {
    const txnRef      = r.transaction_ref;
    const referrerKey = r.referral?.referrer_email || r.referral?.referrer_username || 'unknown';
    if (!txnRef) return;
    if (!window.confirm('Revoke approval for this commission? It will return to "Pending Approval".')) return;

    // 1️⃣ Remove from rowApprovalSet
    const updatedRowSet = { ...rowApprovalSet };
    delete updatedRowSet[txnRef];
    setRowApprovalSet(updatedRowSet);

    // 2️⃣ Remove from approvedTxnRefs in approvedPayments (cumulative batch)
    const updatedPayments = approvedPayments.map(p => {
      if (!Array.isArray(p.approvedTxnRefs)) return p;
      return {
        ...p,
        approvedTxnRefs: p.approvedTxnRefs.filter(ref => ref !== txnRef),
      };
    });
    persistApprovals(updatedPayments);

    // 3️⃣ Backend: delete row-level approval if it exists
    try {
      await fetch(`${API_BASE}/admin/referrals/row-approvals/${encodeURIComponent(txnRef)}/`, {
        method: 'DELETE',
        headers: { Authorization: `Token ${getToken()}` },
      });
    } catch { /* silent — non-fatal */ }
  }, [rowApprovalSet, approvedPayments, getToken, persistApprovals]);

  // ── "Approved & Not Paid" table pagination ───────────────────────────────────
  const [anpPage,      setAnpPage]      = useState(1);
  const [anpPageSize,  setAnpPageSize]  = useState(10);
  const [anpPageInput, setAnpPageInput] = useState('10');
  const totalAnpPages = Math.max(1, Math.ceil(approvedNotPaidRows.length / anpPageSize));
  const safeAnpPage   = Math.min(anpPage, totalAnpPages);
  const anpFirstIdx   = (safeAnpPage - 1) * anpPageSize;
  const pagedAnpRows  = approvedNotPaidRows.slice(anpFirstIdx, anpFirstIdx + anpPageSize);
  const anpPageNumbers = (() => {
    const tp = totalAnpPages, sp = safeAnpPage;
    if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1);
    if (sp <= 4)      return [1,2,3,4,5,'…',tp];
    if (sp >= tp - 3) return [1,'…',tp-4,tp-3,tp-2,tp-1,tp];
    return [1,'…',sp-1,sp,sp+1,'…',tp];
  })();
  const handleAnpPageSizeChange = () => {
    const n = parseInt(anpPageInput, 10);
    if (!isNaN(n) && n >= 1 && n <= 500) { setAnpPageSize(n); setAnpPage(1); }
    else setAnpPageInput(String(anpPageSize));
  };

  // ── Page size change handlers ─────────────────────────────────────────────────
  const handlePageSizeChange = () => {
    const n = parseInt(pageSizeInput, 10);
    if (!isNaN(n) && n >= 1 && n <= 500) { setPageSize(n); setCurrentPage(1); }
    else { setPageSizeInput(String(pageSize)); }
  };
  const handleCommPageSizeChange = () => {
    const n = parseInt(commPageInput, 10);
    if (!isNaN(n) && n >= 1 && n <= 500) { setCommPageSize(n); setCommPage(1); }
    else { setCommPageInput(String(commPageSize)); }
  };
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="rup-container">

      {/* ── Header ── */}
      <div className="rup-header">
        <h1 className="rup-title">👥 Referred Users &amp; Commissions Management</h1>
      </div>

      {/* ── Campaign Progress Bar ── */}
      <CampaignBar campaign={campaignData} onUpdate={saveCampaign} />

      {/* ── Stats ── */}
      {loading ? (
        <div className="rup-loading"><div className="rup-spinner" /><p>Loading referral data…</p></div>
      ) : error ? (
        <div className="rup-error">{error}<button className="rup-retry-btn" onClick={fetchData}>Retry</button></div>
      ) : (
        <>
          {/* Stats date filter bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#636e72', whiteSpace: 'nowrap' }}>📅 AD POSTED:</span>
            {[
              { value: 'day',        label: 'Today' },
              { value: 'yesterday',  label: 'Yesterday' },
              { value: 'week',       label: 'This Week' },
              { value: 'last_week',  label: 'Last Week' },
              { value: 'month',      label: 'This Month' },
              { value: 'last_month', label: 'Last Month' },
              { value: 'all',        label: 'All Time' },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setStatsDateFilter(opt.value)}
                style={{
                  padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer',
                  border: `1px solid ${statsDateFilter === opt.value ? '#6c5ce7' : '#dfe6e9'}`,
                  background: statsDateFilter === opt.value ? '#6c5ce7' : '#fff',
                  color: statsDateFilter === opt.value ? '#fff' : '#636e72',
                  transition: 'all 0.15s',
                }}
              >{opt.label}</button>
            ))}
            {statsDateFilter !== 'all' && (
              <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', marginLeft: 4 }}>
                Showing stats for <strong style={{ color: '#6c5ce7' }}>{statsDateLabel}</strong> · {statsFiltered.length} record{statsFiltered.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Cards — first 5 normal, Total Earnings pinned to the right */}
          <div className="rup-stats-grid">
            {statsCards.filter(s => !s.lifetime).map((s, i) => (
              <div key={i} className={`rup-stat-card ${s.dark ? 'rup-stat-card-dark' : ''}`}>
                <div className="rup-stat-icon" style={{ background: `${s.iconColor}15`, color: s.iconColor }}>{s.icon}</div>
                <div className="rup-stat-info">
                  {s.label && <span className={`rup-stat-label ${s.dark ? 'rup-stat-label-dark' : ''}`}>{s.label}{s.hasInfo && <span className="rup-info-icon" title="Pending balance available for payout">ⓘ</span>}</span>}
                  <span className={`rup-stat-value ${s.dark ? 'rup-stat-value-dark' : ''}`}>{s.value}</span>
                  {s.subBg ? <span className="rup-stat-sub-badge" style={{ background: s.subBg, color: s.subColor }}>{s.sub}</span>
                           : <span className="rup-stat-sub" style={{ color: s.subColor }}>{s.sub}</span>}
                </div>
              </div>
            ))}
            {/* Total Earnings — lifetime aggregate, always last/right */}
            {statsCards.filter(s => s.lifetime).map((s, i) => (
              <div key={`lifetime-${i}`} className="rup-stat-card rup-stat-card-dark" style={{ borderLeft: '3px solid #6366f1' }}>
                <div className="rup-stat-icon" style={{ background: `${s.iconColor}15`, color: s.iconColor }}>{s.icon}</div>
                <div className="rup-stat-info">
                  <span className="rup-stat-label rup-stat-label-dark" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {s.label}
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: '#e0e7ff', color: '#6366f1', marginLeft: 4 }}>Lifetime</span>
                  </span>
                  <span className="rup-stat-value rup-stat-value-dark">{s.value}</span>
                  <span className="rup-stat-sub-badge" style={{ background: s.subBg, color: s.subColor }}>{s.sub}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Section header ── */}
      <div className="rup-section-header">
        <h2 className="rup-section-title">
          Referred Users &amp; Commissions
          {!loading && <span className="rup-result-count"> — {filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
              fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 20,
              background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
              whiteSpace: 'nowrap',
            }}>
              ☁️ Synced to backend
            </span>
          <button className="rup-export-btn" onClick={handleExportCSV} disabled={loading || !filtered.length} title="Download CSV">
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* ── Search + Filters ── */}
      <div className="rup-search-row">
        <div className="rup-search-input-wrap">
          <span className="rup-search-icon">🔍</span>
          <input className="rup-search-input" placeholder="Search by new user, referrer, email, ref code, plan type…"
            value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button className="rup-search-clear" onClick={() => setSearch('')} title="Clear">×</button>}
        </div>
        {/* Date filter */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#636e72', whiteSpace: 'nowrap' }}>📅 AD POSTED:</span>
          {[
            { value: 'day',        label: 'Today' },
            { value: 'yesterday',  label: 'Yesterday' },
            { value: 'week',       label: 'This Week' },
            { value: 'last_week',  label: 'Last Week' },
            { value: 'month',      label: 'This Month' },
            { value: 'last_month', label: 'Last Month' },
            { value: 'all',        label: 'All Time' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setDateFilter(opt.value)}
              style={{
                padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', border: `1px solid ${dateFilter === opt.value ? '#6c5ce7' : '#dfe6e9'}`,
                background: dateFilter === opt.value ? '#6c5ce7' : '#fff',
                color: dateFilter === opt.value ? '#fff' : '#636e72',
                transition: 'all 0.15s',
              }}
            >{opt.label}</button>
          ))}
        </div>
        {/* Approval filter */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#636e72', whiteSpace: 'nowrap' }}>✔ APPROVALS:</span>
          {[
            { value: 'all',      label: 'All' },
            { value: 'approved', label: 'Approved' },
            { value: 'pending',  label: 'Pending' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setApprovalFilter(opt.value)}
              style={{
                padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: 'pointer',
                border: `1px solid ${approvalFilter === opt.value ? '#00b894' : '#dfe6e9'}`,
                background: approvalFilter === opt.value ? '#00b894' : '#fff',
                color: approvalFilter === opt.value ? '#fff' : '#636e72',
                transition: 'all 0.15s',
              }}
            >{opt.label}</button>
          ))}
        </div>
      </div>

      {/* ── Toast ── */}
      {disperseMsg && (
        <div className={`rup-toast ${disperseMsg.startsWith('✅') ? 'rup-toast-success' : 'rup-toast-error'}`}>
          <span>{disperseMsg}</span>
          <button className="rup-toast-close" onClick={() => setDisperseMsg('')}>×</button>
        </div>
      )}

      {/* ── Table ── */}
      {!loading && !error && (
        <div className="rup-table-wrap">
          <table className="rup-table">
            <thead>
              <tr>
                <th>NEW USER</th>
                <th>REF CODE</th>
                <th>REFERRER (OLD USER)</th>
                <th>DATE JOINED</th>
                <th>PLAN TYPE</th>
                <th>PLAN PAYMENT</th>
                <th>COMMISSION</th>
                <th>TXN REF</th>
                <th>AD POSTED</th>
                <th>PAYOUT PHONE</th>
                <th style={{ minWidth: 150, textAlign: 'left' }}>APPROVALS</th>
                <th style={{ minWidth: 155, textAlign: 'left' }}>APPROVAL TIME</th>
                <th style={{ minWidth: 145, textAlign: 'left' }}>GROUPED CODE</th>
                <th style={{ minWidth: 130, textAlign: 'left' }}>PAYMENT STATUS</th>
                <th style={{ minWidth: 80, textAlign: 'center' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    ✏️ EDIT
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedGroupRows.length === 0 ? (
                <tr><td colSpan={15} className="rup-empty-cell">
                  {search ? `No results for "${search}"` : 'No referred users yet. Share your referral link to get started.'}
                </td></tr>
              ) : (
                pagedGroupRows.map((item, i) => {
                  /* ── SUMMARY ROW ── */
                  if (item.type === 'summary') {
                    const g = item.data;
                    const approvalStatus     = approvalMap[g.referrerKey];
                    const isApproving        = approvingKey === g.referrerKey;
                    const isPaid             = approvalStatus === 'paid';
                    // ✅ KEY: new commissions after last approvedAt need re-approval
                    const needsReApproval    = approvalStatus === 'approved' && g.hasNewSinceApproval;
                    const isEffectivelyApproved = (
                      (approvalStatus === 'approved' || approvalStatus === 'pending' || isPaid)
                      && !needsReApproval
                    );

                    return (
                      <tr key={`summary-${g.referrerKey}`} style={{
                        background: isPaid
                          ? 'linear-gradient(90deg, #f0fdf4 0%, #dcfce7 100%)'
                          : needsReApproval
                          ? 'linear-gradient(90deg, #fffbeb 0%, #fef3c7 100%)'
                          : 'linear-gradient(90deg, #f0fdf4 0%, #ecfdf5 100%)',
                        borderTop:    `2px solid ${needsReApproval ? '#fcd34d' : '#6ee7b7'}`,
                        borderBottom: `2px solid ${needsReApproval ? '#fcd34d' : '#6ee7b7'}`,
                      }}>
                        <td colSpan={5} style={{ paddingLeft: 16 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: needsReApproval ? '#92400e' : '#065f46' }}>
                              📊 Cumulative — {g.refName}
                            </span>
                            <span style={{ fontSize: 11, color: '#6b7280' }}>
                              ({g.rows.length} transaction{g.rows.length !== 1 ? 's' : ''})
                            </span>
                            {needsReApproval && (
                              <span style={{
                                fontSize: 11, fontWeight: 700,
                                background: '#fef3c7', color: '#92400e',
                                border: '1px solid #fcd34d',
                                padding: '2px 8px', borderRadius: 20,
                              }}>⚠️ New commissions since last approval</span>
                            )}
                          </div>
                        </td>
                        <td style={{ fontWeight: 800, color: '#1a1a2e' }}>{fmtKsh(g.totalPlan)}</td>
                        <td>
                          <span style={{ fontWeight: 800, color: '#00b894', fontSize: 15 }}>
                            {fmtKsh(g.totalComm)}
                          </span>
                        </td>
                        <td colSpan={3} />{/* TXN REF + AD POSTED + PAYOUT PHONE */}

                        {/* APPROVALS */}
                        <td style={{ whiteSpace: 'nowrap', textAlign: 'left', verticalAlign: 'middle' }}>
                          {isEffectivelyApproved ? (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              background: '#d1fae5', color: '#065f46',
                              border: '1px solid #6ee7b7',
                              padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                            }}>✅ Approved</span>
                          ) : (
                            <button
                              onClick={() => handleApprove(g)}
                              disabled={isApproving || isPaid}
                              style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                padding: '7px 18px',
                                background: isPaid ? '#94a3b8' : isApproving ? '#6c5ce7aa' : needsReApproval ? '#f59e0b' : '#6c5ce7',
                                color: '#fff', border: 'none', borderRadius: 10,
                                fontSize: 13, fontWeight: 700,
                                cursor: (isApproving || isPaid) ? 'not-allowed' : 'pointer',
                                transition: 'background 0.2s', minWidth: 120,
                              }}
                            >
                              {isApproving
                                ? <><span className="rup-btn-spinner" /> Approving…</>
                                : needsReApproval ? '🔄 Re-Approve' : '✔ Approve'}
                            </button>
                          )}
                        </td>

                        {/* APPROVAL TIME ── new column */}
                        <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {g.approvedAt ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: needsReApproval ? '#92400e' : '#1e293b' }}>
                                🕐 {fmtDate(g.approvedAt)}
                              </span>
                              <span style={{ fontSize: 10.5, color: '#94a3b8' }}>
                                {new Date(g.approvedAt).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          ) : (
                            <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>
                          )}
                        </td>

                        {/* GROUPED CODE — only shown when the batch is approved-but-NOT-paid.
                            Once paid the batch is closed; the code disappears here.
                            New approvals after payment will generate a fresh code. */}
                        <td style={{ verticalAlign: 'middle' }}>
                          {(() => {
                            // Only show if the cumulative approval exists and is NOT paid
                            const ea = approvedPayments.find(
                              p => p.referrerKey === g.referrerKey && p.status !== 'paid'
                            );
                            const rowGc = !ea
                              ? Object.values(rowApprovalSet)
                                  .find(ra => {
                                    if (ra.referrerKey !== g.referrerKey || !ra.groupCode) return false;
                                    // Only use if the batch for this txn ref is NOT paid
                                    const batch = approvedPayments.find(p =>
                                      Array.isArray(p.approvedTxnRefs) && p.approvedTxnRefs.includes(ra.txnRef)
                                    );
                                    return !batch || batch.status !== 'paid';
                                  })?.groupCode
                              : null;
                            const gc = ea?.groupCode || rowGc;
                            if (!gc) return <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>;
                            return (
                              <span style={{
                                fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
                                background: '#eef2ff', color: '#6366f1',
                                border: '1px solid #c7d2fe',
                                padding: '3px 8px', borderRadius: 6,
                                userSelect: 'all', cursor: 'text', whiteSpace: 'nowrap',
                              }} title='Batch code — all approved-but-unpaid commissions for this referrer share this code'>
                                {gc}
                              </span>
                            );
                          })()}
                        </td>

{/* PAYMENT STATUS */}
                        <td style={{ whiteSpace: 'nowrap', textAlign: 'left', verticalAlign: 'middle' }}>
                          {isPaid ? (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
                              padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                            }}>💚 Paid</span>
                          ) : approvalStatus === 'pending' ? (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa',
                              padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                            }}>⏳ Processing</span>
                          ) : (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0',
                              padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                            }}>— Not Paid</span>
                          )}
                        </td>
                        <td />{/* EDIT column — empty for summary */}
                      </tr>
                    );
                  }

                  /* ── DATA ROW ── */
                  const r        = item.data;
                  const absIdx   = i;
                  const color    = avatarColor(absIdx);
                  const initials = r.initials || getInitials(r.name || '');

                  const planType    = r.premium_type     || 'Free';
                  const planPayment = r.plan_amount       || 0;
                  const commAmt     = r.commission_amount || 0;
                  const pColor      = planColor(planType);
                  const refName     = r.referral?.referrer_username || r.referral?.referrer_name || '—';
                  const refEmail    = r.referral?.referrer_email || '—';
                  const rowKey      = r.id;
                  // Inherit referrer's approval status for this row
                  const rowReferrerKey    = r.referral?.referrer_email || r.referral?.referrer_username || 'unknown';
                  const rowApprovalStatus = approvalMap[rowReferrerKey]; // 'approved'|'pending'|'paid'|undefined
                  const rowIsPaid         = rowApprovalStatus === 'paid';

                  return (
                    <tr key={rowKey}>

                      {/* NEW USER */}
                      <td>
                        <div className="rup-user-cell">
                          <div className="rup-avatar" style={{ background: `${color}20`, color }}>{initials}</div>
                          <div>
                            <div className="rup-user-name">{r.name || '—'}</div>
                            <div className="rup-user-email">{r.email || '—'}</div>
                          </div>
                        </div>
                      </td>

                      {/* REF CODE */}
                      <td>
                        {r.referred_by_code ? (
                          <span style={{
                            fontFamily: 'monospace', fontSize: 11.5, fontWeight: 700,
                            background: '#eef2ff', color: '#6366f1', padding: '2px 8px',
                            borderRadius: 6, border: '1px solid #c7d2fe', letterSpacing: '0.05em',
                          }}>
                            {r.referred_by_code}
                          </span>
                        ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>

                      {/* REFERRER */}
                      <td>
                        <div className="rup-user-name">{refName}</div>
                        <div className="rup-user-email">{refEmail}</div>
                      </td>

                      {/* DATE JOINED */}
                      <td>{fmtDate(r.joined_at)}</td>

                      {/* PLAN TYPE */}
                      <td>
                        <span className="rup-plan-badge" style={{ background: `${pColor}15`, color: pColor, border: `1px solid ${pColor}30` }}>
                          {planType}
                        </span>
                      </td>

                      {/* PLAN PAYMENT */}
                      <td style={{ fontWeight: 700 }}>
                        {planPayment > 0 ? fmtKsh(planPayment) : '—'}
                      </td>

                      {/* COMMISSION */}
                      <td>
                        <span style={{ fontWeight: 700, color: commAmt > 0 ? '#00b894' : 'inherit' }}>
                          {commAmt > 0 ? fmtKsh(commAmt) : '—'}
                        </span>
                      </td>

                      {/* COMM STATUS — removed, merged into APPROVALS column */}

                      {/* TXN REF — click to expand/collapse full code */}
                      <td>
                        {r.transaction_ref ? (
                          <button
                            onClick={() => toggleTxnRef(rowKey)}
                            title={expandedTxnRefs.has(rowKey) ? 'Click to collapse' : 'Click to view full code'}
                            style={{
                              fontFamily: 'monospace',
                              fontSize: 10.5,
                              color: '#475569',
                              background: expandedTxnRefs.has(rowKey) ? '#eef2ff' : 'none',
                              border: expandedTxnRefs.has(rowKey) ? '1px solid #c7d2fe' : 'none',
                              borderRadius: 6,
                              padding: expandedTxnRefs.has(rowKey) ? '3px 7px' : '0',
                              cursor: 'pointer',
                              userSelect: 'all',
                              wordBreak: 'break-all',
                              textAlign: 'left',
                              maxWidth: expandedTxnRefs.has(rowKey) ? 220 : 140,
                              display: 'block',
                              transition: 'all 0.15s',
                              lineHeight: 1.5,
                            }}
                          >
                            {expandedTxnRefs.has(rowKey)
                              ? r.transaction_ref
                              : (r.transaction_ref.length > 16
                                  ? r.transaction_ref.slice(0, 16) + '…'
                                  : r.transaction_ref)
                            }
                          </button>
                        ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>

                      {/* AD POSTED */}
                      <td>
                        {(() => {
                          const dt = fmtDateTime(r.ad_posted_at);
                          return dt ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap' }}>📦 {dt.date}</span>
                              <span style={{ fontSize: 10.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>🕐 {dt.time}</span>
                            </div>
                          ) : <span style={{ color: '#cbd5e1' }}>—</span>;
                        })()}
                      </td>

                      {/* PAYOUT PHONE — referrer's M-Pesa number from their user profile */}
                      <td>
                        {(() => {
                          // Look up the referrer's referral_mpesa from the admin users list.
                          // Falls back to any phone already on the row for backwards compat.
                          const refKey = (r.referral?.referrer_email || r.referral?.referrer_username || '').toLowerCase();
                          const phone  =
                            referrerMpesaMap[refKey] ||
                            r.payout_phone           ||
                            r.referral?.referrer_mpesa ||
                            null;
                          return phone ? (
                            <span style={{ fontFamily: 'monospace', fontSize: 11.5, fontWeight: 700, color: '#0984e3' }}>
                              📱 {phone}
                            </span>
                          ) : <span style={{ color: '#cbd5e1' }}>—</span>;
                        })()}
                      </td>

                      {/* APPROVALS — individual row: TXN-ref-keyed, independent of cumulative approvalMap */}
                      <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>
                        {(() => {
                          const txnRef          = r.transaction_ref;
                          const isRowApproving  = approvingRowKey === rowKey;

                          // ── Paid (referrer-level lock) ──────────────────────
                          if (rowIsPaid) {
                            return (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
                                padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                              }}>💚 Paid</span>
                            );
                          }

                          // ── Individually approved by TXN ref ────────────────
                          if (txnRef && rowApprovalSet[txnRef]) {
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 5,
                                  background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7',
                                  padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                                }}>✅ Approved</span>
                                <button
                                  onClick={() => handleRevokeRowApproval(r)}
                                  title="Revoke this row's approval"
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    background: '#fff0f0', color: '#dc2626', border: '1px solid #fca5a5',
                                    padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                                    cursor: 'pointer', whiteSpace: 'nowrap',
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = '#fff0f0'; }}
                                >🚫 Revoke</button>
                              </div>
                            );
                          }

                          // ── Cumulative approval covers this row ──────────────
                          // Only applies if the row is NOT newer than the last approvedAt.
                          // New rows (after last cumulative approval) must be approved individually.
                          const existingApproval = approvedPayments.find(p => p.referrerKey === rowReferrerKey);
                          const approvedAt = existingApproval?.approvedAt ? new Date(existingApproval.approvedAt) : null;
                          const rowTs      = r.ad_posted_at || r.joined_at;
                          const isNewRow   = approvedAt && rowTs && new Date(rowTs) > approvedAt;

                          if (!isNewRow && (rowApprovalStatus === 'approved' || rowApprovalStatus === 'pending')) {
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 5,
                                  background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7',
                                  padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                                }}>✅ Approved</span>
                                <button
                                  onClick={() => handleRevokeRowApproval(r)}
                                  title="Revoke this row's approval (removes TXN ref from approval batch)"
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    background: '#fff0f0', color: '#dc2626', border: '1px solid #fca5a5',
                                    padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                                    cursor: 'pointer', whiteSpace: 'nowrap',
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = '#fff0f0'; }}
                                >🚫 Revoke</button>
                              </div>
                            );
                          }

                          // ── New row after last cumulative approval ───────────
                          if (isNewRow) {
                            return (
                              <button
                                onClick={() => handleApproveRow(r, item.group)}
                                disabled={isRowApproving}
                                title="This row was added after the last cumulative approval — approve it individually"
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 5,
                                  background: isRowApproving ? '#f59e0baa' : '#f59e0b',
                                  color: '#fff', border: 'none',
                                  padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                                  cursor: isRowApproving ? 'not-allowed' : 'pointer',
                                  transition: 'background 0.15s',
                                }}
                              >
                                {isRowApproving
                                  ? <><span className="rup-btn-spinner" /> Approving…</>
                                  : '⚠️ Approve Row'}
                              </button>
                            );
                          }

                          // ── Not yet approved — show individual approve button ─
                          return (
                            <button
                              onClick={() => handleApproveRow(r, item.group)}
                              disabled={isRowApproving || !txnRef}
                              title={txnRef ? 'Approve this individual commission row' : 'No TXN ref — use cumulative approval'}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                background: !txnRef ? '#e2e8f0' : isRowApproving ? '#6c5ce7aa' : '#6c5ce7',
                                color: !txnRef ? '#94a3b8' : '#fff', border: 'none',
                                padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                                cursor: (!txnRef || isRowApproving) ? 'not-allowed' : 'pointer',
                                transition: 'background 0.15s',
                              }}
                            >
                              {isRowApproving
                                ? <><span className="rup-btn-spinner" /> Approving…</>
                                : txnRef ? '✔ Approve' : '— No TXN'}
                            </button>
                          );
                        })()}
                      </td>

                      {/* APPROVAL TIME — row-level ts takes priority over cumulative */}
                      <td style={{ verticalAlign: 'middle' }}>
                        {(() => {
                          const txnRef = r.transaction_ref;
                          // Priority 1: per-row individual approval timestamp
                          const rowApproval   = txnRef ? rowApprovalSet[txnRef] : null;
                          // Priority 2: cumulative referrer approval timestamp
                          const cumulApproval = approvedPayments.find(p => p.referrerKey === rowReferrerKey);
                          const approvedAtTs  = rowApproval?.approvedAt || cumulApproval?.approvedAt || null;
                          if (!approvedAtTs) return <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>;
                          const dt = fmtDateTime(approvedAtTs);
                          const isRowLevel = !!rowApproval?.approvedAt;
                          return dt ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>🕐 {dt.date}</span>
                              <span style={{ fontSize: 10.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>{dt.time}</span>
                              {isRowLevel && (
                                <span style={{ fontSize: 9.5, color: '#a5b4fc', fontWeight: 600 }}>row-level</span>
                              )}
                            </div>
                          ) : <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>;
                        })()}
                      </td>

                      {/* GROUPED CODE — shows batch code when approved & not yet paid.
                          Priority: cumulative batch code → individual row-level code.
                          Hidden (—) once the batch is paid (payment is complete). */}
                      <td style={{ verticalAlign: 'middle' }}>
                        {(() => {
                          const txnRef = r.transaction_ref;

                          // 1️⃣ Check cumulative batch — only show if NOT paid
                          const batchApproval = txnRef
                            ? approvedPayments.find(p =>
                                Array.isArray(p.approvedTxnRefs) && p.approvedTxnRefs.includes(txnRef)
                              )
                            : null;
                          const batchGc = (batchApproval && batchApproval.status !== 'paid')
                            ? batchApproval.groupCode
                            : null;

                          // 2️⃣ Fallback: individual row-level approval code (also only if not paid)
                          const rowApproval = txnRef ? rowApprovalSet[txnRef] : null;
                          const rowGc = rowApproval?.groupCode || null;

                          const gc = batchGc || rowGc;
                          if (!gc) return <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>;
                          return (
                            <span style={{
                              fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
                              background: '#eef2ff', color: '#6366f1',
                              border: '1px solid #c7d2fe',
                              padding: '3px 8px', borderRadius: 6,
                              userSelect: 'all', cursor: 'text', whiteSpace: 'nowrap',
                            }} title='Batch approval code — groups all approved-but-unpaid commissions for this referrer'>
                              {gc}
                            </span>
                          );
                        })()}
                      </td>

                      {/* PAYMENT STATUS — per-transaction, NOT per-referrer.
                          Priority: row-level commission_status (from DB after marking paid)
                          → batch approval status → "— Not Paid" fallback. */}
                      <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>
                        {(() => {
                          const txnRef = r.transaction_ref;
                          // Row-level paid (set via inline edit → PATCH to backend)
                          const rowPaidDirectly = r.commission_status === 'paid' || r.payment_status === 'paid';

                          // Find any approval entry that explicitly includes this TXN REF
                          const matchingApproval = txnRef
                            ? approvedPayments.find(p =>
                                Array.isArray(p.approvedTxnRefs) && p.approvedTxnRefs.includes(txnRef)
                              )
                            : null;

                          const txnStatus = matchingApproval?.status; // 'approved'|'pending'|'paid'|undefined

                          if (txnStatus === 'paid' || rowIsPaid || rowPaidDirectly) {
                            return (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
                                padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                              }}>💚 Paid</span>
                            );
                          }
                          if (txnStatus === 'pending') {
                            return (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa',
                                padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                              }}>⏳ Processing</span>
                            );
                          }
                          if (txnStatus === 'approved') {
                            return (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7',
                                padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                              }}>✅ Approved</span>
                            );
                          }
                          // Not in any approved batch — default "— Not Paid"
                          return (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              background: '#f1f5f9', color: '#94a3b8', border: '1px solid #e2e8f0',
                              padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                            }}>— Not Paid</span>
                          );
                        })()}
                      </td>

                      {/* EDIT — fully locked when referrer is paid */}
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {rowIsPaid || r.commission_status === 'paid' || r.payment_status === 'paid' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                            <span style={{ fontSize: 12, color: '#b2bec3', fontStyle: 'italic' }}>🔒 Locked</span>
                            <button
                              onClick={() => toggleEditMode(rowKey)}
                              title="[DEV] Temporarily unlock this paid record"
                              style={{
                                background: editModeRows.has(rowKey) ? '#fef3c7' : '#f8fafc',
                                color: editModeRows.has(rowKey) ? '#b45309' : '#94a3b8',
                                border: `1px solid ${editModeRows.has(rowKey) ? '#fde68a' : '#e2e8f0'}`,
                                borderRadius: 8, padding: '4px 8px', fontSize: 11, fontWeight: 700,
                                cursor: 'pointer', transition: 'all 0.15s',
                              }}
                            >{editModeRows.has(rowKey) ? '🔓' : '🔑'}</button>
                            {editModeRows.has(rowKey) && (
                              <button
                                onClick={() => handleDelete(rowKey, r.id)}
                                style={{
                                  background: '#fff0f0', color: '#dc2626', border: '1px solid #fca5a5',
                                  borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 700,
                                  cursor: 'pointer',
                                }}
                              >🗑 Delete</button>
                            )}
                          </div>
                        ) : (() => {
                          const unlocked = editModeRows.has(rowKey);
                          const isSavingPay = !!payStatusSaving[rowKey];
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, minWidth: unlocked ? 200 : 'auto' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <button
                                  onClick={() => toggleEditMode(rowKey)}
                                  title={unlocked ? 'Lock row' : 'Unlock to edit this row'}
                                  style={{
                                    background: unlocked ? '#fef3c7' : '#f1f5f9',
                                    color: unlocked ? '#b45309' : '#94a3b8',
                                    border: `1px solid ${unlocked ? '#fde68a' : '#e2e8f0'}`,
                                    borderRadius: 8, padding: '6px 9px',
                                    fontSize: 14, cursor: 'pointer', transition: 'all 0.15s', lineHeight: 1,
                                  }}
                                >
                                  {unlocked ? '🔓' : '✏️'}
                                </button>
                                <button
                                  onClick={() => unlocked && handleDelete(rowKey, r.id)}
                                  title={unlocked ? 'Delete this record' : 'Click ✏️ first to enable delete'}
                                  disabled={!unlocked}
                                  style={{
                                    background: unlocked ? '#fff0f0' : '#f8fafc',
                                    color: unlocked ? '#dc2626' : '#cbd5e1',
                                    border: `1px solid ${unlocked ? '#fca5a5' : '#e2e8f0'}`,
                                    borderRadius: 8, padding: '6px 12px',
                                    fontSize: 13, fontWeight: 700,
                                    cursor: unlocked ? 'pointer' : 'not-allowed',
                                    transition: 'all 0.15s', opacity: unlocked ? 1 : 0.5,
                                  }}
                                  onMouseEnter={e => { if (unlocked) e.currentTarget.style.background = '#fee2e2'; }}
                                  onMouseLeave={e => { if (unlocked) e.currentTarget.style.background = '#fff0f0'; }}
                                >
                                  🗑 Delete
                                </button>
                              </div>
                              {/* ── Payment status selector (only when unlocked) ── */}
                              {unlocked && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap' }}>Pay status:</span>
                                  <select
                                    defaultValue=""
                                    disabled={isSavingPay}
                                    onChange={e => {
                                      if (e.target.value === 'paid') {
                                        handleMarkRowPaid(rowKey, r.id, r.transaction_ref, rowReferrerKey);
                                      }
                                      e.target.value = '';
                                    }}
                                    style={{
                                      height: 28, padding: '0 8px',
                                      border: '1.5px solid #6c5ce7', borderRadius: 7,
                                      fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                      background: '#fff', color: '#1a1a2e', outline: 'none',
                                    }}
                                  >
                                    <option value="" disabled>— set status —</option>
                                    <option value="paid">💚 Mark as Paid</option>
                                  </select>
                                  {isSavingPay && <span className="rup-btn-spinner" style={{ borderColor: '#6c5ce7', borderTopColor: 'transparent' }} />}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>

                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination — Table 1 ── */}
      {!loading && !error && (
        <div className="rup-pv-pagination">
          <div className="rup-pv-pag-info">
            {dataRowCount === 0
              ? 'No records'
              : `${((safePageGrouped-1)*pageSize)+1}–${Math.min(safePageGrouped*pageSize, dataRowCount)} of ${dataRowCount}`}
          </div>
          <div className="rup-pv-pag-controls">
            <button className="rup-pv-pag-btn" onClick={() => setCurrentPage(p => Math.max(1,p-1))} disabled={safePageGrouped===1}>‹</button>
            {pageNumbers.map((n, idx) =>
              n === '…'
                ? <span key={`e${idx}`} className="rup-pv-pag-ellipsis">…</span>
                : <button key={n} className={`rup-pv-pag-btn${safePageGrouped===n?' active':''}`} onClick={() => setCurrentPage(n)}>{n}</button>
            )}
            <button className="rup-pv-pag-btn" onClick={() => setCurrentPage(p => Math.min(totalPagesGrouped,p+1))} disabled={safePageGrouped===totalPagesGrouped}>›</button>
          </div>
          <div className="rup-pv-rows-ctrl">
            <label className="rup-pv-rows-label">Rows per page:</label>
            <input
              type="number" min="1" max="500"
              value={pageSizeInput}
              onChange={e => setPageSizeInput(e.target.value)}
              onBlur={handlePageSizeChange}
              onKeyDown={e => { if (e.key === 'Enter') handlePageSizeChange(); }}
              className="rup-pv-rows-input"
            />
          </div>
          <div className="rup-pv-pag-info">Page {safePageGrouped} of {totalPagesGrouped}</div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          APPROVED & NOT PAID TABLE — grouped by referrer (cumulative)
      ══════════════════════════════════════════════════════════════════ */}
      {(() => {
        // Group approvedNotPaidRows by referrer key
        const anpGroups = {};
        approvedNotPaidRows.forEach(r => {
          const refKey   = r.referral?.referrer_email || r.referral?.referrer_username || 'unknown';
          const refName  = r.referral?.referrer_username || r.referral?.referrer_name || '—';
          const refEmail = r.referral?.referrer_email || '—';
          if (!anpGroups[refKey]) {
            anpGroups[refKey] = { refKey, refName, refEmail, rows: [], totalPlan: 0, totalComm: 0 };
          }
          anpGroups[refKey].rows.push(r);
          anpGroups[refKey].totalPlan += parseFloat(r.plan_amount || 0);
          anpGroups[refKey].totalComm += parseFloat(r.commission_amount || 0);
        });
        // Extra guard: exclude any group whose cumulative approval batch is already paid
        const anpGroupList = Object.values(anpGroups).filter(g => {
          const ap = approvedPayments.find(p => p.referrerKey === g.refKey);
          return ap?.status !== 'paid' && approvalMap[g.refKey] !== 'paid';
        });

        return (
          <div style={{ marginTop: 36 }}>
            <div className="rup-section-header" style={{ marginBottom: 16 }}>
              <h2 className="rup-section-title">
                ✅ Approved &amp; Not Paid
                <span className="rup-result-count"> — {anpGroupList.length} referrer{anpGroupList.length !== 1 ? 's' : ''} ({approvedNotPaidRows.length} commission{approvedNotPaidRows.length !== 1 ? 's' : ''})</span>
              </h2>
              {anpGroupList.length > 0 && (
                <button
                  className="rup-export-btn"
                  onClick={() => {
                    const hdrs = ['Referrer','Referrer Email','Transactions','Total Plan Payment (KSh)','Total Commission (KSh)','Payout Phone','Payout Receipt','Status','Grouped Code','Approvals','Approval Time'];
                    const csvRows = anpGroupList.map(g => {
                      const ap = approvedPayments.find(p => p.referrerKey === g.refKey);
                      const phone = (ap?.payoutPhone && ap.payoutPhone !== '—') ? ap.payoutPhone
                        : referrerMpesaMap[(g.refEmail||'').toLowerCase()] || '—';
                      return [
                        g.refName, g.refEmail,
                        g.rows.length,
                        g.totalPlan.toFixed(2),
                        g.totalComm.toFixed(2),
                        phone,
                        ap?.payoutReceipt || '—',
                        ap?.status || 'approved',
                        ap?.groupCode || '—',
                        'Approved',
                        ap?.approvedAt ? new Date(ap.approvedAt).toLocaleString('en-KE') : '—',
                      ].map(v => `"${String(v).replace(/"/g,'""')}"`).join(',');
                    });
                    const blob = new Blob(['﻿' + [hdrs.join(','), ...csvRows].join('\n')], { type: 'text/csv;charset=utf-8;' });
                    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'approved-not-paid.csv';
                    document.body.appendChild(a); a.click(); URL.revokeObjectURL(a.href); document.body.removeChild(a);
                  }}
                >
                  📥 Export CSV
                </button>
              )}
            </div>

            <div className="rup-table-wrap">
              <table className="rup-table" style={{ minWidth: 1320 }}>
                <thead>
                  <tr>
                    <th style={{ width: 32 }}></th>{/* expand toggle */}
                    <th>REFERRER (OLD USER)</th>
                    <th style={{ textAlign: 'center' }}>TRANSACTIONS</th>
                    <th>TOTAL PLAN PAYMENT</th>
                    <th>TOTAL COMMISSION</th>
                    <th>PAYOUT PHONE</th>
                    <th>PAYOUT RECEIPT</th>
                    <th>STATUS</th>
                    <th style={{ minWidth: 180 }}>DISPERSE PAYMENT</th>
                    <th>GROUPED CODE</th>
                    <th style={{ textAlign: 'center' }}>APPROVALS</th>
                    <th style={{ minWidth: 155 }}>APPROVAL TIME</th>
                  </tr>
                </thead>
                <tbody>
                  {anpGroupList.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="rup-empty-cell">
                        No approved-but-unpaid commissions. Rows appear here once a referrer is approved.
                      </td>
                    </tr>
                  ) : anpGroupList.map((g, idx) => {
                    const ap        = approvedPayments.find(p => p.referrerKey === g.refKey);
                    const isPaid    = ap?.status === 'paid';
                    const isPending = ap?.status === 'pending';
                    const isDisping = dispersingPayment[g.refKey];
                    // ── GROUPED CODE: read from cumulative approval first,
                    // then fall back to any individual commission row in this group
                    // that has been approved via rowApprovalSet (individual approve button).
                    // This ensures the top cumulative row always shows the same code
                    // that the expanded sub-rows display.
                    const gc = ap?.groupCode ||
                      g.rows.reduce((found, r) => {
                        if (found) return found;
                        const txnRef = r.transaction_ref;
                        return (txnRef && rowApprovalSet[txnRef]?.groupCode) || null;
                      }, null) || null;
                    const approvedAt  = ap?.approvedAt || null;
                    const approvalDt  = approvedAt ? fmtDateTime(approvedAt) : null;
                    const isExpanded  = expandedAnpRows.has(g.refKey);

                    // Payout phone — live lookup fallback
                    const phone = (ap?.payoutPhone && ap.payoutPhone !== '—')
                      ? ap.payoutPhone
                      : referrerMpesaMap[(g.refEmail||'').toLowerCase()] || null;

                    const rowBg = idx % 2 === 0 ? '#fff' : '#fafbfd';

                    return (
                      <React.Fragment key={g.refKey}>
                        <tr style={{ background: rowBg, cursor: 'pointer' }} onClick={() => toggleAnpRow(g.refKey)}>

                          {/* EXPAND TOGGLE */}
                          <td style={{ textAlign: 'center', paddingLeft: 12 }}>
                            <button
                              onClick={e => { e.stopPropagation(); toggleAnpRow(g.refKey); }}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                fontSize: 14, color: '#94a3b8', padding: 2, lineHeight: 1,
                                transition: 'transform 0.2s',
                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                              }}
                              title={isExpanded ? 'Collapse commissions' : 'Expand to see individual commissions'}
                            >▶</button>
                          </td>

                          {/* REFERRER */}
                          <td>
                            <div className="rup-user-cell">
                              <div className="rup-avatar" style={{ background: `${avatarColor(idx)}20`, color: avatarColor(idx) }}>
                                {getInitials(g.refName)}
                              </div>
                              <div>
                                <div className="rup-user-name">{g.refName}</div>
                                <div className="rup-user-email">{g.refEmail}</div>
                              </div>
                            </div>
                          </td>

                          {/* TRANSACTIONS — clickable badge */}
                          <td style={{ textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              background: isExpanded ? '#6366f1' : '#eef2ff',
                              color: isExpanded ? '#fff' : '#6366f1',
                              fontWeight: 700, fontSize: 13, padding: '3px 12px', borderRadius: 20,
                              transition: 'all 0.15s',
                            }}>{g.rows.length} {isExpanded ? '▲' : '▼'}</span>
                          </td>

                          {/* TOTAL PLAN PAYMENT */}
                          <td style={{ fontWeight: 700 }}>{fmtKsh(g.totalPlan)}</td>

                          {/* TOTAL COMMISSION */}
                          <td>
                            <span style={{ fontWeight: 800, color: '#00b894', fontSize: 15 }}>{fmtKsh(g.totalComm)}</span>
                          </td>

                          {/* PAYOUT PHONE */}
                          <td onClick={e => e.stopPropagation()}>
                            {phone
                              ? <span style={{ fontFamily: 'monospace', fontSize: 11.5, fontWeight: 700, color: '#0984e3' }}>📱 {phone}</span>
                              : <span style={{ color: '#cbd5e1' }}>—</span>}
                          </td>

                          {/* PAYOUT RECEIPT */}
                          <td onClick={e => e.stopPropagation()}>
                            {ap?.payoutReceipt ? (
                              <span style={{
                                fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
                                background: '#d1fae5', color: '#065f46',
                                padding: '2px 8px', borderRadius: 6, userSelect: 'all', cursor: 'text',
                              }}>✅ {ap.payoutReceipt}</span>
                            ) : <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>Pending</span>}
                          </td>

                          {/* STATUS */}
                          <td onClick={e => e.stopPropagation()}>
                            <span style={{
                              display: 'inline-block', padding: '4px 14px', borderRadius: 20,
                              fontSize: 13, fontWeight: 600,
                              background: isPaid ? '#dcfce7' : '#d1fae5',
                              color: isPaid ? '#166534' : '#065f46',
                              border: isPaid ? '1px solid #86efac' : '1px solid #6ee7b7',
                            }}>{isPaid ? '💚 Paid' : '✅ Approved'}</span>
                          </td>

                          {/* DISPERSE PAYMENT */}
                          <td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                            {isPaid ? (
                              <span className="rup-action-done">💚 Payment Complete</span>
                            ) : (
                              <button
                                className="rup-action-primary"
                                disabled={!!isDisping || isPending}
                                onClick={e => { e.stopPropagation(); handleDispersePayment(g.refKey, ap?.approvalId); }}
                                style={{ whiteSpace: 'nowrap', minWidth: 160, opacity: isPending ? 0.65 : 1 }}
                                title={isPending ? 'Dispersal in progress…' : 'Send M-Pesa payment'}
                              >
                                {isDisping
                                  ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <span className="rup-btn-spinner" /> Processing…
                                    </span>
                                  : isPending ? '⏳ Processing…' : '💸 Disperse Payment'}
                              </button>
                            )}
                          </td>

                          {/* GROUPED CODE */}
                          <td onClick={e => e.stopPropagation()}>
                            {gc ? (
                              <span style={{
                                fontFamily: 'monospace', fontSize: 12, fontWeight: 700,
                                background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d',
                                padding: '3px 10px', borderRadius: 8, letterSpacing: '0.05em',
                                whiteSpace: 'nowrap', userSelect: 'all', cursor: 'text',
                              }} title="Batch code — all approved-but-unpaid commissions for this referrer share this code">{gc}</span>
                            ) : <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>}
                          </td>

                          {/* APPROVALS — Revoke only when not paid */}
                          <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                            {isPaid ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                                <span style={{ fontSize: 12, color: '#b2bec3', fontStyle: 'italic' }}>🔒 Locked</span>
                                <button
                                  onClick={() => handleRevoke(g.refKey, ap?.approvalId)}
                                  title="[DEV] Force-unlock this paid record"
                                  style={{
                                    background: '#f8fafc', color: '#94a3b8', border: '1px solid #e2e8f0',
                                    borderRadius: 8, padding: '3px 10px', fontSize: 11, fontWeight: 700,
                                    cursor: 'pointer', transition: 'all 0.15s',
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#fff0f0'; e.currentTarget.style.color = '#dc2626'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#94a3b8'; }}
                                >🔑 Unlock</button>
                              </div>
                            ) : (
                              <button
                                onClick={e => { e.stopPropagation(); handleRevoke(g.refKey, ap?.approvalId); }}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                                  background: '#fff0f0', color: '#dc2626', border: '1px solid #fca5a5',
                                  padding: '6px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                                  cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = '#fff0f0'; }}
                              >
                                🚫 Revoke Approval
                              </button>
                            )}
                          </td>

                          {/* APPROVAL TIME */}
                          <td style={{ verticalAlign: 'middle' }} onClick={e => e.stopPropagation()}>
                            {approvalDt ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap' }}>🕐 {approvalDt.date}</span>
                                <span style={{ fontSize: 10.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>{approvalDt.time}</span>
                              </div>
                            ) : <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>}
                          </td>

                        </tr>

                        {/* ── EXPANDED DETAIL ROWS — individual commissions ── */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={12} style={{ padding: 0, background: rowBg }}>
                              <div style={{
                                borderLeft: '3px solid #6366f1',
                                background: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)',
                                padding: '12px 16px 16px 20px',
                              }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                                  📋 {g.rows.length} Commission{g.rows.length !== 1 ? 's' : ''} — {g.refName}
                                </div>
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 860 }}>
                                    <thead>
                                      <tr style={{ background: '#e0e7ff' }}>
                                        <th style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>NEW USER</th>
                                        <th style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>PLAN</th>
                                        <th style={{ padding: '7px 12px', textAlign: 'right', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>PLAN PMT</th>
                                        <th style={{ padding: '7px 12px', textAlign: 'right', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>COMMISSION</th>
                                        <th style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>TXN REF</th>
                                        <th style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>DATE JOINED</th>
                                        <th style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>AD POSTED</th>
                                        <th style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>GROUPED CODE</th>
                                        <th style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>PAY STATUS</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {g.rows.map((cr, ci) => {
                                        const crPlanColor = planColor(cr.premium_type || 'Free');
                                        const crDt = cr.ad_posted_at ? fmtDateTime(cr.ad_posted_at) : null;
                                        const crPaid = cr.commission_status === 'paid' || cr.payment_status === 'paid';
                                        // Check if in a paid batch
                                        const crBatch = cr.transaction_ref
                                          ? approvedPayments.find(p => Array.isArray(p.approvedTxnRefs) && p.approvedTxnRefs.includes(cr.transaction_ref))
                                          : null;
                                        const crTxnStatus = crBatch?.status;
                                        return (
                                          <tr key={cr.id} style={{ background: ci % 2 === 0 ? 'rgba(255,255,255,0.7)' : 'rgba(238,242,255,0.5)', borderBottom: '1px solid #e0e7ff' }}>
                                            <td style={{ padding: '8px 12px' }}>
                                              <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 12.5 }}>{cr.name || '—'}</div>
                                              <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{cr.email || '—'}</div>
                                            </td>
                                            <td style={{ padding: '8px 12px' }}>
                                              <span style={{ background: `${crPlanColor}15`, color: crPlanColor, border: `1px solid ${crPlanColor}30`, padding: '2px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 600 }}>
                                                {cr.premium_type || 'Free'}
                                              </span>
                                            </td>
                                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap' }}>{cr.plan_amount > 0 ? fmtKsh(cr.plan_amount) : '—'}</td>
                                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 800, color: '#00b894', whiteSpace: 'nowrap' }}>{cr.commission_amount > 0 ? fmtKsh(cr.commission_amount) : '—'}</td>
                                            <td style={{ padding: '8px 12px' }}>
                                              {cr.transaction_ref
                                                ? <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#475569', userSelect: 'all', wordBreak: 'break-all', maxWidth: 160, display: 'block' }}>{cr.transaction_ref.length > 20 ? cr.transaction_ref.slice(0,20) + '…' : cr.transaction_ref}</span>
                                                : <span style={{ color: '#cbd5e1' }}>—</span>}
                                            </td>
                                            <td style={{ padding: '8px 12px', fontSize: 12, color: '#475569', whiteSpace: 'nowrap' }}>{fmtDate(cr.joined_at)}</td>
                                            <td style={{ padding: '8px 12px' }}>
                                              {crDt ? (
                                                <div>
                                                  <div style={{ fontSize: 11.5, fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap' }}>📦 {crDt.date}</div>
                                                  <div style={{ fontSize: 10, color: '#94a3b8' }}>🕐 {crDt.time}</div>
                                                </div>
                                              ) : <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>}
                                            </td>
                                            {/* GROUPED CODE — row-level code from DB (rowApprovalSet) or parent batch code */}
                                            <td style={{ padding: '8px 12px' }}>
                                              {(() => {
                                                const crTxnRef = cr.transaction_ref;
                                                // Priority 1: individual row-level code loaded from DB
                                                const rowGc = crTxnRef ? rowApprovalSet[crTxnRef]?.groupCode : null;
                                                // Priority 2: parent cumulative batch code (gc is in outer scope)
                                                const displayGc = rowGc || gc || null;
                                                if (!displayGc) return <span style={{ color: '#cbd5e1', fontSize: 11 }}>—</span>;
                                                return (
                                                  <span style={{
                                                    fontFamily: 'monospace', fontSize: 10.5, fontWeight: 700,
                                                    background: '#fef3c7', color: '#92400e',
                                                    border: '1px solid #fcd34d',
                                                    padding: '2px 7px', borderRadius: 6,
                                                    letterSpacing: '0.04em', whiteSpace: 'nowrap',
                                                    userSelect: 'all', cursor: 'text',
                                                  }} title="Batch payment group code">{displayGc}</span>
                                                );
                                              })()}
                                            </td>
                                            <td style={{ padding: '8px 12px' }}>
                                              {crPaid || crTxnStatus === 'paid' ? (
                                                <span style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 700 }}>💚 Paid</span>
                                              ) : crTxnStatus === 'approved' ? (
                                                <span style={{ background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 700 }}>✅ Approved</span>
                                              ) : crTxnStatus === 'pending' ? (
                                                <span style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 700 }}>⏳ Processing</span>
                                              ) : (
                                                <span style={{ background: '#f1f5f9', color: '#94a3b8', border: '1px solid #e2e8f0', padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 600 }}>— Not Paid</span>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                    <tfoot>
                                      <tr style={{ background: '#e0e7ff', borderTop: '2px solid #c7d2fe' }}>
                                        <td colSpan={2} style={{ padding: '8px 12px', fontSize: 11.5, fontWeight: 700, color: '#4338ca' }}>Subtotal</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 800, color: '#1e293b', fontSize: 12.5, whiteSpace: 'nowrap' }}>{fmtKsh(g.totalPlan)}</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 800, color: '#00b894', fontSize: 13, whiteSpace: 'nowrap' }}>{fmtKsh(g.totalComm)}</td>
                                        <td colSpan={5} style={{ padding: '8px 12px' }} />{/* TXN REF + DATE + AD POSTED + GROUPED CODE + PAY STATUS */}
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Grand total footer */}
            {anpGroupList.length > 0 && (
              <div style={{
                display: 'flex', justifyContent: 'flex-end', gap: 24,
                background: '#fff', border: '1px solid #eef1f6', borderRadius: 12,
                padding: '14px 24px', marginTop: 8,
              }}>
                <span style={{ fontSize: 13, color: '#636e72', fontWeight: 600 }}>
                  Total Referrers: <strong style={{ color: '#1a1a2e' }}>{anpGroupList.length}</strong>
                </span>
                <span style={{ fontSize: 13, color: '#636e72', fontWeight: 600 }}>
                  Total Commission Approved:&nbsp;
                  <strong style={{ color: '#00b894', fontSize: 16 }}>
                    {fmtKsh(anpGroupList.reduce((s, g) => s + g.totalComm, 0))}
                  </strong>
                </span>
              </div>
            )}
          </div>
        );
      })()}

    </div>
  );
};

export default ReferredUsersPage;































// import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import './ReferredUsersPage.css';
// import { useAuthContext } from '../../../../context/AuthContext';

// const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';

// // ─── Avatar colour pool ───────────────────────────────────────────────────────
// const AVATAR_COLORS = ['#6c5ce7', '#00b894', '#e17055', '#0984e3', '#fd79a8', '#fdcb6e'];
// const avatarColor = (idx) => AVATAR_COLORS[idx % AVATAR_COLORS.length];

// // ─── Plan colour map ──────────────────────────────────────────────────────────
// const PLAN_COLORS = {
//   business: '#6c5ce7', premium: '#6c5ce7',
//   pro: '#0984e3', basic: '#fdcb6e', standard: '#00b894', free: '#b2bec3',
// };
// function generateGroupCode() {
//   // 12-char code e.g. 'ABCD-EFGH-JKLM' — groups TXN refs approved together
//   const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
//   let code = '';
//   for (let i = 0; i < 12; i++) {
//     if (i === 4 || i === 8) code += '-';
//     code += chars[Math.floor(Math.random() * chars.length)];
//   }
//   return code;
// }

// const planColor = (type = '') =>
//   PLAN_COLORS[(type || '').toLowerCase()] || '#b2bec3';

// // ─── Helpers ──────────────────────────────────────────────────────────────────
// function getInitials(name = '') {
//   const parts = name.trim().split(/\s+/);
//   if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
//   return name.slice(0, 2).toUpperCase() || '??';
// }
// function fmtDate(iso) {
//   if (!iso) return '—';
//   return new Date(iso).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
// }
// function fmtDateTime(iso) {
//   if (!iso) return null;
//   const d = new Date(iso);
//   return {
//     date: d.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }),
//     time: d.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' }),
//   };
// }
// function fmtKsh(val) {
//   const n = parseFloat(val) || 0;
//   return `KSh ${n.toLocaleString('en-KE', { minimumFractionDigits: 0 })}`;
// }
// function daysBetween(a, b) {
//   return Math.max(0, Math.ceil((new Date(b) - new Date(a)) / 86400000));
// }

// // PAGE_SIZE is now dynamic per-table (see state below)

// // ─── Campaign Progress Bar ────────────────────────────────────────────────────
// function CampaignBar({ campaign, onUpdate }) {
//   const [editing, setEditing] = useState(false);
//   const [startDate, setStartDate] = useState(campaign.start_date || '');
//   const [endDate, setEndDate] = useState(campaign.end_date || '');
//   const [saving, setSaving] = useState(false);

//   const now = new Date();
//   const start = campaign.start_date ? new Date(campaign.start_date) : null;
//   const end   = campaign.end_date   ? new Date(campaign.end_date)   : null;

//   let pct = 0;
//   let daysLeft = null;
//   let statusLabel = 'Not configured';
//   let barColor = '#b2bec3';

//   if (start && end) {
//     const total   = daysBetween(start, end);
//     const elapsed = daysBetween(start, now);
//     pct       = total > 0 ? Math.min(100, Math.round((elapsed / total) * 100)) : 0;
//     daysLeft  = Math.max(0, daysBetween(now, end));
//     if (!campaign.is_active) {
//       statusLabel = '⏸ Paused'; barColor = '#f59e0b';
//     } else if (now < start) {
//       statusLabel = '⏳ Not started'; barColor = '#0984e3'; pct = 0;
//     } else if (now > end) {
//       statusLabel = '✅ Ended'; barColor = '#00b894'; pct = 100;
//     } else {
//       statusLabel = `🟢 Active · ${daysLeft}d left`; barColor = '#6c5ce7';
//     }
//   } else if (campaign.is_active) {
//     statusLabel = '🟢 Running (no end date)'; barColor = '#6c5ce7';
//   }

//   const handleSave = async () => {
//     setSaving(true);
//     await onUpdate({ start_date: startDate || null, end_date: endDate || null });
//     setSaving(false);
//     setEditing(false);
//   };

//   return (
//     <div style={{
//       background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
//       padding: '14px 18px', marginBottom: 18,
//     }}>
//       <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
//         <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
//           <span style={{ fontSize: 15, fontWeight: 800, color: '#1e293b' }}>📅 Campaign Period</span>
//           <span style={{
//             fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20,
//             background: `${barColor}18`, color: barColor, border: `1px solid ${barColor}40`,
//           }}>{statusLabel}</span>
//           {start && end && (
//             <span style={{ fontSize: 11, color: '#94a3b8' }}>
//               {fmtDate(campaign.start_date)} → {fmtDate(campaign.end_date)}
//             </span>
//           )}
//         </div>

//         <div style={{ display: 'flex', gap: 8 }}>
//           {/* ── Pause / Resume ── */}
//           <button
//             onClick={() => onUpdate({ is_active: !campaign.is_active })}
//             style={{
//               padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700,
//               cursor: 'pointer', border: 'none',
//               background: campaign.is_active ? '#fef3c7' : '#d1fae5',
//               color: campaign.is_active ? '#b45309' : '#065f46',
//             }}
//           >
//             {campaign.is_active ? '⏸ Pause Referrals' : '▶ Resume Referrals'}
//           </button>

//           {/* ── Set dates ── */}
//           <button
//             onClick={() => setEditing(e => !e)}
//             style={{
//               padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700,
//               cursor: 'pointer', border: '1px solid #c7d2fe',
//               background: '#eef2ff', color: '#6366f1',
//             }}
//           >
//             📆 {editing ? 'Cancel' : 'Set Dates'}
//           </button>
//         </div>
//       </div>

//       {/* ── Date picker inline form ── */}
//       {editing && (
//         <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, background: '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>
//           <label style={{ fontSize: 12, fontWeight: 700, color: '#6366f1' }}>
//             Start
//             <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
//               style={{ marginLeft: 6, height: 32, padding: '0 8px', border: '1.5px solid #c7d2fe', borderRadius: 7, fontSize: 12 }} />
//           </label>
//           <label style={{ fontSize: 12, fontWeight: 700, color: '#6366f1' }}>
//             End
//             <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
//               min={startDate}
//               style={{ marginLeft: 6, height: 32, padding: '0 8px', border: '1.5px solid #c7d2fe', borderRadius: 7, fontSize: 12 }} />
//           </label>
//           <button onClick={handleSave} disabled={saving}
//             style={{ height: 32, padding: '0 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
//             {saving ? '…' : '✓ Save'}
//           </button>
//         </div>
//       )}

//       {/* ── Progress bar ── */}
//       {start && end ? (
//         <div>
//           <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
//             <span style={{ fontSize: 11, color: '#94a3b8' }}>Progress</span>
//             <span style={{ fontSize: 11, fontWeight: 800, color: barColor }}>{pct}%</span>
//           </div>
//           <div style={{ height: 8, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
//             <div style={{
//               height: '100%', width: `${pct}%`, borderRadius: 99,
//               background: `linear-gradient(90deg, ${barColor}, ${barColor}bb)`,
//               transition: 'width 0.5s ease',
//             }} />
//           </div>
//           <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
//             <span style={{ fontSize: 10, color: '#94a3b8' }}>{fmtDate(campaign.start_date)}</span>
//             <span style={{ fontSize: 10, color: '#94a3b8' }}>{fmtDate(campaign.end_date)}</span>
//           </div>
//         </div>
//       ) : (
//         <div style={{ fontSize: 11.5, color: '#94a3b8', fontStyle: 'italic' }}>
//           Set start &amp; end dates to see a campaign progress bar.
//         </div>
//       )}
//     </div>
//   );
// }

// // =============================================================================
// // MAIN COMPONENT
// // =============================================================================
// const ReferredUsersPage = () => {
//   const { getToken } = useAuthContext();

//   // ── Remote data ──────────────────────────────────────────────────────────────
//   const [stats,           setStats]           = useState(null);
//   const [rows,            setRows]            = useState([]);
//   const [loading,         setLoading]         = useState(true);
//   const [error,           setError]           = useState('');
//   // Maps referrer email → their referral_mpesa number from UserProfile
//   // Built once at fetch time — used in both table PAYOUT PHONE cells and handleApprove
//   const [referrerMpesaMap, setReferrerMpesaMap] = useState({});

//   // ── Campaign state (stored in localStorage — replace with API when ready) ────
//   const [campaign, setCampaign] = useState(() => {
//     try {
//       return JSON.parse(localStorage.getItem('referral_campaign') || '{}');
//     } catch { return {}; }
//   });
//   const saveCampaign = (updates) => {
//     const next = { ...campaign, ...updates };
//     setCampaign(next);
//     localStorage.setItem('referral_campaign', JSON.stringify(next));
//     return Promise.resolve();
//   };
//   const campaignData = { is_active: true, ...campaign };

//   // ── UI ───────────────────────────────────────────────────────────────────────
//   const [search,           setSearch]           = useState('');
//   const [statsDateFilter,  setStatsDateFilter]  = useState('month'); // for stats cards
//   const [dateFilter,       setDateFilter]       = useState('month'); // for table rows
//   const [approvalFilter,   setApprovalFilter]   = useState('all');
//   const [currentPage,    setCurrentPage]    = useState(1);
//   const [pageSize,       setPageSize]       = useState(10);
//   const [pageSizeInput,  setPageSizeInput]  = useState('10');
//   // Commission Payments table — separate pagination
//   const [commPage,       setCommPage]       = useState(1);
//   const [commPageSize,   setCommPageSize]   = useState(10);
//   const [commPageInput,  setCommPageInput]  = useState('10');
//   const [disperseMsg,    setDisperseMsg]    = useState('');
//   const [editModeRows,   setEditModeRows]   = useState(new Set());
//   // ── Expandable TXN REF — set of row IDs where the full ref is shown ──────────
//   const [expandedTxnRefs, setExpandedTxnRefs] = useState(new Set());
//   // ── Expanded rows in Approved & Not Paid table (click to see individual commissions) ─
//   const [expandedAnpRows, setExpandedAnpRows] = useState(new Set());
//   const toggleAnpRow = useCallback((refKey) => {
//     setExpandedAnpRows(prev => {
//       const next = new Set(prev);
//       if (next.has(refKey)) { next.delete(refKey); } else { next.add(refKey); }
//       return next;
//     });
//   }, []);
//   // ── Inline payment-status editing for individual commission rows ──────────────
//   const [payStatusSaving, setPayStatusSaving] = useState({});
//   const toggleTxnRef = useCallback((rowKey) => {
//     setExpandedTxnRefs(prev => {
//       const next = new Set(prev);
//       if (next.has(rowKey)) { next.delete(rowKey); } else { next.add(rowKey); }
//       return next;
//     });
//   }, []);

//   // ── Approval state ────────────────────────────────────────────────────────────
//   // ✅ FIX: localStorage completely removed from approval flow.
//   // All approval state is loaded exclusively from the backend on every fetch,
//   // so all tabs/browsers/sessions always show the same data without stale
//   // localStorage values overriding what's in the database.
//   //
//   // approvalMap:      { [referrerKey]: 'approved' | 'pending' | 'paid' }
//   // approvedPayments: full row objects for the Commission Payments table
//   // rowApprovalSet:   { [txnRef]: { approvedAt, referrerKey } }

//   const [rowApprovalSet,    setRowApprovalSet]    = useState({});
//   const [approvalMap,       setApprovalMap]       = useState({});
//   const [approvedPayments,  setApprovedPayments]  = useState([]);
//   const [approvingKey,      setApprovingKey]      = useState(null);
//   const [approvingRowKey,   setApprovingRowKey]   = useState(null);
//   const [dispersingPayment, setDispersingPayment] = useState({});

//   // ── Helper: update React state — backend is the only source of truth ─────────
//   const persistApprovals = useCallback((payments) => {
//     const newMap = Object.fromEntries(payments.map(p => [p.referrerKey, p.status]));
//     setApprovedPayments(payments);
//     setApprovalMap(newMap);
//     // ✅ No localStorage write — DB is the source of truth
//   }, []);

//   // ── Fetch ────────────────────────────────────────────────────────────────────
//   const fetchData = useCallback(async () => {
//     setLoading(true); setError('');
//     try {
//       const token   = getToken();
//       const headers = { Authorization: `Token ${token}` };

//       // Fetch referrals + users — these endpoints always exist
//       const [statsRes, usersRes] = await Promise.all([
//         fetch(`${API_BASE}/admin/referrals/`, { headers })
//           .then(r => { if (!r.ok) throw new Error(`referrals: ${r.status}`); return r.json(); }),
//         fetch(`${API_BASE}/admin/referrals/referred-users/`, { headers })
//           .then(r => { if (!r.ok) throw new Error(`referred-users: ${r.status}`); return r.json(); }),
//       ]);

//       // ── Build referrer email → referral_mpesa lookup map ─────────────────────
//       // Fetches the admin users list (same source as UsersView REFERRAL MPESA column).
//       // Non-fatal: if this call fails the page still loads, PAYOUT PHONE just shows —
//       let mpesaMap = {};
//       try {
//         const adminUsersRes = await fetch(`${API_BASE}/admin/users/?page_size=1000`, { headers });
//         if (adminUsersRes.ok) {
//           const adminUsersData = await adminUsersRes.json();
//           const userList = adminUsersData?.results || adminUsersData || [];
//           userList.forEach(u => {
//             if (u.email && u.referral_mpesa) {
//               mpesaMap[u.email.toLowerCase()] = u.referral_mpesa;
//             }
//             if (u.username && u.referral_mpesa) {
//               mpesaMap[u.username.toLowerCase()] = u.referral_mpesa;
//             }
//           });
//           setReferrerMpesaMap(mpesaMap);
//         }
//       } catch {
//         // Non-fatal — PAYOUT PHONE column will show — until resolved
//       }

//       // Build stats
//       const referralList    = statsRes.results || [];
//       const totalReferrals  = referralList.reduce((s, r) => s + (r.total_referrals  || 0), 0);
//       const activeReferrals = referralList.reduce((s, r) => s + (r.active_referrals || 0), 0);
//       const totalEarnings   = referralList.reduce((s, r) => s + (r.total_earnings   || 0), 0);
//       const pendingBalance  = referralList.reduce((s, r) => s + (r.pending_balance  || 0), 0);

//       setStats({
//         total_referrals:  totalReferrals,
//         active_referrals: activeReferrals,
//         total_earnings:   totalEarnings,
//         pending_balance:  pendingBalance,
//         withdrawn_total:  totalEarnings - pendingBalance,
//       });

//       setRows(usersRes?.results || []);

//       // ✅ FIX: Load cumulative approvals from backend — no localStorage fallback.
//       // Backend is the single source of truth so all tabs always show the same state.
//       try {
//         const approvalsRes = await fetch(`${API_BASE}/admin/referrals/approvals/`, { headers });
//         if (approvalsRes.ok) {
//           const approvalsData = await approvalsRes.json();
//           const approvals = approvalsData?.results || [];
//           const newPayments = approvals.map(a => {
//             const refEmailKey = (a.referrer_email || '').toLowerCase();
//             const refKeyKey   = (a.referrer_key   || '').toLowerCase();
//             const resolvedPhone =
//               mpesaMap[refEmailKey] ||
//               mpesaMap[refKeyKey]   ||
//               a.payout_phone        ||
//               '—';
//             return {
//               referrerKey:      a.referrer_key,
//               approvalId:       a.id,
//               refName:          a.referrer_name  || a.referrer_key,
//               refEmail:         a.referrer_email || '—',
//               totalCommission:  parseFloat(a.total_commission)   || 0,
//               totalPlanPayment: parseFloat(a.total_plan_payment) || 0,
//               rowCount:         a.row_count      || 0,
//               payoutPhone:      resolvedPhone,
//               payoutReceipt:    a.payout_receipt || null,
//               status:           a.status,
//               approvedAt:       a.approved_at,
//               groupCode:        a.group_code || null,
//               dispersedAt:      a.dispersed_at || null,
//             };
//           });
//           persistApprovals(newPayments);
//         }
//         // If endpoint returns non-200, approvals start empty — user sees unapproved state
//       } catch { /* non-fatal — approvals will show as empty until resolved */ }

//       // ✅ FIX: Load individual row approvals from backend only — no localStorage merge.
//       try {
//         const rowApprovalsRes = await fetch(
//           `${API_BASE}/admin/referrals/row-approvals/`,
//           { headers }
//         );
//         if (rowApprovalsRes.ok) {
//           const rowApprovalsData = await rowApprovalsRes.json();
//           const rowApprovalsList = rowApprovalsData?.results || rowApprovalsData || [];
//           const fromBackend = {};
//           rowApprovalsList.forEach(ra => {
//             if (ra.txn_ref) {
//               fromBackend[ra.txn_ref] = {
//                 approvedAt:  ra.approved_at,
//                 referrerKey: ra.referrer_key,
//                 groupCode:   ra.group_code || null,  // ✅ FIX: load groupCode from backend
//               };
//             }
//           });
//           setRowApprovalSet(fromBackend);  // ✅ backend only — no localStorage merge
//         }
//       } catch { /* non-fatal */ }

//     } catch (err) {
//       console.error('ReferredUsersPage fetch error:', err);
//       setError('Failed to load referral data. Please refresh.');
//     } finally {
//       setLoading(false);
//     }
//   }, [getToken, persistApprovals]);

//   useEffect(() => { fetchData(); }, [fetchData]);
//   useEffect(() => { setCurrentPage(1); }, [search, dateFilter, approvalFilter]);

//   // ── Filter + paginate ────────────────────────────────────────────────────────
//   const filtered = useMemo(() => {
//     const now = new Date();
//     let result = rows;

//     // Date filter by ad_posted_at
//     if (dateFilter !== 'all') {
//       result = result.filter(r => {
//         if (!r.ad_posted_at) return false;
//         const d = new Date(r.ad_posted_at);
//         if (dateFilter === 'day') {
//           return d.toDateString() === now.toDateString();
//         }
//         if (dateFilter === 'yesterday') {
//           const yest = new Date(now); yest.setDate(now.getDate() - 1);
//           return d.toDateString() === yest.toDateString();
//         }
//         if (dateFilter === 'week') {
//           const weekAgo = new Date(now); weekAgo.setDate(now.getDate() - 7);
//           return d >= weekAgo;
//         }
//         if (dateFilter === 'last_week') {
//           // Calendar last week: Mon–Sun of the previous week
//           const dayOfWeek = now.getDay() || 7; // 1=Mon … 7=Sun
//           const thisMonday = new Date(now); thisMonday.setDate(now.getDate() - dayOfWeek + 1); thisMonday.setHours(0,0,0,0);
//           const lastMonday = new Date(thisMonday); lastMonday.setDate(thisMonday.getDate() - 7);
//           const lastSunday = new Date(thisMonday); lastSunday.setMilliseconds(-1);
//           return d >= lastMonday && d <= lastSunday;
//         }
//         if (dateFilter === 'month') {
//           return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
//         }
//         if (dateFilter === 'last_month') {
//           const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
//           const lmEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
//           return d >= lm && d <= lmEnd;
//         }
//         return true;
//       });
//     }

//     // Approval filter
//     if (approvalFilter !== 'all') {
//       result = result.filter(r => {
//         const key    = r.referral?.referrer_email || r.referral?.referrer_username || 'unknown';
//         const status = approvalMap[key]; // 'approved' | 'pending' | 'paid' | undefined
//         if (approvalFilter === 'approved') return status === 'approved' || status === 'paid';
//         if (approvalFilter === 'pending')  return !status || status === 'pending';
//         return true;
//       });
//     }

//     // Text search
//     if (search.trim()) {
//       const q = search.toLowerCase();
//       result = result.filter(r =>
//         (r.name || '').toLowerCase().includes(q) ||
//         (r.email || '').toLowerCase().includes(q) ||
//         (r.referral?.referrer_username || '').toLowerCase().includes(q) ||
//         (r.referral?.referrer_email || '').toLowerCase().includes(q) ||
//         (r.premium_type || '').toLowerCase().includes(q) ||
//         (r.referred_by_code || '').toLowerCase().includes(q)
//       );
//     }

//     return result;
//   }, [rows, search, dateFilter, approvalFilter, approvalMap]);

//   // ── Stats-section filtered rows (uses its own date filter, independent of table) ─
//   const statsFiltered = useMemo(() => {
//     if (statsDateFilter === 'all') return rows;
//     const now = new Date();
//     return rows.filter(r => {
//       if (!r.ad_posted_at) return false;
//       const d = new Date(r.ad_posted_at);
//       if (statsDateFilter === 'day')   return d.toDateString() === now.toDateString();
//       if (statsDateFilter === 'yesterday') {
//         const yest = new Date(now); yest.setDate(now.getDate() - 1);
//         return d.toDateString() === yest.toDateString();
//       }
//       if (statsDateFilter === 'week')  { const wa = new Date(now); wa.setDate(now.getDate() - 7); return d >= wa; }
//       if (statsDateFilter === 'last_week') {
//         const dayOfWeek = now.getDay() || 7;
//         const thisMonday = new Date(now); thisMonday.setDate(now.getDate() - dayOfWeek + 1); thisMonday.setHours(0,0,0,0);
//         const lastMonday = new Date(thisMonday); lastMonday.setDate(thisMonday.getDate() - 7);
//         const lastSunday = new Date(thisMonday); lastSunday.setMilliseconds(-1);
//         return d >= lastMonday && d <= lastSunday;
//       }
//       if (statsDateFilter === 'month') return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
//       if (statsDateFilter === 'last_month') {
//         const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
//         const lmEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
//         return d >= lm && d <= lmEnd;
//       }
//       return true;
//     });
//   }, [rows, statsDateFilter]);

//   const statsPlanTotal  = useMemo(() => statsFiltered.reduce((s, r) => s + (r.plan_amount       || 0), 0), [statsFiltered]);
//   const statsCommTotal  = useMemo(() => statsFiltered.reduce((s, r) => s + (r.commission_amount || 0), 0), [statsFiltered]);
//   const statsTotalRefs  = statsFiltered.length;
//   const statsActiveRefs = useMemo(() => statsFiltered.filter(r => r.is_active).length, [statsFiltered]);
//   const statsPending    = useMemo(() => statsFiltered.reduce((s, r) => s + (r.pending_balance || 0), 0), [statsFiltered]);
//   const statsWithdrawn  = useMemo(() => statsFiltered.reduce((s, r) => s + (r.plan_amount     || 0) - (r.pending_balance || 0), 0), [statsFiltered]);

//   const statsDateLabel = {
//     day: 'Today', yesterday: 'Yesterday',
//     week: 'This Week', last_week: 'Last Week',
//     month: 'This Month', last_month: 'Last Month',
//     all: 'All Time',
//   }[statsDateFilter] || 'All Time';

//   // ── Stats cards ──────────────────────────────────────────────────────────────
//   const statsCards = stats ? [
//     { icon: '👥', label: 'Total Referrals',  value: String(statsTotalRefs),   sub: `↑ +${statsActiveRefs} active`,                                                                                         subColor: '#6c5ce7', iconColor: '#6c5ce7' },
//     { icon: '✅', label: 'Active Referrals',  value: String(statsActiveRefs),  sub: statsTotalRefs > 0 ? `◎ ${Math.round((statsActiveRefs / statsTotalRefs) * 100)}% approved` : '◎ 0% approved',          subColor: '#00b894', iconColor: '#00b894' },
//     { icon: '💼', label: 'Pending Balance',   value: fmtKsh(statsPending),     sub: `● Withdrawn: ${fmtKsh(statsWithdrawn)}`,                                                                               subColor: '#e17055', iconColor: '#e17055', hasInfo: true },
//     { icon: '🧾', label: 'Plan Payment',      value: fmtKsh(statsPlanTotal),   sub: `📅 ${statsDateLabel}`,                                                                                                 subColor: '#0984e3', iconColor: '#0984e3' },
//     { icon: '💰', label: 'Commission',         value: fmtKsh(statsCommTotal),   sub: `📅 ${statsDateLabel}`,                                                                                                 subColor: '#00b894', subBg: '#e0faf0',   iconColor: '#00b894' },
//     { icon: '💸', label: 'Total Earnings',    value: fmtKsh(stats.total_earnings ?? 0), sub: '◆ Disperse Payments',                                                                                         subColor: '#065f46', subBg: '#d1fae5',   iconColor: '#6366f1', dark: true, lifetime: true },
//   ] : [];

//   // ── CSV Export ───────────────────────────────────────────────────────────────
//   const handleExportCSV = () => {
//     if (!filtered.length) return;
//     const now = new Date();
//     const pad = n => String(n).padStart(2, '0');
//     const filename = `referred-users_${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}.csv`;
//     const headers = ['New User Name','New User Email','Ref Code','Referrer','Referrer Email','Date Joined','Plan Type','Plan Payment (KSh)','Commission (KSh)','Comm Status','TXN Ref','Ad Posted At','Payout Phone','Payout Receipt (M-Pesa Code)','Status'];
//     const csvRows = filtered.map(r => {
//       // ✅ All fields now come directly from the row — no txnMap lookup
//       const commStatus = r.commission_status || (r.is_active ? 'Paid' : 'Pending');
//       const refKey     = (r.referral?.referrer_email || r.referral?.referrer_username || '').toLowerCase();
//       const payoutPhone = referrerMpesaMap[refKey] || r.payout_phone || '—';
//       return [
//         r.name  || '—', r.email || '—',
//         r.referred_by_code || '—',
//         r.referral?.referrer_username || '—', r.referral?.referrer_email || '—',
//         fmtDate(r.joined_at),
//         r.premium_type || 'Free',
//         (r.plan_amount || 0).toFixed(2),
//         (r.commission_amount || 0).toFixed(2),
//         commStatus,
//         r.transaction_ref || '—',
//         r.ad_posted_at ? new Date(r.ad_posted_at).toLocaleString('en-KE') : '—',
//         payoutPhone,
//         r.payout_receipt || 'Pending',
//       ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
//     });
//     const blob = new Blob(['\uFEFF' + [headers.join(','), ...csvRows].join('\n')], { type: 'text/csv;charset=utf-8;' });
//     const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename;
//     document.body.appendChild(a); a.click(); URL.revokeObjectURL(a.href); document.body.removeChild(a);
//   };

//   // ── Delete row ───────────────────────────────────────────────────────────────
//   const handleDelete = useCallback(async (rowId, commissionId) => {
//     if (!window.confirm('Delete this commission record? This cannot be undone.')) return;
//     try {
//       const res = await fetch(`${API_BASE}/admin/referrals/commissions/${commissionId}/`, {
//         method: 'DELETE',
//         headers: { Authorization: `Token ${getToken()}` },
//       });
//       if (res.ok || res.status === 204) {
//         setRows(prev => prev.filter(r => r.id !== rowId));
//         setDisperseMsg('✅ Record deleted successfully.');
//       } else {
//         const data = await res.json().catch(() => ({}));
//         setDisperseMsg(`❌ ${data.detail || data.error || 'Delete failed.'}`);
//       }
//     } catch (err) {
//       setDisperseMsg(`❌ ${err?.message || 'Delete failed.'}`);
//     }
//   }, [getToken]);

//   // ── Toggle edit mode for a row (unlocks delete) ───────────────────────────
//   const toggleEditMode = useCallback((rowKey) => {
//     setEditModeRows(prev => {
//       const next = new Set(prev);
//       if (next.has(rowKey)) { next.delete(rowKey); } else { next.add(rowKey); }
//       return next;
//     });
//   }, []);

//   // ── Mark individual commission row as Paid (saves to DB) ────────────────────
//   // Called from the EDIT cell when user selects "Paid" from the dropdown.
//   // PATCHes the commission record's payment_status to 'paid' on the backend.
//   // On success it also updates approvedPayments so the GROUPED CODE clears for that txn.
//   const handleMarkRowPaid = useCallback(async (rowId, commissionId, txnRef, referrerKey) => {
//     setPayStatusSaving(prev => ({ ...prev, [rowId]: true }));
//     try {
//       const res = await fetch(`${API_BASE}/admin/referrals/commissions/${commissionId}/`, {
//         method: 'PATCH',
//         headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
//         body: JSON.stringify({ payment_status: 'paid', commission_status: 'paid' }),
//       });
//       if (res.ok) {
//         // Update the local rows so PAYMENT STATUS shows Paid immediately
//         setRows(prev => prev.map(r =>
//           r.id === rowId ? { ...r, commission_status: 'paid', payment_status: 'paid' } : r
//         ));
//         // Remove this txnRef from its batch's approvedTxnRefs so the grouped code clears
//         if (txnRef) {
//           setApprovedPayments(prev => prev.map(p => {
//             if (!Array.isArray(p.approvedTxnRefs) || !p.approvedTxnRefs.includes(txnRef)) return p;
//             const remaining = p.approvedTxnRefs.filter(r => r !== txnRef);
//             // If all txns in the batch are now paid, mark the batch itself as paid
//             return { ...p, approvedTxnRefs: remaining };
//           }));
//         }
//         // Close edit mode for this row
//         setEditModeRows(prev => { const n = new Set(prev); n.delete(rowId); return n; });
//         setDisperseMsg('✅ Payment status updated to Paid.');
//       } else {
//         const data = await res.json().catch(() => ({}));
//         setDisperseMsg(`❌ ${data.detail || data.error || 'Update failed.'}`);
//       }
//     } catch (err) {
//       setDisperseMsg(`❌ ${err?.message || 'Update failed.'}`);
//     } finally {
//       setPayStatusSaving(prev => { const n = { ...prev }; delete n[rowId]; return n; });
//     }
//   }, [getToken]);

//   // ── Approve ───────────────────────────────────────────────────────────────────
//   // KEY FIX: approved_at is ALWAYS updated to now() on every approval/re-approval.
//   // This is the timestamp the frontend compares against commission ad_posted_at
//   // to detect new commissions that need fresh approval.
//   // Re-approval is always allowed unless the referrer is already paid.
//   const handleApprove = useCallback(async (group) => {
//     const { referrerKey, refName, refEmail, totalComm, totalPlan, rows: groupRows } = group;
//     const existingStatus = approvalMap[referrerKey];
//     // Only block if already paid — 'approved' can always be re-approved
//     if (existingStatus === 'paid') return;
//     setApprovingKey(referrerKey);
//     try {
//       const now = new Date().toISOString();  // ✅ always fresh — never reused from existingPayment
//       const existingPayment = approvedPayments.find(p => p.referrerKey === referrerKey);

//       // ✅ KEY FIX: capture the exact TXN REFs included in this approval batch.
//       // PAYMENT STATUS on individual rows is driven by this set — NOT by the
//       // referrer's global approval status. This prevents new post-approval
//       // commissions from inheriting "Approved" status automatically.
//       const approvedTxnRefs = groupRows
//         .map(r => r.transaction_ref)
//         .filter(Boolean);

//       // ✅ FIX: Resolve groupCode using this priority:
//       //   1. Existing cumulative approval for this referrer — BUT ONLY if NOT yet paid
//       //      (re-approval of unpaid batch → keep same code so rows stay grouped)
//       //      If already paid → the old batch is closed; generate a FRESH code for the new group
//       //   2. Any individual row already in rowApprovalSet for this referrer that is NOT paid
//       //      (rows approved individually before cumulative → share their unpaid code)
//       //   3. Generate a fresh code (first approval, or all previous batches are paid)
//       const unpaidExistingPayment = existingPayment?.status !== 'paid' ? existingPayment : null;
//       const existingRowCode = Object.values(rowApprovalSet)
//         .find(ra => {
//           if (ra.referrerKey !== referrerKey || !ra.groupCode) return false;
//           // Only reuse the code if this TXN ref's batch is NOT paid
//           const batch = approvedPayments.find(p =>
//             Array.isArray(p.approvedTxnRefs) && p.approvedTxnRefs.includes(ra.txnRef)
//           );
//           return !batch || batch.status !== 'paid';
//         })?.groupCode;
//       const groupCode = unpaidExistingPayment?.groupCode || existingRowCode || generateGroupCode();

//       const newPayment = {
//         referrerKey,
//         approvalId:       existingPayment?.approvalId || null,
//         refName:          refName  || referrerKey,
//         refEmail:         refEmail || '—',
//         totalCommission:  totalComm  || 0,
//         totalPlanPayment: totalPlan  || 0,
//         rowCount:         groupRows.length,
//         // ── Payout phone: look up referrer's referral_mpesa from users list ──
//         // referrerMpesaMap is keyed by email (lowercase) and built at fetch time.
//         payoutPhone: (
//           referrerMpesaMap[(refEmail || '').toLowerCase()] ||
//           referrerMpesaMap[(referrerKey || '').toLowerCase()] ||
//           groupRows.find(r => r.payout_phone)?.payout_phone ||
//           '—'
//         ),
//         payoutReceipt:    groupRows.find(r => r.payout_receipt)?.payout_receipt || null,
//         status:           'approved',
//         approvedAt:       now,   // ✅ ALWAYS NOW — refreshes the comparison baseline
//         approvedTxnRefs,         // ✅ per-transaction approval tracking
//         groupCode,               // ✅ NEW — batch identifier for GROUPED CODE column
//         dispersedAt:      null,
//       };

//       // 1️⃣ Optimistic UI update
//       const updated = existingPayment
//         ? approvedPayments.map(p => p.referrerKey === referrerKey ? newPayment : p)
//         : [...approvedPayments, newPayment];
//       persistApprovals(updated);

//       // 2️⃣ Persist to backend — always required (no localStorage fallback) if row exists (UniqueConstraint = one row per referrer)
//       {  // ✅ Always persist to backend
//         const payload = {
//           referrer_key:       referrerKey,
//           referrer_name:      refName,
//           referrer_email:     refEmail,
//           total_commission:   totalComm,
//           total_plan_payment: totalPlan,
//           row_count:          groupRows.length,
//           payout_phone:       newPayment.payoutPhone !== '—' ? newPayment.payoutPhone : null,
//           payout_receipt:     newPayment.payoutReceipt || null,
//           status:             'approved',
//           approved_at:        now,
//           group_code:         groupCode,  // ✅ NEW — batch identifier
//         };
//         // Use PATCH if we have an approvalId, otherwise POST
//         // If POST returns 409 (UniqueConstraint), fall back to a lookup + PATCH
//         const url    = existingPayment?.approvalId
//           ? `${API_BASE}/admin/referrals/approvals/${existingPayment.approvalId}/`
//           : `${API_BASE}/admin/referrals/approvals/`;
//         const method = existingPayment?.approvalId ? 'PATCH' : 'POST';
//         try {
//           let res = await fetch(url, {
//             method,
//             headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
//             body: JSON.stringify(payload),
//           });

//           // If POST hit the UniqueConstraint (409) — fetch the existing row and PATCH it
//           if (!res.ok && method === 'POST' && res.status === 409) {
//             const listRes = await fetch(
//               `${API_BASE}/admin/referrals/approvals/?referrer_key=${encodeURIComponent(referrerKey)}`,
//               { headers: { Authorization: `Token ${getToken()}` } }
//             );
//             if (listRes.ok) {
//               const listData = await listRes.json();
//               const existing = (listData.results || listData)[0];
//               if (existing?.id) {
//                 res = await fetch(`${API_BASE}/admin/referrals/approvals/${existing.id}/`, {
//                   method: 'PATCH',
//                   headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
//                   body: JSON.stringify(payload),
//                 });
//               }
//             }
//           }

//           if (res.ok) {
//             const saved = await res.json();
//             const withId = updated.map(p =>
//               p.referrerKey === referrerKey ? { ...p, approvalId: saved.id } : p
//             );
//             persistApprovals(withId);
//           }
//           // Non-ok responses are silent — optimistic UI update already applied
//         } catch { /* non-fatal */ }
//       }
//     } finally {
//       setApprovingKey(null);
//     }
//   }, [approvalMap, approvedPayments, getToken, persistApprovals]);

//   // ── Approve individual row ────────────────────────────────────────────────────
//   // Tracked PURELY by TXN ref in rowApprovalSet.
//   // Does NOT touch approvalMap or approvedPayments — those are for cumulative only.
//   // This prevents a single row approval from marking all rows as approved.
//   const handleApproveRow = useCallback(async (row, group) => {
//     const txnRef      = row.transaction_ref;
//     const referrerKey = group.referrerKey;
//     const rowKey      = row.id;

//     if (!txnRef) return; // no TXN ref — can't track individually
//     if (approvalMap[referrerKey] === 'paid') return; // whole referrer locked
//     if (rowApprovalSet[txnRef]) return;              // already individually approved

//     setApprovingRowKey(rowKey);
//     try {
//       const now = new Date().toISOString();

//       // ✅ FIX: Resolve groupCode for this individual row using same priority as handleApprove:
//       //   1. Existing cumulative approval for this referrer (approved but NOT paid)
//       //   2. Any other individual row already approved for this referrer whose batch is NOT paid
//       //   3. Generate a fresh code (first approval, or all previous batches are paid)
//       const existingCumulative = approvedPayments.find(
//         p => p.referrerKey === referrerKey && p.status !== 'paid'
//       );
//       const existingRowCode = Object.values(rowApprovalSet)
//         .find(ra => {
//           if (ra.referrerKey !== referrerKey || !ra.groupCode) return false;
//           // Only reuse if the batch containing this row's TXN ref is not yet paid
//           const batch = approvedPayments.find(p =>
//             Array.isArray(p.approvedTxnRefs) && p.approvedTxnRefs.includes(ra.txnRef)
//           );
//           return !batch || batch.status !== 'paid';
//         })?.groupCode;
//       const groupCode = existingCumulative?.groupCode || existingRowCode || generateGroupCode();

//       // 1️⃣ Write only to rowApprovalSet — never to approvalMap
//       const updatedRowSet = {
//         ...rowApprovalSet,
//         [txnRef]: { approvedAt: now, referrerKey, groupCode },
//       };
//       setRowApprovalSet(updatedRowSet);
//       // ✅ No localStorage write — backend is source of truth

//       // 2️⃣ Persist row approval to backend
//       try {
//         const rowSaveRes = await fetch(`${API_BASE}/admin/referrals/row-approvals/`, {
//           method: 'POST',
//           headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
//           body: JSON.stringify({
//             txn_ref:      txnRef,
//             referrer_key: referrerKey,
//             approved_at:  now,
//             group_code:   groupCode,   // ✅ FIX: persist groupCode to backend
//           }),
//         });
//         // 409 = already exists (unique on txn_ref) — not an error, just a no-op
//         if (!rowSaveRes.ok && rowSaveRes.status !== 409) {
//           console.warn('[row-approvals] POST failed:', rowSaveRes.status, 'txn_ref:', txnRef);
//         }
//       } catch (err) {
//         console.warn('[row-approvals] backend sync failed (non-fatal):', err?.message);
//       }
//     } finally {
//       setApprovingRowKey(null);
//     }
//   }, [approvalMap, rowApprovalSet, getToken]);

//   // ── Revoke individual row approval ───────────────────────────────────────────
//   // Removes a single TXN ref from rowApprovalSet and deletes it from the backend.
//   // Uses the TXN ref string directly in the URL — no integer PK needed.
//   const handleRevokeRow = useCallback(async (txnRef) => {
//     if (!txnRef || !rowApprovalSet[txnRef]) return; // not approved — nothing to revoke

//     // 1️⃣ Optimistic UI update — remove from local state immediately
//     const updatedRowSet = { ...rowApprovalSet };
//     delete updatedRowSet[txnRef];
//     setRowApprovalSet(updatedRowSet);

//     // 2️⃣ DELETE from backend — URL uses txn_ref string (not int pk)
//     try {
//       const res = await fetch(
//         `${API_BASE}/admin/referrals/row-approvals/${encodeURIComponent(txnRef)}/`,
//         {
//           method: 'DELETE',
//           headers: { Authorization: `Token ${getToken()}` },
//         }
//       );
//       if (!res.ok && res.status !== 404) {
//         // Revert optimistic update if backend returned unexpected error
//         setRowApprovalSet(prev => ({ ...prev, [txnRef]: rowApprovalSet[txnRef] }));
//         console.warn('[row-approvals] DELETE failed:', res.status, 'txn_ref:', txnRef);
//       }
//     } catch (err) {
//       // Revert optimistic update on network error
//       setRowApprovalSet(prev => ({ ...prev, [txnRef]: rowApprovalSet[txnRef] }));
//       console.warn('[row-approvals] revoke failed (non-fatal):', err?.message);
//     }
//   }, [rowApprovalSet, getToken]);

//   // ── Revoke cumulative approval ────────────────────────────────────────────────
//   const handleRevoke = useCallback(async (referrerKey, approvalId) => {
//     if (approvalMap[referrerKey] === 'paid') return;
//     if (!window.confirm('Revoke this approval? The referrer will be moved back to unapproved.')) return;

//     // 1️⃣ Optimistic UI update — remove from local state and localStorage immediately
//     const updated = approvedPayments.filter(p => p.referrerKey !== referrerKey);
//     persistApprovals(updated);

//     // 2️⃣ Delete from backend
//     // Strategy: use the known approvalId directly if we have it.
//     // If not (e.g. approval was stored in localStorage before backend sync),
//     // look up the row by referrer_key first, then DELETE it.
//     try {
//       const headers = { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' };

//       let idToDelete = approvalId;

//       if (!idToDelete) {
//         // Lookup the backend row by referrer_key to get its id
//         const lookupRes = await fetch(
//           `${API_BASE}/admin/referrals/approvals/?referrer_key=${encodeURIComponent(referrerKey)}`,
//           { headers }
//         );
//         if (lookupRes.ok) {
//           const lookupData = await lookupRes.json();
//           const existing = (lookupData?.results || lookupData)?.[0];
//           idToDelete = existing?.id || null;
//         }
//       }

//       if (idToDelete) {
//         const deleteRes = await fetch(
//           `${API_BASE}/admin/referrals/approvals/${idToDelete}/`,
//           { method: 'DELETE', headers }
//         );
//         if (!deleteRes.ok && deleteRes.status !== 404) {
//           // DELETE failed — re-fetch to resync state with whatever the backend actually has
//           console.error(`Revoke DELETE failed: ${deleteRes.status}`);
//           fetchData();
//         }
//       }
//       // If idToDelete is still null, the row never reached the backend —
//       // localStorage was already cleared above, so UI is correct.
//     } catch (err) {
//       console.error('Revoke error:', err);
//       // On network error, re-fetch so UI reflects true backend state on next render
//       fetchData();
//     }
//   }, [approvalMap, approvedPayments, getToken, persistApprovals, fetchData]);

//   // ── Disperse payment ─────────────────────────────────────────────────────────
//   const handleDispersePayment = useCallback(async (referrerKey, approvalId) => {
//     if (approvalMap[referrerKey] === 'paid') return;
//     setDispersingPayment(prev => ({ ...prev, [referrerKey]: true }));
//     setDisperseMsg('');
//     try {
//       // 1️⃣ Optimistic UI update — mark as 'pending'
//       const updated = approvedPayments.map(p =>
//         p.referrerKey === referrerKey ? { ...p, status: 'pending' } : p
//       );
//       persistApprovals(updated);

//       // 2️⃣ PATCH backend status
//       if (approvalId) {
//         try {
//           await fetch(`${API_BASE}/admin/referrals/approvals/${approvalId}/`, {
//             method: 'PATCH',
//             headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
//             body: JSON.stringify({ status: 'pending' }),
//           });
//         } catch { /* silent */ }
//       }

//       // 3️⃣ Trigger M-Pesa payout
//       const withdrawRes = await fetch(`${API_BASE}/referrals/withdraw/`, {
//         method: 'POST',
//         headers: { Authorization: `Token ${getToken()}`, 'Content-Type': 'application/json' },
//         body: JSON.stringify({}),
//       });
//       const withdrawData = await withdrawRes.json();
//       if (withdrawRes.ok) {
//         setDisperseMsg('✅ Payment dispersal initiated — M-Pesa payout is processing.');
//         // If backend returns a receipt, mark as paid
//         if (withdrawData?.receipt || withdrawData?.transaction_id) {
//           const paid = approvedPayments.map(p =>
//             p.referrerKey === referrerKey
//               ? { ...p, status: 'paid', payoutReceipt: withdrawData.receipt || withdrawData.transaction_id, dispersedAt: new Date().toISOString() }
//               : p
//           );
//           persistApprovals(paid);
//         }
//         await fetchData();
//       } else {
//         setDisperseMsg(`❌ ${withdrawData.detail || withdrawData.error || 'Dispersal failed.'}`);
//         // Revert optimistic update
//         const reverted = approvedPayments.map(p =>
//           p.referrerKey === referrerKey ? { ...p, status: 'approved' } : p
//         );
//         persistApprovals(reverted);
//       }
//     } catch (err) {
//       setDisperseMsg(`❌ ${err?.message || 'Dispersal failed.'}`);
//       const reverted = approvedPayments.map(p =>
//         p.referrerKey === referrerKey ? { ...p, status: 'approved' } : p
//       );
//       persistApprovals(reverted);
//     } finally {
//       setDispersingPayment(prev => ({ ...prev, [referrerKey]: false }));
//     }
//   }, [approvalMap, approvedPayments, getToken, persistApprovals, fetchData]);
//   const groupedByReferrer = useMemo(() => {
//     const groups = {};
//     filtered.forEach(r => {
//       const key = r.referral?.referrer_email || r.referral?.referrer_username || 'unknown';
//       if (!groups[key]) {
//         groups[key] = {
//           rows: [],
//           referrerKey: key,
//           refName:  r.referral?.referrer_username || r.referral?.referrer_name || '—',
//           refEmail: r.referral?.referrer_email || '—',
//         };
//       }
//       groups[key].rows.push(r);
//     });
//     return groups;
//   }, [filtered]);

//   // Flattened list with summary rows injected after each referrer group
//   const groupedPageRows = useMemo(() => {
//     const flat = [];
//     Object.values(groupedByReferrer).forEach(group => {
//       group.rows.forEach(r => flat.push({ type: 'row', data: r, group }));
//       const totalComm = group.rows.reduce((s, r) => s + (r.commission_amount || 0), 0);
//       const totalPlan = group.rows.reduce((s, r) => s + (r.plan_amount || 0), 0);

//       // ── Stale-approval detection ──────────────────────────────────────────
//       // If any commission's ad_posted_at is AFTER the last approvedAt,
//       // the summary row needs re-approval — not just approved.
//       const existingApproval = approvedPayments.find(p => p.referrerKey === group.referrerKey);
//       const approvedAt = existingApproval?.approvedAt ? new Date(existingApproval.approvedAt) : null;
//       const hasNewSinceApproval = approvedAt
//         ? group.rows.some(r => {
//             const ts = r.ad_posted_at || r.joined_at;
//             return ts && new Date(ts) > approvedAt;
//           })
//         : false;

//       flat.push({ type: 'summary', data: {
//         ...group, totalComm, totalPlan,
//         hasNewSinceApproval,
//         approvedAt: existingApproval?.approvedAt || null,
//       }});
//     });
//     return flat;
//   }, [groupedByReferrer, approvedPayments]);

//   // Page only data rows
//   const dataRowCount = useMemo(() => groupedPageRows.filter(r => r.type === 'row').length, [groupedPageRows]);
//   const totalPagesGrouped = Math.max(1, Math.ceil(dataRowCount / pageSize));
//   const safePageGrouped   = Math.min(currentPage, totalPagesGrouped);

//   const pagedGroupRows = useMemo(() => {    const start = (safePageGrouped - 1) * pageSize;
//     const end   = start + pageSize;
//     let dataCount = 0, startIdx = -1, endIdx = -1;
//     for (let i = 0; i < groupedPageRows.length; i++) {
//       if (groupedPageRows[i].type === 'row') {
//         if (dataCount === start) startIdx = i;
//         dataCount++;
//         if (dataCount === end) { endIdx = i; break; }
//       }
//     }
//     if (startIdx === -1) return [];
//     const slice = groupedPageRows.slice(startIdx, endIdx === -1 ? undefined : endIdx + 1);
//     // Include trailing summary row if it follows the last included row
//     const nextIdx = (endIdx === -1 ? groupedPageRows.length : endIdx) + 1;
//     if (nextIdx < groupedPageRows.length && groupedPageRows[nextIdx]?.type === 'summary') {
//       slice.push(groupedPageRows[nextIdx]);
//     }
//     return slice;
//   }, [groupedPageRows, safePageGrouped, pageSize]);

//   // ── Pagination numbers (must be after totalPagesGrouped & safePageGrouped) ──
//   const pageNumbers = useMemo(() => {
//     const tp = totalPagesGrouped;
//     const sp = safePageGrouped;
//     if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1);
//     if (sp <= 4)      return [1,2,3,4,5,'…',tp];
//     if (sp >= tp - 3) return [1,'…',tp-4,tp-3,tp-2,tp-1,tp];
//     return [1,'…',sp-1,sp,sp+1,'…',tp];
//   }, [totalPagesGrouped, safePageGrouped]);

//   // ── Commission Payments table — grouped from ALL rows (unfiltered) ───────────
//   // Independent of the upper table search / date / approval filters so that a
//   // referrer always appears here with FULL cumulative totals once their summary
//   // row has been cumulatively approved (approvalMap = approved | pending).
//   const allGroupedByReferrer = useMemo(() => {
//     const groups = {};
//     rows.forEach(r => {
//       const key = r.referral?.referrer_email || r.referral?.referrer_username || 'unknown';
//       if (!groups[key]) {
//         groups[key] = {
//           rows:        [],
//           referrerKey: key,
//           refName:     r.referral?.referrer_username || r.referral?.referrer_name || 'Unknown',
//           refEmail:    r.referral?.referrer_email || 'Unknown',
//         };
//       }
//       groups[key].rows.push(r);
//     });
//     return groups;
//   }, [rows]);

//   const commTableRows = useMemo(() => {
//     return Object.values(allGroupedByReferrer)
//       .filter(group => {
//         const status = approvalMap[group.referrerKey];
//         return status === 'approved' || status === 'pending';
//       })
//       .map(group => {
//         const totalComm = group.rows.reduce((s, r) => s + (r.commission_amount || 0), 0);
//         const totalPlan = group.rows.reduce((s, r) => s + (r.plan_amount || 0), 0);
//         const stored = approvedPayments.find(p => p.referrerKey === group.referrerKey);
//         const phone =
//           (stored?.payoutPhone && stored.payoutPhone !== '—' ? stored.payoutPhone : null) ||
//           referrerMpesaMap[(group.refEmail || '').toLowerCase()] ||
//           referrerMpesaMap[(group.referrerKey || '').toLowerCase()] ||
//           null;
//         return {
//           referrerKey:      group.referrerKey,
//           approvalId:       stored?.approvalId  || null,
//           refName:          group.refName,
//           refEmail:         group.refEmail,
//           totalCommission:  totalComm,
//           totalPlanPayment: totalPlan,
//           rowCount:         group.rows.length,
//           payoutPhone:      phone || '—',
//           payoutReceipt:    stored?.payoutReceipt || null,
//           status:           approvalMap[group.referrerKey],
//           approvedAt:       stored?.approvedAt || null,
//         };
//       });
//   }, [allGroupedByReferrer, approvalMap, approvedPayments, referrerMpesaMap]);

//   // ── Commission Payments table pagination ─────────────────────────────────────
//   const totalCommPages = Math.max(1, Math.ceil(commTableRows.length / commPageSize));
//   const safeCommPage   = Math.min(commPage, totalCommPages);
//   const commFirstIdx   = (safeCommPage - 1) * commPageSize;
//   const pagedPayments  = commTableRows.slice(commFirstIdx, commFirstIdx + commPageSize);
//   const commPageNumbers = (() => {
//     const tp = totalCommPages, sp = safeCommPage;
//     if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1);
//     if (sp <= 4)      return [1,2,3,4,5,'…',tp];
//     if (sp >= tp - 3) return [1,'…',tp-4,tp-3,tp-2,tp-1,tp];
//     return [1,'…',sp-1,sp,sp+1,'…',tp];
//   })();

//   // ── "Approved & Not Paid" table — flat list of individual commission rows ─────
//   // A row qualifies if:
//   //   • APPROVALS column = "Approved" (individually via rowApprovalSet OR cumulatively via approvalMap)
//   //   • PAYMENT STATUS   ≠ "Paid"     (txn ref not in a paid batch AND referrer not globally paid)
//   const approvedNotPaidRows = useMemo(() => {
//     return rows.filter(r => {
//       const txnRef         = r.transaction_ref;
//       const referrerKey    = r.referral?.referrer_email || r.referral?.referrer_username || 'unknown';
//       const refApproval    = approvalMap[referrerKey]; // 'approved'|'pending'|'paid'|undefined
//       const rowIsPaidGlobal = refApproval === 'paid';

//       // ── PAYMENT STATUS check — must NOT be paid ──────────────────────────
//       if (rowIsPaidGlobal) return false; // referrer paid → skip
//       const batchApproval  = txnRef
//         ? approvedPayments.find(p => Array.isArray(p.approvedTxnRefs) && p.approvedTxnRefs.includes(txnRef))
//         : null;
//       if (batchApproval?.status === 'paid') return false; // this txn specifically paid → skip

//       // ── APPROVALS check — must be Approved ──────────────────────────────
//       // Case 1: individually approved via rowApprovalSet
//       if (txnRef && rowApprovalSet[txnRef]) return true;
//       // Case 2: covered by cumulative approval (not newer than approvedAt)
//       const existingApproval = approvedPayments.find(p => p.referrerKey === referrerKey);
//       const approvedAt       = existingApproval?.approvedAt ? new Date(existingApproval.approvedAt) : null;
//       const rowTs            = r.ad_posted_at || r.joined_at;
//       const isNewRow         = approvedAt && rowTs && new Date(rowTs) > approvedAt;
//       if (!isNewRow && (refApproval === 'approved' || refApproval === 'pending')) return true;

//       return false;
//     });
//   }, [rows, approvalMap, approvedPayments, rowApprovalSet]);

//   // ── Revoke approval for a single row (removes from rowApprovalSet by TXN ref) ─
//   const handleRevokeRowApproval = useCallback(async (r) => {
//     const txnRef      = r.transaction_ref;
//     const referrerKey = r.referral?.referrer_email || r.referral?.referrer_username || 'unknown';
//     if (!txnRef) return;
//     if (!window.confirm('Revoke approval for this commission? It will return to "Pending Approval".')) return;

//     // 1️⃣ Remove from rowApprovalSet
//     const updatedRowSet = { ...rowApprovalSet };
//     delete updatedRowSet[txnRef];
//     setRowApprovalSet(updatedRowSet);

//     // 2️⃣ Remove from approvedTxnRefs in approvedPayments (cumulative batch)
//     const updatedPayments = approvedPayments.map(p => {
//       if (!Array.isArray(p.approvedTxnRefs)) return p;
//       return {
//         ...p,
//         approvedTxnRefs: p.approvedTxnRefs.filter(ref => ref !== txnRef),
//       };
//     });
//     persistApprovals(updatedPayments);

//     // 3️⃣ Backend: delete row-level approval if it exists
//     try {
//       await fetch(`${API_BASE}/admin/referrals/row-approvals/${encodeURIComponent(txnRef)}/`, {
//         method: 'DELETE',
//         headers: { Authorization: `Token ${getToken()}` },
//       });
//     } catch { /* silent — non-fatal */ }
//   }, [rowApprovalSet, approvedPayments, getToken, persistApprovals]);

//   // ── "Approved & Not Paid" table pagination ───────────────────────────────────
//   const [anpPage,      setAnpPage]      = useState(1);
//   const [anpPageSize,  setAnpPageSize]  = useState(10);
//   const [anpPageInput, setAnpPageInput] = useState('10');
//   const totalAnpPages = Math.max(1, Math.ceil(approvedNotPaidRows.length / anpPageSize));
//   const safeAnpPage   = Math.min(anpPage, totalAnpPages);
//   const anpFirstIdx   = (safeAnpPage - 1) * anpPageSize;
//   const pagedAnpRows  = approvedNotPaidRows.slice(anpFirstIdx, anpFirstIdx + anpPageSize);
//   const anpPageNumbers = (() => {
//     const tp = totalAnpPages, sp = safeAnpPage;
//     if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1);
//     if (sp <= 4)      return [1,2,3,4,5,'…',tp];
//     if (sp >= tp - 3) return [1,'…',tp-4,tp-3,tp-2,tp-1,tp];
//     return [1,'…',sp-1,sp,sp+1,'…',tp];
//   })();
//   const handleAnpPageSizeChange = () => {
//     const n = parseInt(anpPageInput, 10);
//     if (!isNaN(n) && n >= 1 && n <= 500) { setAnpPageSize(n); setAnpPage(1); }
//     else setAnpPageInput(String(anpPageSize));
//   };

//   // ── Page size change handlers ─────────────────────────────────────────────────
//   const handlePageSizeChange = () => {
//     const n = parseInt(pageSizeInput, 10);
//     if (!isNaN(n) && n >= 1 && n <= 500) { setPageSize(n); setCurrentPage(1); }
//     else { setPageSizeInput(String(pageSize)); }
//   };
//   const handleCommPageSizeChange = () => {
//     const n = parseInt(commPageInput, 10);
//     if (!isNaN(n) && n >= 1 && n <= 500) { setCommPageSize(n); setCommPage(1); }
//     else { setCommPageInput(String(commPageSize)); }
//   };
//   // ═══════════════════════════════════════════════════════════════════════════
//   return (
//     <div className="rup-container">

//       {/* ── Header ── */}
//       <div className="rup-header">
//         <h1 className="rup-title">👥 Referred Users &amp; Commissions Management</h1>
//       </div>

//       {/* ── Campaign Progress Bar ── */}
//       <CampaignBar campaign={campaignData} onUpdate={saveCampaign} />

//       {/* ── Stats ── */}
//       {loading ? (
//         <div className="rup-loading"><div className="rup-spinner" /><p>Loading referral data…</p></div>
//       ) : error ? (
//         <div className="rup-error">{error}<button className="rup-retry-btn" onClick={fetchData}>Retry</button></div>
//       ) : (
//         <>
//           {/* Stats date filter bar */}
//           <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
//             <span style={{ fontSize: 12, fontWeight: 700, color: '#636e72', whiteSpace: 'nowrap' }}>📅 AD POSTED:</span>
//             {[
//               { value: 'day',        label: 'Today' },
//               { value: 'yesterday',  label: 'Yesterday' },
//               { value: 'week',       label: 'This Week' },
//               { value: 'last_week',  label: 'Last Week' },
//               { value: 'month',      label: 'This Month' },
//               { value: 'last_month', label: 'Last Month' },
//               { value: 'all',        label: 'All Time' },
//             ].map(opt => (
//               <button
//                 key={opt.value}
//                 onClick={() => setStatsDateFilter(opt.value)}
//                 style={{
//                   padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
//                   cursor: 'pointer',
//                   border: `1px solid ${statsDateFilter === opt.value ? '#6c5ce7' : '#dfe6e9'}`,
//                   background: statsDateFilter === opt.value ? '#6c5ce7' : '#fff',
//                   color: statsDateFilter === opt.value ? '#fff' : '#636e72',
//                   transition: 'all 0.15s',
//                 }}
//               >{opt.label}</button>
//             ))}
//             {statsDateFilter !== 'all' && (
//               <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', marginLeft: 4 }}>
//                 Showing stats for <strong style={{ color: '#6c5ce7' }}>{statsDateLabel}</strong> · {statsFiltered.length} record{statsFiltered.length !== 1 ? 's' : ''}
//               </span>
//             )}
//           </div>

//           {/* Cards — first 5 normal, Total Earnings pinned to the right */}
//           <div className="rup-stats-grid">
//             {statsCards.filter(s => !s.lifetime).map((s, i) => (
//               <div key={i} className={`rup-stat-card ${s.dark ? 'rup-stat-card-dark' : ''}`}>
//                 <div className="rup-stat-icon" style={{ background: `${s.iconColor}15`, color: s.iconColor }}>{s.icon}</div>
//                 <div className="rup-stat-info">
//                   {s.label && <span className={`rup-stat-label ${s.dark ? 'rup-stat-label-dark' : ''}`}>{s.label}{s.hasInfo && <span className="rup-info-icon" title="Pending balance available for payout">ⓘ</span>}</span>}
//                   <span className={`rup-stat-value ${s.dark ? 'rup-stat-value-dark' : ''}`}>{s.value}</span>
//                   {s.subBg ? <span className="rup-stat-sub-badge" style={{ background: s.subBg, color: s.subColor }}>{s.sub}</span>
//                            : <span className="rup-stat-sub" style={{ color: s.subColor }}>{s.sub}</span>}
//                 </div>
//               </div>
//             ))}
//             {/* Total Earnings — lifetime aggregate, always last/right */}
//             {statsCards.filter(s => s.lifetime).map((s, i) => (
//               <div key={`lifetime-${i}`} className="rup-stat-card rup-stat-card-dark" style={{ borderLeft: '3px solid #6366f1' }}>
//                 <div className="rup-stat-icon" style={{ background: `${s.iconColor}15`, color: s.iconColor }}>{s.icon}</div>
//                 <div className="rup-stat-info">
//                   <span className="rup-stat-label rup-stat-label-dark" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
//                     {s.label}
//                     <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: '#e0e7ff', color: '#6366f1', marginLeft: 4 }}>Lifetime</span>
//                   </span>
//                   <span className="rup-stat-value rup-stat-value-dark">{s.value}</span>
//                   <span className="rup-stat-sub-badge" style={{ background: s.subBg, color: s.subColor }}>{s.sub}</span>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </>
//       )}

//       {/* ── Section header ── */}
//       <div className="rup-section-header">
//         <h2 className="rup-section-title">
//           Referred Users &amp; Commissions
//           {!loading && <span className="rup-result-count"> — {filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>}
//         </h2>
//         <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
//           <span style={{
//               fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 20,
//               background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
//               whiteSpace: 'nowrap',
//             }}>
//               ☁️ Synced to backend
//             </span>
//           <button className="rup-export-btn" onClick={handleExportCSV} disabled={loading || !filtered.length} title="Download CSV">
//             📥 Export CSV
//           </button>
//         </div>
//       </div>

//       {/* ── Search + Filters ── */}
//       <div className="rup-search-row">
//         <div className="rup-search-input-wrap">
//           <span className="rup-search-icon">🔍</span>
//           <input className="rup-search-input" placeholder="Search by new user, referrer, email, ref code, plan type…"
//             value={search} onChange={e => setSearch(e.target.value)} />
//           {search && <button className="rup-search-clear" onClick={() => setSearch('')} title="Clear">×</button>}
//         </div>
//         {/* Date filter */}
//         <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
//           <span style={{ fontSize: 12, fontWeight: 600, color: '#636e72', whiteSpace: 'nowrap' }}>📅 AD POSTED:</span>
//           {[
//             { value: 'day',        label: 'Today' },
//             { value: 'yesterday',  label: 'Yesterday' },
//             { value: 'week',       label: 'This Week' },
//             { value: 'last_week',  label: 'Last Week' },
//             { value: 'month',      label: 'This Month' },
//             { value: 'last_month', label: 'Last Month' },
//             { value: 'all',        label: 'All Time' },
//           ].map(opt => (
//             <button
//               key={opt.value}
//               onClick={() => setDateFilter(opt.value)}
//               style={{
//                 padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
//                 cursor: 'pointer', border: `1px solid ${dateFilter === opt.value ? '#6c5ce7' : '#dfe6e9'}`,
//                 background: dateFilter === opt.value ? '#6c5ce7' : '#fff',
//                 color: dateFilter === opt.value ? '#fff' : '#636e72',
//                 transition: 'all 0.15s',
//               }}
//             >{opt.label}</button>
//           ))}
//         </div>
//         {/* Approval filter */}
//         <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
//           <span style={{ fontSize: 12, fontWeight: 600, color: '#636e72', whiteSpace: 'nowrap' }}>✔ APPROVALS:</span>
//           {[
//             { value: 'all',      label: 'All' },
//             { value: 'approved', label: 'Approved' },
//             { value: 'pending',  label: 'Pending' },
//           ].map(opt => (
//             <button
//               key={opt.value}
//               onClick={() => setApprovalFilter(opt.value)}
//               style={{
//                 padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
//                 cursor: 'pointer',
//                 border: `1px solid ${approvalFilter === opt.value ? '#00b894' : '#dfe6e9'}`,
//                 background: approvalFilter === opt.value ? '#00b894' : '#fff',
//                 color: approvalFilter === opt.value ? '#fff' : '#636e72',
//                 transition: 'all 0.15s',
//               }}
//             >{opt.label}</button>
//           ))}
//         </div>
//       </div>

//       {/* ── Toast ── */}
//       {disperseMsg && (
//         <div className={`rup-toast ${disperseMsg.startsWith('✅') ? 'rup-toast-success' : 'rup-toast-error'}`}>
//           <span>{disperseMsg}</span>
//           <button className="rup-toast-close" onClick={() => setDisperseMsg('')}>×</button>
//         </div>
//       )}

//       {/* ── Table ── */}
//       {!loading && !error && (
//         <div className="rup-table-wrap">
//           <table className="rup-table">
//             <thead>
//               <tr>
//                 <th>NEW USER</th>
//                 <th>REF CODE</th>
//                 <th>REFERRER (OLD USER)</th>
//                 <th>DATE JOINED</th>
//                 <th>PLAN TYPE</th>
//                 <th>PLAN PAYMENT</th>
//                 <th>COMMISSION</th>
//                 <th>TXN REF</th>
//                 <th>AD POSTED</th>
//                 <th>PAYOUT PHONE</th>
//                 <th style={{ minWidth: 150, textAlign: 'left' }}>APPROVALS</th>
//                 <th style={{ minWidth: 155, textAlign: 'left' }}>APPROVAL TIME</th>
//                 <th style={{ minWidth: 145, textAlign: 'left' }}>GROUPED CODE</th>
//                 <th style={{ minWidth: 130, textAlign: 'left' }}>PAYMENT STATUS</th>
//                 <th style={{ minWidth: 80, textAlign: 'center' }}>
//                   <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
//                     ✏️ EDIT
//                   </span>
//                 </th>
//               </tr>
//             </thead>
//             <tbody>
//               {pagedGroupRows.length === 0 ? (
//                 <tr><td colSpan={15} className="rup-empty-cell">
//                   {search ? `No results for "${search}"` : 'No referred users yet. Share your referral link to get started.'}
//                 </td></tr>
//               ) : (
//                 pagedGroupRows.map((item, i) => {
//                   /* ── SUMMARY ROW ── */
//                   if (item.type === 'summary') {
//                     const g = item.data;
//                     const approvalStatus     = approvalMap[g.referrerKey];
//                     const isApproving        = approvingKey === g.referrerKey;
//                     const isPaid             = approvalStatus === 'paid';
//                     // ✅ KEY: new commissions after last approvedAt need re-approval
//                     const needsReApproval    = approvalStatus === 'approved' && g.hasNewSinceApproval;
//                     const isEffectivelyApproved = (
//                       (approvalStatus === 'approved' || approvalStatus === 'pending' || isPaid)
//                       && !needsReApproval
//                     );

//                     return (
//                       <tr key={`summary-${g.referrerKey}`} style={{
//                         background: isPaid
//                           ? 'linear-gradient(90deg, #f0fdf4 0%, #dcfce7 100%)'
//                           : needsReApproval
//                           ? 'linear-gradient(90deg, #fffbeb 0%, #fef3c7 100%)'
//                           : 'linear-gradient(90deg, #f0fdf4 0%, #ecfdf5 100%)',
//                         borderTop:    `2px solid ${needsReApproval ? '#fcd34d' : '#6ee7b7'}`,
//                         borderBottom: `2px solid ${needsReApproval ? '#fcd34d' : '#6ee7b7'}`,
//                       }}>
//                         <td colSpan={5} style={{ paddingLeft: 16 }}>
//                           <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
//                             <span style={{ fontSize: 12, fontWeight: 700, color: needsReApproval ? '#92400e' : '#065f46' }}>
//                               📊 Cumulative — {g.refName}
//                             </span>
//                             <span style={{ fontSize: 11, color: '#6b7280' }}>
//                               ({g.rows.length} transaction{g.rows.length !== 1 ? 's' : ''})
//                             </span>
//                             {needsReApproval && (
//                               <span style={{
//                                 fontSize: 11, fontWeight: 700,
//                                 background: '#fef3c7', color: '#92400e',
//                                 border: '1px solid #fcd34d',
//                                 padding: '2px 8px', borderRadius: 20,
//                               }}>⚠️ New commissions since last approval</span>
//                             )}
//                           </div>
//                         </td>
//                         <td style={{ fontWeight: 800, color: '#1a1a2e' }}>{fmtKsh(g.totalPlan)}</td>
//                         <td>
//                           <span style={{ fontWeight: 800, color: '#00b894', fontSize: 15 }}>
//                             {fmtKsh(g.totalComm)}
//                           </span>
//                         </td>
//                         <td colSpan={3} />{/* TXN REF + AD POSTED + PAYOUT PHONE */}

//                         {/* APPROVALS */}
//                         <td style={{ whiteSpace: 'nowrap', textAlign: 'left', verticalAlign: 'middle' }}>
//                           {isEffectivelyApproved ? (
//                             <span style={{
//                               display: 'inline-flex', alignItems: 'center', gap: 5,
//                               background: '#d1fae5', color: '#065f46',
//                               border: '1px solid #6ee7b7',
//                               padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
//                             }}>✅ Approved</span>
//                           ) : (
//                             <button
//                               onClick={() => handleApprove(g)}
//                               disabled={isApproving || isPaid}
//                               style={{
//                                 display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
//                                 padding: '7px 18px',
//                                 background: isPaid ? '#94a3b8' : isApproving ? '#6c5ce7aa' : needsReApproval ? '#f59e0b' : '#6c5ce7',
//                                 color: '#fff', border: 'none', borderRadius: 10,
//                                 fontSize: 13, fontWeight: 700,
//                                 cursor: (isApproving || isPaid) ? 'not-allowed' : 'pointer',
//                                 transition: 'background 0.2s', minWidth: 120,
//                               }}
//                             >
//                               {isApproving
//                                 ? <><span className="rup-btn-spinner" /> Approving…</>
//                                 : needsReApproval ? '🔄 Re-Approve' : '✔ Approve'}
//                             </button>
//                           )}
//                         </td>

//                         {/* APPROVAL TIME ── new column */}
//                         <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
//                           {g.approvedAt ? (
//                             <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
//                               <span style={{ fontSize: 12, fontWeight: 600, color: needsReApproval ? '#92400e' : '#1e293b' }}>
//                                 🕐 {fmtDate(g.approvedAt)}
//                               </span>
//                               <span style={{ fontSize: 10.5, color: '#94a3b8' }}>
//                                 {new Date(g.approvedAt).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' })}
//                               </span>
//                             </div>
//                           ) : (
//                             <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>
//                           )}
//                         </td>

//                         {/* GROUPED CODE — only shown when the batch is approved-but-NOT-paid.
//                             Once paid the batch is closed; the code disappears here.
//                             New approvals after payment will generate a fresh code. */}
//                         <td style={{ verticalAlign: 'middle' }}>
//                           {(() => {
//                             // Only show if the cumulative approval exists and is NOT paid
//                             const ea = approvedPayments.find(
//                               p => p.referrerKey === g.referrerKey && p.status !== 'paid'
//                             );
//                             const rowGc = !ea
//                               ? Object.values(rowApprovalSet)
//                                   .find(ra => {
//                                     if (ra.referrerKey !== g.referrerKey || !ra.groupCode) return false;
//                                     // Only use if the batch for this txn ref is NOT paid
//                                     const batch = approvedPayments.find(p =>
//                                       Array.isArray(p.approvedTxnRefs) && p.approvedTxnRefs.includes(ra.txnRef)
//                                     );
//                                     return !batch || batch.status !== 'paid';
//                                   })?.groupCode
//                               : null;
//                             const gc = ea?.groupCode || rowGc;
//                             if (!gc) return <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>;
//                             return (
//                               <span style={{
//                                 fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
//                                 background: '#eef2ff', color: '#6366f1',
//                                 border: '1px solid #c7d2fe',
//                                 padding: '3px 8px', borderRadius: 6,
//                                 userSelect: 'all', cursor: 'text', whiteSpace: 'nowrap',
//                               }} title='Batch code — all approved-but-unpaid commissions for this referrer share this code'>
//                                 {gc}
//                               </span>
//                             );
//                           })()}
//                         </td>

// {/* PAYMENT STATUS */}
//                         <td style={{ whiteSpace: 'nowrap', textAlign: 'left', verticalAlign: 'middle' }}>
//                           {isPaid ? (
//                             <span style={{
//                               display: 'inline-flex', alignItems: 'center', gap: 5,
//                               background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
//                               padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
//                             }}>💚 Paid</span>
//                           ) : approvalStatus === 'pending' ? (
//                             <span style={{
//                               display: 'inline-flex', alignItems: 'center', gap: 5,
//                               background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa',
//                               padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
//                             }}>⏳ Processing</span>
//                           ) : (
//                             <span style={{
//                               display: 'inline-flex', alignItems: 'center', gap: 5,
//                               background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0',
//                               padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
//                             }}>— Not Paid</span>
//                           )}
//                         </td>
//                         <td />{/* EDIT column — empty for summary */}
//                       </tr>
//                     );
//                   }

//                   /* ── DATA ROW ── */
//                   const r        = item.data;
//                   const absIdx   = i;
//                   const color    = avatarColor(absIdx);
//                   const initials = r.initials || getInitials(r.name || '');

//                   const planType    = r.premium_type     || 'Free';
//                   const planPayment = r.plan_amount       || 0;
//                   const commAmt     = r.commission_amount || 0;
//                   const pColor      = planColor(planType);
//                   const refName     = r.referral?.referrer_username || r.referral?.referrer_name || '—';
//                   const refEmail    = r.referral?.referrer_email || '—';
//                   const rowKey      = r.id;
//                   // Inherit referrer's approval status for this row
//                   const rowReferrerKey    = r.referral?.referrer_email || r.referral?.referrer_username || 'unknown';
//                   const rowApprovalStatus = approvalMap[rowReferrerKey]; // 'approved'|'pending'|'paid'|undefined
//                   const rowIsPaid         = rowApprovalStatus === 'paid';

//                   return (
//                     <tr key={rowKey}>

//                       {/* NEW USER */}
//                       <td>
//                         <div className="rup-user-cell">
//                           <div className="rup-avatar" style={{ background: `${color}20`, color }}>{initials}</div>
//                           <div>
//                             <div className="rup-user-name">{r.name || '—'}</div>
//                             <div className="rup-user-email">{r.email || '—'}</div>
//                           </div>
//                         </div>
//                       </td>

//                       {/* REF CODE */}
//                       <td>
//                         {r.referred_by_code ? (
//                           <span style={{
//                             fontFamily: 'monospace', fontSize: 11.5, fontWeight: 700,
//                             background: '#eef2ff', color: '#6366f1', padding: '2px 8px',
//                             borderRadius: 6, border: '1px solid #c7d2fe', letterSpacing: '0.05em',
//                           }}>
//                             {r.referred_by_code}
//                           </span>
//                         ) : <span style={{ color: '#cbd5e1' }}>—</span>}
//                       </td>

//                       {/* REFERRER */}
//                       <td>
//                         <div className="rup-user-name">{refName}</div>
//                         <div className="rup-user-email">{refEmail}</div>
//                       </td>

//                       {/* DATE JOINED */}
//                       <td>{fmtDate(r.joined_at)}</td>

//                       {/* PLAN TYPE */}
//                       <td>
//                         <span className="rup-plan-badge" style={{ background: `${pColor}15`, color: pColor, border: `1px solid ${pColor}30` }}>
//                           {planType}
//                         </span>
//                       </td>

//                       {/* PLAN PAYMENT */}
//                       <td style={{ fontWeight: 700 }}>
//                         {planPayment > 0 ? fmtKsh(planPayment) : '—'}
//                       </td>

//                       {/* COMMISSION */}
//                       <td>
//                         <span style={{ fontWeight: 700, color: commAmt > 0 ? '#00b894' : 'inherit' }}>
//                           {commAmt > 0 ? fmtKsh(commAmt) : '—'}
//                         </span>
//                       </td>

//                       {/* COMM STATUS — removed, merged into APPROVALS column */}

//                       {/* TXN REF — click to expand/collapse full code */}
//                       <td>
//                         {r.transaction_ref ? (
//                           <button
//                             onClick={() => toggleTxnRef(rowKey)}
//                             title={expandedTxnRefs.has(rowKey) ? 'Click to collapse' : 'Click to view full code'}
//                             style={{
//                               fontFamily: 'monospace',
//                               fontSize: 10.5,
//                               color: '#475569',
//                               background: expandedTxnRefs.has(rowKey) ? '#eef2ff' : 'none',
//                               border: expandedTxnRefs.has(rowKey) ? '1px solid #c7d2fe' : 'none',
//                               borderRadius: 6,
//                               padding: expandedTxnRefs.has(rowKey) ? '3px 7px' : '0',
//                               cursor: 'pointer',
//                               userSelect: 'all',
//                               wordBreak: 'break-all',
//                               textAlign: 'left',
//                               maxWidth: expandedTxnRefs.has(rowKey) ? 220 : 140,
//                               display: 'block',
//                               transition: 'all 0.15s',
//                               lineHeight: 1.5,
//                             }}
//                           >
//                             {expandedTxnRefs.has(rowKey)
//                               ? r.transaction_ref
//                               : (r.transaction_ref.length > 16
//                                   ? r.transaction_ref.slice(0, 16) + '…'
//                                   : r.transaction_ref)
//                             }
//                           </button>
//                         ) : <span style={{ color: '#cbd5e1' }}>—</span>}
//                       </td>

//                       {/* AD POSTED */}
//                       <td>
//                         {(() => {
//                           const dt = fmtDateTime(r.ad_posted_at);
//                           return dt ? (
//                             <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
//                               <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap' }}>📦 {dt.date}</span>
//                               <span style={{ fontSize: 10.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>🕐 {dt.time}</span>
//                             </div>
//                           ) : <span style={{ color: '#cbd5e1' }}>—</span>;
//                         })()}
//                       </td>

//                       {/* PAYOUT PHONE — referrer's M-Pesa number from their user profile */}
//                       <td>
//                         {(() => {
//                           // Look up the referrer's referral_mpesa from the admin users list.
//                           // Falls back to any phone already on the row for backwards compat.
//                           const refKey = (r.referral?.referrer_email || r.referral?.referrer_username || '').toLowerCase();
//                           const phone  =
//                             referrerMpesaMap[refKey] ||
//                             r.payout_phone           ||
//                             r.referral?.referrer_mpesa ||
//                             null;
//                           return phone ? (
//                             <span style={{ fontFamily: 'monospace', fontSize: 11.5, fontWeight: 700, color: '#0984e3' }}>
//                               📱 {phone}
//                             </span>
//                           ) : <span style={{ color: '#cbd5e1' }}>—</span>;
//                         })()}
//                       </td>

//                       {/* APPROVALS — individual row: TXN-ref-keyed, independent of cumulative approvalMap */}
//                       <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>
//                         {(() => {
//                           const txnRef          = r.transaction_ref;
//                           const isRowApproving  = approvingRowKey === rowKey;

//                           // ── Paid (referrer-level lock) ──────────────────────
//                           if (rowIsPaid) {
//                             return (
//                               <span style={{
//                                 display: 'inline-flex', alignItems: 'center', gap: 5,
//                                 background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
//                                 padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
//                               }}>💚 Paid</span>
//                             );
//                           }

//                           // ── Individually approved by TXN ref ────────────────
//                           if (txnRef && rowApprovalSet[txnRef]) {
//                             return (
//                               <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
//                                 <span style={{
//                                   display: 'inline-flex', alignItems: 'center', gap: 5,
//                                   background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7',
//                                   padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
//                                 }}>✅ Approved</span>
//                                 <button
//                                   onClick={() => handleRevokeRowApproval(r)}
//                                   title="Revoke this row's approval"
//                                   style={{
//                                     display: 'inline-flex', alignItems: 'center', gap: 4,
//                                     background: '#fff0f0', color: '#dc2626', border: '1px solid #fca5a5',
//                                     padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700,
//                                     cursor: 'pointer', whiteSpace: 'nowrap',
//                                   }}
//                                   onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
//                                   onMouseLeave={e => { e.currentTarget.style.background = '#fff0f0'; }}
//                                 >🚫 Revoke</button>
//                               </div>
//                             );
//                           }

//                           // ── Cumulative approval covers this row ──────────────
//                           // Only applies if the row is NOT newer than the last approvedAt.
//                           // New rows (after last cumulative approval) must be approved individually.
//                           const existingApproval = approvedPayments.find(p => p.referrerKey === rowReferrerKey);
//                           const approvedAt = existingApproval?.approvedAt ? new Date(existingApproval.approvedAt) : null;
//                           const rowTs      = r.ad_posted_at || r.joined_at;
//                           const isNewRow   = approvedAt && rowTs && new Date(rowTs) > approvedAt;

//                           if (!isNewRow && (rowApprovalStatus === 'approved' || rowApprovalStatus === 'pending')) {
//                             return (
//                               <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
//                                 <span style={{
//                                   display: 'inline-flex', alignItems: 'center', gap: 5,
//                                   background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7',
//                                   padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
//                                 }}>✅ Approved</span>
//                                 <button
//                                   onClick={() => handleRevokeRowApproval(r)}
//                                   title="Revoke this row's approval (removes TXN ref from approval batch)"
//                                   style={{
//                                     display: 'inline-flex', alignItems: 'center', gap: 4,
//                                     background: '#fff0f0', color: '#dc2626', border: '1px solid #fca5a5',
//                                     padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700,
//                                     cursor: 'pointer', whiteSpace: 'nowrap',
//                                   }}
//                                   onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
//                                   onMouseLeave={e => { e.currentTarget.style.background = '#fff0f0'; }}
//                                 >🚫 Revoke</button>
//                               </div>
//                             );
//                           }

//                           // ── New row after last cumulative approval ───────────
//                           if (isNewRow) {
//                             return (
//                               <button
//                                 onClick={() => handleApproveRow(r, item.group)}
//                                 disabled={isRowApproving}
//                                 title="This row was added after the last cumulative approval — approve it individually"
//                                 style={{
//                                   display: 'inline-flex', alignItems: 'center', gap: 5,
//                                   background: isRowApproving ? '#f59e0baa' : '#f59e0b',
//                                   color: '#fff', border: 'none',
//                                   padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
//                                   cursor: isRowApproving ? 'not-allowed' : 'pointer',
//                                   transition: 'background 0.15s',
//                                 }}
//                               >
//                                 {isRowApproving
//                                   ? <><span className="rup-btn-spinner" /> Approving…</>
//                                   : '⚠️ Approve Row'}
//                               </button>
//                             );
//                           }

//                           // ── Not yet approved — show individual approve button ─
//                           return (
//                             <button
//                               onClick={() => handleApproveRow(r, item.group)}
//                               disabled={isRowApproving || !txnRef}
//                               title={txnRef ? 'Approve this individual commission row' : 'No TXN ref — use cumulative approval'}
//                               style={{
//                                 display: 'inline-flex', alignItems: 'center', gap: 5,
//                                 background: !txnRef ? '#e2e8f0' : isRowApproving ? '#6c5ce7aa' : '#6c5ce7',
//                                 color: !txnRef ? '#94a3b8' : '#fff', border: 'none',
//                                 padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
//                                 cursor: (!txnRef || isRowApproving) ? 'not-allowed' : 'pointer',
//                                 transition: 'background 0.15s',
//                               }}
//                             >
//                               {isRowApproving
//                                 ? <><span className="rup-btn-spinner" /> Approving…</>
//                                 : txnRef ? '✔ Approve' : '— No TXN'}
//                             </button>
//                           );
//                         })()}
//                       </td>

//                       {/* APPROVAL TIME — row-level ts takes priority over cumulative */}
//                       <td style={{ verticalAlign: 'middle' }}>
//                         {(() => {
//                           const txnRef = r.transaction_ref;
//                           // Priority 1: per-row individual approval timestamp
//                           const rowApproval   = txnRef ? rowApprovalSet[txnRef] : null;
//                           // Priority 2: cumulative referrer approval timestamp
//                           const cumulApproval = approvedPayments.find(p => p.referrerKey === rowReferrerKey);
//                           const approvedAtTs  = rowApproval?.approvedAt || cumulApproval?.approvedAt || null;
//                           if (!approvedAtTs) return <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>;
//                           const dt = fmtDateTime(approvedAtTs);
//                           const isRowLevel = !!rowApproval?.approvedAt;
//                           return dt ? (
//                             <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
//                               <span style={{ fontSize: 12, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>🕐 {dt.date}</span>
//                               <span style={{ fontSize: 10.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>{dt.time}</span>
//                               {isRowLevel && (
//                                 <span style={{ fontSize: 9.5, color: '#a5b4fc', fontWeight: 600 }}>row-level</span>
//                               )}
//                             </div>
//                           ) : <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>;
//                         })()}
//                       </td>

//                       {/* GROUPED CODE — shows batch code when approved & not yet paid.
//                           Priority: cumulative batch code → individual row-level code.
//                           Hidden (—) once the batch is paid (payment is complete). */}
//                       <td style={{ verticalAlign: 'middle' }}>
//                         {(() => {
//                           const txnRef = r.transaction_ref;

//                           // 1️⃣ Check cumulative batch — only show if NOT paid
//                           const batchApproval = txnRef
//                             ? approvedPayments.find(p =>
//                                 Array.isArray(p.approvedTxnRefs) && p.approvedTxnRefs.includes(txnRef)
//                               )
//                             : null;
//                           const batchGc = (batchApproval && batchApproval.status !== 'paid')
//                             ? batchApproval.groupCode
//                             : null;

//                           // 2️⃣ Fallback: individual row-level approval code (also only if not paid)
//                           const rowApproval = txnRef ? rowApprovalSet[txnRef] : null;
//                           const rowGc = rowApproval?.groupCode || null;

//                           const gc = batchGc || rowGc;
//                           if (!gc) return <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>;
//                           return (
//                             <span style={{
//                               fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
//                               background: '#eef2ff', color: '#6366f1',
//                               border: '1px solid #c7d2fe',
//                               padding: '3px 8px', borderRadius: 6,
//                               userSelect: 'all', cursor: 'text', whiteSpace: 'nowrap',
//                             }} title='Batch approval code — groups all approved-but-unpaid commissions for this referrer'>
//                               {gc}
//                             </span>
//                           );
//                         })()}
//                       </td>

//                       {/* PAYMENT STATUS — per-transaction, NOT per-referrer.
//                           Priority: row-level commission_status (from DB after marking paid)
//                           → batch approval status → "— Not Paid" fallback. */}
//                       <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>
//                         {(() => {
//                           const txnRef = r.transaction_ref;
//                           // Row-level paid (set via inline edit → PATCH to backend)
//                           const rowPaidDirectly = r.commission_status === 'paid' || r.payment_status === 'paid';

//                           // Find any approval entry that explicitly includes this TXN REF
//                           const matchingApproval = txnRef
//                             ? approvedPayments.find(p =>
//                                 Array.isArray(p.approvedTxnRefs) && p.approvedTxnRefs.includes(txnRef)
//                               )
//                             : null;

//                           const txnStatus = matchingApproval?.status; // 'approved'|'pending'|'paid'|undefined

//                           if (txnStatus === 'paid' || rowIsPaid || rowPaidDirectly) {
//                             return (
//                               <span style={{
//                                 display: 'inline-flex', alignItems: 'center', gap: 5,
//                                 background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
//                                 padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
//                               }}>💚 Paid</span>
//                             );
//                           }
//                           if (txnStatus === 'pending') {
//                             return (
//                               <span style={{
//                                 display: 'inline-flex', alignItems: 'center', gap: 5,
//                                 background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa',
//                                 padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
//                               }}>⏳ Processing</span>
//                             );
//                           }
//                           if (txnStatus === 'approved') {
//                             return (
//                               <span style={{
//                                 display: 'inline-flex', alignItems: 'center', gap: 5,
//                                 background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7',
//                                 padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
//                               }}>✅ Approved</span>
//                             );
//                           }
//                           // Not in any approved batch — default "— Not Paid"
//                           return (
//                             <span style={{
//                               display: 'inline-flex', alignItems: 'center', gap: 5,
//                               background: '#f1f5f9', color: '#94a3b8', border: '1px solid #e2e8f0',
//                               padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
//                             }}>— Not Paid</span>
//                           );
//                         })()}
//                       </td>

//                       {/* EDIT — fully locked when referrer is paid */}
//                       <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
//                         {rowIsPaid || r.commission_status === 'paid' || r.payment_status === 'paid' ? (
//                           <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
//                             <span style={{ fontSize: 12, color: '#b2bec3', fontStyle: 'italic' }}>🔒 Locked</span>
//                             <button
//                               onClick={() => toggleEditMode(rowKey)}
//                               title="[DEV] Temporarily unlock this paid record"
//                               style={{
//                                 background: editModeRows.has(rowKey) ? '#fef3c7' : '#f8fafc',
//                                 color: editModeRows.has(rowKey) ? '#b45309' : '#94a3b8',
//                                 border: `1px solid ${editModeRows.has(rowKey) ? '#fde68a' : '#e2e8f0'}`,
//                                 borderRadius: 8, padding: '4px 8px', fontSize: 11, fontWeight: 700,
//                                 cursor: 'pointer', transition: 'all 0.15s',
//                               }}
//                             >{editModeRows.has(rowKey) ? '🔓' : '🔑'}</button>
//                             {editModeRows.has(rowKey) && (
//                               <button
//                                 onClick={() => handleDelete(rowKey, r.id)}
//                                 style={{
//                                   background: '#fff0f0', color: '#dc2626', border: '1px solid #fca5a5',
//                                   borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 700,
//                                   cursor: 'pointer',
//                                 }}
//                               >🗑 Delete</button>
//                             )}
//                           </div>
//                         ) : (() => {
//                           const unlocked = editModeRows.has(rowKey);
//                           const isSavingPay = !!payStatusSaving[rowKey];
//                           return (
//                             <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, minWidth: unlocked ? 200 : 'auto' }}>
//                               <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
//                                 <button
//                                   onClick={() => toggleEditMode(rowKey)}
//                                   title={unlocked ? 'Lock row' : 'Unlock to edit this row'}
//                                   style={{
//                                     background: unlocked ? '#fef3c7' : '#f1f5f9',
//                                     color: unlocked ? '#b45309' : '#94a3b8',
//                                     border: `1px solid ${unlocked ? '#fde68a' : '#e2e8f0'}`,
//                                     borderRadius: 8, padding: '6px 9px',
//                                     fontSize: 14, cursor: 'pointer', transition: 'all 0.15s', lineHeight: 1,
//                                   }}
//                                 >
//                                   {unlocked ? '🔓' : '✏️'}
//                                 </button>
//                                 <button
//                                   onClick={() => unlocked && handleDelete(rowKey, r.id)}
//                                   title={unlocked ? 'Delete this record' : 'Click ✏️ first to enable delete'}
//                                   disabled={!unlocked}
//                                   style={{
//                                     background: unlocked ? '#fff0f0' : '#f8fafc',
//                                     color: unlocked ? '#dc2626' : '#cbd5e1',
//                                     border: `1px solid ${unlocked ? '#fca5a5' : '#e2e8f0'}`,
//                                     borderRadius: 8, padding: '6px 12px',
//                                     fontSize: 13, fontWeight: 700,
//                                     cursor: unlocked ? 'pointer' : 'not-allowed',
//                                     transition: 'all 0.15s', opacity: unlocked ? 1 : 0.5,
//                                   }}
//                                   onMouseEnter={e => { if (unlocked) e.currentTarget.style.background = '#fee2e2'; }}
//                                   onMouseLeave={e => { if (unlocked) e.currentTarget.style.background = '#fff0f0'; }}
//                                 >
//                                   🗑 Delete
//                                 </button>
//                               </div>
//                               {/* ── Payment status selector (only when unlocked) ── */}
//                               {unlocked && (
//                                 <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
//                                   <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap' }}>Pay status:</span>
//                                   <select
//                                     defaultValue=""
//                                     disabled={isSavingPay}
//                                     onChange={e => {
//                                       if (e.target.value === 'paid') {
//                                         handleMarkRowPaid(rowKey, r.id, r.transaction_ref, rowReferrerKey);
//                                       }
//                                       e.target.value = '';
//                                     }}
//                                     style={{
//                                       height: 28, padding: '0 8px',
//                                       border: '1.5px solid #6c5ce7', borderRadius: 7,
//                                       fontSize: 12, fontWeight: 600, cursor: 'pointer',
//                                       background: '#fff', color: '#1a1a2e', outline: 'none',
//                                     }}
//                                   >
//                                     <option value="" disabled>— set status —</option>
//                                     <option value="paid">💚 Mark as Paid</option>
//                                   </select>
//                                   {isSavingPay && <span className="rup-btn-spinner" style={{ borderColor: '#6c5ce7', borderTopColor: 'transparent' }} />}
//                                 </div>
//                               )}
//                             </div>
//                           );
//                         })()}
//                       </td>

//                     </tr>
//                   );
//                 })
//               )}
//             </tbody>
//           </table>
//         </div>
//       )}

//       {/* ── Pagination — Table 1 ── */}
//       {!loading && !error && (
//         <div className="rup-pv-pagination">
//           <div className="rup-pv-pag-info">
//             {dataRowCount === 0
//               ? 'No records'
//               : `${((safePageGrouped-1)*pageSize)+1}–${Math.min(safePageGrouped*pageSize, dataRowCount)} of ${dataRowCount}`}
//           </div>
//           <div className="rup-pv-pag-controls">
//             <button className="rup-pv-pag-btn" onClick={() => setCurrentPage(p => Math.max(1,p-1))} disabled={safePageGrouped===1}>‹</button>
//             {pageNumbers.map((n, idx) =>
//               n === '…'
//                 ? <span key={`e${idx}`} className="rup-pv-pag-ellipsis">…</span>
//                 : <button key={n} className={`rup-pv-pag-btn${safePageGrouped===n?' active':''}`} onClick={() => setCurrentPage(n)}>{n}</button>
//             )}
//             <button className="rup-pv-pag-btn" onClick={() => setCurrentPage(p => Math.min(totalPagesGrouped,p+1))} disabled={safePageGrouped===totalPagesGrouped}>›</button>
//           </div>
//           <div className="rup-pv-rows-ctrl">
//             <label className="rup-pv-rows-label">Rows per page:</label>
//             <input
//               type="number" min="1" max="500"
//               value={pageSizeInput}
//               onChange={e => setPageSizeInput(e.target.value)}
//               onBlur={handlePageSizeChange}
//               onKeyDown={e => { if (e.key === 'Enter') handlePageSizeChange(); }}
//               className="rup-pv-rows-input"
//             />
//           </div>
//           <div className="rup-pv-pag-info">Page {safePageGrouped} of {totalPagesGrouped}</div>
//         </div>
//       )}

//       {/* ══════════════════════════════════════════════════════════════════
//           APPROVED & NOT PAID TABLE — grouped by referrer (cumulative)
//       ══════════════════════════════════════════════════════════════════ */}
//       {(() => {
//         // Group approvedNotPaidRows by referrer key
//         const anpGroups = {};
//         approvedNotPaidRows.forEach(r => {
//           const refKey   = r.referral?.referrer_email || r.referral?.referrer_username || 'unknown';
//           const refName  = r.referral?.referrer_username || r.referral?.referrer_name || '—';
//           const refEmail = r.referral?.referrer_email || '—';
//           if (!anpGroups[refKey]) {
//             anpGroups[refKey] = { refKey, refName, refEmail, rows: [], totalPlan: 0, totalComm: 0 };
//           }
//           anpGroups[refKey].rows.push(r);
//           anpGroups[refKey].totalPlan += parseFloat(r.plan_amount || 0);
//           anpGroups[refKey].totalComm += parseFloat(r.commission_amount || 0);
//         });
//         const anpGroupList = Object.values(anpGroups);

//         return (
//           <div style={{ marginTop: 36 }}>
//             <div className="rup-section-header" style={{ marginBottom: 16 }}>
//               <h2 className="rup-section-title">
//                 ✅ Approved &amp; Not Paid
//                 <span className="rup-result-count"> — {anpGroupList.length} referrer{anpGroupList.length !== 1 ? 's' : ''} ({approvedNotPaidRows.length} commission{approvedNotPaidRows.length !== 1 ? 's' : ''})</span>
//               </h2>
//               {anpGroupList.length > 0 && (
//                 <button
//                   className="rup-export-btn"
//                   onClick={() => {
//                     const hdrs = ['Referrer','Referrer Email','Transactions','Total Plan Payment (KSh)','Total Commission (KSh)','Payout Phone','Payout Receipt','Status','Grouped Code','Approvals','Approval Time'];
//                     const csvRows = anpGroupList.map(g => {
//                       const ap = approvedPayments.find(p => p.referrerKey === g.refKey);
//                       const phone = (ap?.payoutPhone && ap.payoutPhone !== '—') ? ap.payoutPhone
//                         : referrerMpesaMap[(g.refEmail||'').toLowerCase()] || '—';
//                       return [
//                         g.refName, g.refEmail,
//                         g.rows.length,
//                         g.totalPlan.toFixed(2),
//                         g.totalComm.toFixed(2),
//                         phone,
//                         ap?.payoutReceipt || '—',
//                         ap?.status || 'approved',
//                         ap?.groupCode || '—',
//                         'Approved',
//                         ap?.approvedAt ? new Date(ap.approvedAt).toLocaleString('en-KE') : '—',
//                       ].map(v => `"${String(v).replace(/"/g,'""')}"`).join(',');
//                     });
//                     const blob = new Blob(['﻿' + [hdrs.join(','), ...csvRows].join('\n')], { type: 'text/csv;charset=utf-8;' });
//                     const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'approved-not-paid.csv';
//                     document.body.appendChild(a); a.click(); URL.revokeObjectURL(a.href); document.body.removeChild(a);
//                   }}
//                 >
//                   📥 Export CSV
//                 </button>
//               )}
//             </div>

//             <div className="rup-table-wrap">
//               <table className="rup-table" style={{ minWidth: 1320 }}>
//                 <thead>
//                   <tr>
//                     <th style={{ width: 32 }}></th>{/* expand toggle */}
//                     <th>REFERRER (OLD USER)</th>
//                     <th style={{ textAlign: 'center' }}>TRANSACTIONS</th>
//                     <th>TOTAL PLAN PAYMENT</th>
//                     <th>TOTAL COMMISSION</th>
//                     <th>PAYOUT PHONE</th>
//                     <th>PAYOUT RECEIPT</th>
//                     <th>STATUS</th>
//                     <th style={{ minWidth: 180 }}>DISPERSE PAYMENT</th>
//                     <th>GROUPED CODE</th>
//                     <th style={{ textAlign: 'center' }}>APPROVALS</th>
//                     <th style={{ minWidth: 155 }}>APPROVAL TIME</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {anpGroupList.length === 0 ? (
//                     <tr>
//                       <td colSpan={12} className="rup-empty-cell">
//                         No approved-but-unpaid commissions. Rows appear here once a referrer is approved.
//                       </td>
//                     </tr>
//                   ) : anpGroupList.map((g, idx) => {
//                     const ap        = approvedPayments.find(p => p.referrerKey === g.refKey);
//                     const isPaid    = ap?.status === 'paid';
//                     const isPending = ap?.status === 'pending';
//                     const isDisping = dispersingPayment[g.refKey];
//                     const gc        = ap?.groupCode || null;
//                     const approvedAt  = ap?.approvedAt || null;
//                     const approvalDt  = approvedAt ? fmtDateTime(approvedAt) : null;
//                     const isExpanded  = expandedAnpRows.has(g.refKey);

//                     // Payout phone — live lookup fallback
//                     const phone = (ap?.payoutPhone && ap.payoutPhone !== '—')
//                       ? ap.payoutPhone
//                       : referrerMpesaMap[(g.refEmail||'').toLowerCase()] || null;

//                     const rowBg = idx % 2 === 0 ? '#fff' : '#fafbfd';

//                     return (
//                       <React.Fragment key={g.refKey}>
//                         <tr style={{ background: rowBg, cursor: 'pointer' }} onClick={() => toggleAnpRow(g.refKey)}>

//                           {/* EXPAND TOGGLE */}
//                           <td style={{ textAlign: 'center', paddingLeft: 12 }}>
//                             <button
//                               onClick={e => { e.stopPropagation(); toggleAnpRow(g.refKey); }}
//                               style={{
//                                 background: 'none', border: 'none', cursor: 'pointer',
//                                 fontSize: 14, color: '#94a3b8', padding: 2, lineHeight: 1,
//                                 transition: 'transform 0.2s',
//                                 transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
//                               }}
//                               title={isExpanded ? 'Collapse commissions' : 'Expand to see individual commissions'}
//                             >▶</button>
//                           </td>

//                           {/* REFERRER */}
//                           <td>
//                             <div className="rup-user-cell">
//                               <div className="rup-avatar" style={{ background: `${avatarColor(idx)}20`, color: avatarColor(idx) }}>
//                                 {getInitials(g.refName)}
//                               </div>
//                               <div>
//                                 <div className="rup-user-name">{g.refName}</div>
//                                 <div className="rup-user-email">{g.refEmail}</div>
//                               </div>
//                             </div>
//                           </td>

//                           {/* TRANSACTIONS — clickable badge */}
//                           <td style={{ textAlign: 'center' }}>
//                             <span style={{
//                               display: 'inline-flex', alignItems: 'center', gap: 5,
//                               background: isExpanded ? '#6366f1' : '#eef2ff',
//                               color: isExpanded ? '#fff' : '#6366f1',
//                               fontWeight: 700, fontSize: 13, padding: '3px 12px', borderRadius: 20,
//                               transition: 'all 0.15s',
//                             }}>{g.rows.length} {isExpanded ? '▲' : '▼'}</span>
//                           </td>

//                           {/* TOTAL PLAN PAYMENT */}
//                           <td style={{ fontWeight: 700 }}>{fmtKsh(g.totalPlan)}</td>

//                           {/* TOTAL COMMISSION */}
//                           <td>
//                             <span style={{ fontWeight: 800, color: '#00b894', fontSize: 15 }}>{fmtKsh(g.totalComm)}</span>
//                           </td>

//                           {/* PAYOUT PHONE */}
//                           <td onClick={e => e.stopPropagation()}>
//                             {phone
//                               ? <span style={{ fontFamily: 'monospace', fontSize: 11.5, fontWeight: 700, color: '#0984e3' }}>📱 {phone}</span>
//                               : <span style={{ color: '#cbd5e1' }}>—</span>}
//                           </td>

//                           {/* PAYOUT RECEIPT */}
//                           <td onClick={e => e.stopPropagation()}>
//                             {ap?.payoutReceipt ? (
//                               <span style={{
//                                 fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
//                                 background: '#d1fae5', color: '#065f46',
//                                 padding: '2px 8px', borderRadius: 6, userSelect: 'all', cursor: 'text',
//                               }}>✅ {ap.payoutReceipt}</span>
//                             ) : <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>Pending</span>}
//                           </td>

//                           {/* STATUS */}
//                           <td onClick={e => e.stopPropagation()}>
//                             <span style={{
//                               display: 'inline-block', padding: '4px 14px', borderRadius: 20,
//                               fontSize: 13, fontWeight: 600,
//                               background: isPaid ? '#dcfce7' : '#d1fae5',
//                               color: isPaid ? '#166534' : '#065f46',
//                               border: isPaid ? '1px solid #86efac' : '1px solid #6ee7b7',
//                             }}>{isPaid ? '💚 Paid' : '✅ Approved'}</span>
//                           </td>

//                           {/* DISPERSE PAYMENT */}
//                           <td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
//                             {isPaid ? (
//                               <span className="rup-action-done">💚 Payment Complete</span>
//                             ) : (
//                               <button
//                                 className="rup-action-primary"
//                                 disabled={!!isDisping || isPending}
//                                 onClick={e => { e.stopPropagation(); handleDispersePayment(g.refKey, ap?.approvalId); }}
//                                 style={{ whiteSpace: 'nowrap', minWidth: 160, opacity: isPending ? 0.65 : 1 }}
//                                 title={isPending ? 'Dispersal in progress…' : 'Send M-Pesa payment'}
//                               >
//                                 {isDisping
//                                   ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
//                                       <span className="rup-btn-spinner" /> Processing…
//                                     </span>
//                                   : isPending ? '⏳ Processing…' : '💸 Disperse Payment'}
//                               </button>
//                             )}
//                           </td>

//                           {/* GROUPED CODE */}
//                           <td onClick={e => e.stopPropagation()}>
//                             {gc ? (
//                               <span style={{
//                                 fontFamily: 'monospace', fontSize: 12, fontWeight: 700,
//                                 background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d',
//                                 padding: '3px 10px', borderRadius: 8, letterSpacing: '0.05em',
//                                 whiteSpace: 'nowrap', userSelect: 'all', cursor: 'text',
//                               }} title="Batch code — all approved-but-unpaid commissions for this referrer share this code">{gc}</span>
//                             ) : <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>}
//                           </td>

//                           {/* APPROVALS — Revoke only when not paid */}
//                           <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
//                             {isPaid ? (
//                               <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
//                                 <span style={{ fontSize: 12, color: '#b2bec3', fontStyle: 'italic' }}>🔒 Locked</span>
//                                 <button
//                                   onClick={() => handleRevoke(g.refKey, ap?.approvalId)}
//                                   title="[DEV] Force-unlock this paid record"
//                                   style={{
//                                     background: '#f8fafc', color: '#94a3b8', border: '1px solid #e2e8f0',
//                                     borderRadius: 8, padding: '3px 10px', fontSize: 11, fontWeight: 700,
//                                     cursor: 'pointer', transition: 'all 0.15s',
//                                   }}
//                                   onMouseEnter={e => { e.currentTarget.style.background = '#fff0f0'; e.currentTarget.style.color = '#dc2626'; }}
//                                   onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#94a3b8'; }}
//                                 >🔑 Unlock</button>
//                               </div>
//                             ) : (
//                               <button
//                                 onClick={e => { e.stopPropagation(); handleRevoke(g.refKey, ap?.approvalId); }}
//                                 style={{
//                                   display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
//                                   background: '#fff0f0', color: '#dc2626', border: '1px solid #fca5a5',
//                                   padding: '6px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700,
//                                   cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap',
//                                 }}
//                                 onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
//                                 onMouseLeave={e => { e.currentTarget.style.background = '#fff0f0'; }}
//                               >
//                                 🚫 Revoke Approval
//                               </button>
//                             )}
//                           </td>

//                           {/* APPROVAL TIME */}
//                           <td style={{ verticalAlign: 'middle' }} onClick={e => e.stopPropagation()}>
//                             {approvalDt ? (
//                               <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
//                                 <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap' }}>🕐 {approvalDt.date}</span>
//                                 <span style={{ fontSize: 10.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>{approvalDt.time}</span>
//                               </div>
//                             ) : <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>}
//                           </td>

//                         </tr>

//                         {/* ── EXPANDED DETAIL ROWS — individual commissions ── */}
//                         {isExpanded && (
//                           <tr>
//                             <td colSpan={12} style={{ padding: 0, background: rowBg }}>
//                               <div style={{
//                                 borderLeft: '3px solid #6366f1',
//                                 background: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)',
//                                 padding: '12px 16px 16px 20px',
//                               }}>
//                                 <div style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
//                                   📋 {g.rows.length} Commission{g.rows.length !== 1 ? 's' : ''} — {g.refName}
//                                 </div>
//                                 <div style={{ overflowX: 'auto' }}>
//                                   <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 860 }}>
//                                     <thead>
//                                       <tr style={{ background: '#e0e7ff' }}>
//                                         <th style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>NEW USER</th>
//                                         <th style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>PLAN</th>
//                                         <th style={{ padding: '7px 12px', textAlign: 'right', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>PLAN PMT</th>
//                                         <th style={{ padding: '7px 12px', textAlign: 'right', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>COMMISSION</th>
//                                         <th style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>TXN REF</th>
//                                         <th style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>DATE JOINED</th>
//                                         <th style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>AD POSTED</th>
//                                         <th style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>GROUPED CODE</th>
//                                         <th style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>PAY STATUS</th>
//                                       </tr>
//                                     </thead>
//                                     <tbody>
//                                       {g.rows.map((cr, ci) => {
//                                         const crPlanColor = planColor(cr.premium_type || 'Free');
//                                         const crDt = cr.ad_posted_at ? fmtDateTime(cr.ad_posted_at) : null;
//                                         const crPaid = cr.commission_status === 'paid' || cr.payment_status === 'paid';
//                                         // Check if in a paid batch
//                                         const crBatch = cr.transaction_ref
//                                           ? approvedPayments.find(p => Array.isArray(p.approvedTxnRefs) && p.approvedTxnRefs.includes(cr.transaction_ref))
//                                           : null;
//                                         const crTxnStatus = crBatch?.status;
//                                         return (
//                                           <tr key={cr.id} style={{ background: ci % 2 === 0 ? 'rgba(255,255,255,0.7)' : 'rgba(238,242,255,0.5)', borderBottom: '1px solid #e0e7ff' }}>
//                                             <td style={{ padding: '8px 12px' }}>
//                                               <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 12.5 }}>{cr.name || '—'}</div>
//                                               <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{cr.email || '—'}</div>
//                                             </td>
//                                             <td style={{ padding: '8px 12px' }}>
//                                               <span style={{ background: `${crPlanColor}15`, color: crPlanColor, border: `1px solid ${crPlanColor}30`, padding: '2px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 600 }}>
//                                                 {cr.premium_type || 'Free'}
//                                               </span>
//                                             </td>
//                                             <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap' }}>{cr.plan_amount > 0 ? fmtKsh(cr.plan_amount) : '—'}</td>
//                                             <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 800, color: '#00b894', whiteSpace: 'nowrap' }}>{cr.commission_amount > 0 ? fmtKsh(cr.commission_amount) : '—'}</td>
//                                             <td style={{ padding: '8px 12px' }}>
//                                               {cr.transaction_ref
//                                                 ? <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#475569', userSelect: 'all', wordBreak: 'break-all', maxWidth: 160, display: 'block' }}>{cr.transaction_ref.length > 20 ? cr.transaction_ref.slice(0,20) + '…' : cr.transaction_ref}</span>
//                                                 : <span style={{ color: '#cbd5e1' }}>—</span>}
//                                             </td>
//                                             <td style={{ padding: '8px 12px', fontSize: 12, color: '#475569', whiteSpace: 'nowrap' }}>{fmtDate(cr.joined_at)}</td>
//                                             <td style={{ padding: '8px 12px' }}>
//                                               {crDt ? (
//                                                 <div>
//                                                   <div style={{ fontSize: 11.5, fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap' }}>📦 {crDt.date}</div>
//                                                   <div style={{ fontSize: 10, color: '#94a3b8' }}>🕐 {crDt.time}</div>
//                                                 </div>
//                                               ) : <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>}
//                                             </td>
//                                             {/* GROUPED CODE — row-level code from DB (rowApprovalSet) or parent batch code */}
//                                             <td style={{ padding: '8px 12px' }}>
//                                               {(() => {
//                                                 const crTxnRef = cr.transaction_ref;
//                                                 // Priority 1: individual row-level code loaded from DB
//                                                 const rowGc = crTxnRef ? rowApprovalSet[crTxnRef]?.groupCode : null;
//                                                 // Priority 2: parent cumulative batch code (gc is in outer scope)
//                                                 const displayGc = rowGc || gc || null;
//                                                 if (!displayGc) return <span style={{ color: '#cbd5e1', fontSize: 11 }}>—</span>;
//                                                 return (
//                                                   <span style={{
//                                                     fontFamily: 'monospace', fontSize: 10.5, fontWeight: 700,
//                                                     background: '#fef3c7', color: '#92400e',
//                                                     border: '1px solid #fcd34d',
//                                                     padding: '2px 7px', borderRadius: 6,
//                                                     letterSpacing: '0.04em', whiteSpace: 'nowrap',
//                                                     userSelect: 'all', cursor: 'text',
//                                                   }} title="Batch payment group code">{displayGc}</span>
//                                                 );
//                                               })()}
//                                             </td>
//                                             <td style={{ padding: '8px 12px' }}>
//                                               {crPaid || crTxnStatus === 'paid' ? (
//                                                 <span style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 700 }}>💚 Paid</span>
//                                               ) : crTxnStatus === 'approved' ? (
//                                                 <span style={{ background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 700 }}>✅ Approved</span>
//                                               ) : crTxnStatus === 'pending' ? (
//                                                 <span style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 700 }}>⏳ Processing</span>
//                                               ) : (
//                                                 <span style={{ background: '#f1f5f9', color: '#94a3b8', border: '1px solid #e2e8f0', padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 600 }}>— Not Paid</span>
//                                               )}
//                                             </td>
//                                           </tr>
//                                         );
//                                       })}
//                                     </tbody>
//                                     <tfoot>
//                                       <tr style={{ background: '#e0e7ff', borderTop: '2px solid #c7d2fe' }}>
//                                         <td colSpan={2} style={{ padding: '8px 12px', fontSize: 11.5, fontWeight: 700, color: '#4338ca' }}>Subtotal</td>
//                                         <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 800, color: '#1e293b', fontSize: 12.5, whiteSpace: 'nowrap' }}>{fmtKsh(g.totalPlan)}</td>
//                                         <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 800, color: '#00b894', fontSize: 13, whiteSpace: 'nowrap' }}>{fmtKsh(g.totalComm)}</td>
//                                         <td colSpan={5} style={{ padding: '8px 12px' }} />{/* TXN REF + DATE + AD POSTED + GROUPED CODE + PAY STATUS */}
//                                       </tr>
//                                     </tfoot>
//                                   </table>
//                                 </div>
//                               </div>
//                             </td>
//                           </tr>
//                         )}
//                       </React.Fragment>
//                     );
//                   })}
//                 </tbody>
//               </table>
//             </div>

//             {/* Grand total footer */}
//             {anpGroupList.length > 0 && (
//               <div style={{
//                 display: 'flex', justifyContent: 'flex-end', gap: 24,
//                 background: '#fff', border: '1px solid #eef1f6', borderRadius: 12,
//                 padding: '14px 24px', marginTop: 8,
//               }}>
//                 <span style={{ fontSize: 13, color: '#636e72', fontWeight: 600 }}>
//                   Total Referrers: <strong style={{ color: '#1a1a2e' }}>{anpGroupList.length}</strong>
//                 </span>
//                 <span style={{ fontSize: 13, color: '#636e72', fontWeight: 600 }}>
//                   Total Commission Approved:&nbsp;
//                   <strong style={{ color: '#00b894', fontSize: 16 }}>
//                     {fmtKsh(anpGroupList.reduce((s, g) => s + g.totalComm, 0))}
//                   </strong>
//                 </span>
//               </div>
//             )}
//           </div>
//         );
//       })()}

//     </div>
//   );
// };

// export default ReferredUsersPage;




















































































































